import React, { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import radioData from "./radioData.json";
import { getArtistIdsForCity } from "./cityArtistsData";
import { ARTISTS_DATA } from "./artistData";
import ArtistCard from "./ArtistCard";

gsap.registerPlugin(ScrollTrigger);

// Interface for city coordinates
interface CityData {
  key: string;
  lat: number;
  lng: number;
  name: string;
}

// Create a lookup table for city coordinates from JSON data
const cities: Record<string, CityData> = {};
const cityNameToKey: Record<string, string> = {};

radioData.cities.forEach(city => {
  cities[city.key] = {
    key: city.key,
    lat: city.lat,
    lng: city.lng,
    name: city.name
  };
  // Create mapping from display name to city key
  cityNameToKey[city.name] = city.key;
});

// Map SVG city IDs to city keys
const svgIdToCityKey: Record<string, string> = {
  'new-york': 'NewYork',
  'london': 'London',
  'paris': 'Paris',
  'amsterdam': 'Amsterdam',
  'berlin': 'Berlin',
  'istanbul': 'Istanbul',
  'seoul': 'Seoul',
  'tokyo': 'Tokyo',
  'shanghai': 'Shanghai',
  'los-angeles': 'LosAngeles',
  'mexico-city': 'MexicoCity',
  'barcelona': 'Barcelona',
  'lisboa': 'Lisbon',
  'taipei': 'Taipei',
  'montreal': 'Montreal',
  'vancouver': 'Vancouver',
  'roma': 'Roma',
  'bangkok': 'Thailand',
  'sydney': 'Sydney'
};

// Map city keys to display names
const cityKeyToDisplayName: Record<string, string> = {
  'NewYork': 'New York',
  'London': 'London',
  'Paris': 'Paris',
  'Amsterdam': 'Amsterdam',
  'Berlin': 'Berlin',
  'Istanbul': 'Istanbul',
  'Seoul': 'Seoul',
  'Tokyo': 'Tokyo',
  'Shanghai': 'Shanghai',
  'LosAngeles': 'Los Angeles',
  'MexicoCity': 'Mexico City',
  'Barcelona': 'Barcelona',
  'Lisbon': 'Lisbon',
  'Taipei': 'Taipei',
  'Montreal': 'Montreal',
  'Vancouver': 'Vancouver',
  'Roma': 'Roma',
  'Thailand': 'Bangkok',
  'Sydney': 'Sydney'
};

interface WorldMapSvgProps {
  theme?: string;
  showTooltips?: boolean;
}

const WorldMapSvg: React.FC<WorldMapSvgProps> = ({
  theme = "default",
  showTooltips = true
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });
  const [modalCity, setModalCity] = useState<string | null>(null);
  const [modalCityData, setModalCityData] = useState<CityData | null>(null);
  const [modalArtists, setModalArtists] = useState<any[]>([]);
  const [modalPosition, setModalPosition] = useState<{ x: number; y: number } | null>(null);
  const [rawClickPosition, setRawClickPosition] = useState<{ x: number; y: number } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalTransform, setModalTransform] = useState<string>('translate(-50%, -100%)');
  const [cityCoords, setCityCoords] = useState<Record<string, { x: number; y: number }>>({});

  // Extract city coordinates from path elements
  useEffect(() => {
    if (!svgRef.current) return;
    
    const coords: Record<string, { x: number; y: number }> = {};
    const cityPaths = svgRef.current.querySelectorAll('path[id]');
    
    cityPaths.forEach((path) => {
      const id = path.getAttribute('id');
      if (id && svgIdToCityKey[id]) {
        try {
          const bbox = (path as SVGPathElement).getBBox();
          coords[id] = {
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2
          };
        } catch (e) {
          // Fallback: extract from path data
          const pathData = path.getAttribute('d');
          if (pathData) {
            const match = pathData.match(/M([\d.]+)\s+([\d.]+)/);
            if (match) {
              coords[id] = {
                x: parseFloat(match[1]),
                y: parseFloat(match[2])
              };
            }
          }
        }
      }
    });
    
    setCityCoords(coords);
  }, []);

  // After the popover renders, clamp its position within container margins based on real size
  useEffect(() => {
    if (!rawClickPosition || !containerRef.current || !modalRef.current) return;
    const bounds = containerRef.current.getBoundingClientRect();
    const width = bounds.width;
    const height = bounds.height;
    const margin = 40;
    const modalWidth = modalRef.current.offsetWidth;
    const modalHeight = modalRef.current.offsetHeight;

    // Clamp so the popover stays within margins horizontally
    const minCenterX = margin + modalWidth / 2;
    const maxCenterX = width - margin - modalWidth / 2;
    const clampedCenterX = Math.max(minCenterX, Math.min(maxCenterX, rawClickPosition.x));

    // Clamp Y so the anchor point isn't too close to top/bottom; popover opens upward
    const clampedY = Math.max(margin + 8, Math.min(height - margin - 8, rawClickPosition.y));

    setModalPosition({ x: clampedCenterX, y: clampedY });

    // Maintain centered transform; no edge-based shift needed when center is clamped
    setModalTransform('translate(-50%, -100%)');
  }, [rawClickPosition]);

  // Get actual SVG dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setSvgDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // GSAP scroll animation for mobile
  useEffect(() => {
    if (!containerRef.current || !scrollWrapperRef.current) return;

    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      const container = containerRef.current;
      const wrapper = scrollWrapperRef.current;
      
      // Wait for container to render to get accurate dimensions
      const initScrollAnimation = () => {
        // Force a reflow to ensure container has rendered
        void container.offsetHeight;
        
        // Get the actual width - use both scrollWidth and getBoundingClientRect for accuracy
        const containerRect = container.getBoundingClientRect();
        const containerScrollWidth = container.scrollWidth;
        const containerWidth = Math.max(containerRect.width, containerScrollWidth);
        const viewportWidth = window.innerWidth;
        
        // Container should be 200vw, which is 2 * viewportWidth
        // To show the rightmost edge and add extra space on the right, we need to scroll more
        // Add extra scroll distance (e.g., 50px) to create space on the right
        const extraRightSpace = 50; // Extra pixels to scroll past the right edge
        const scrollDistance = viewportWidth + extraRightSpace; // 100vw + extra space
        
        // Calculate the exact X position needed to show rightmost edge with extra space
        // When X = -(viewportWidth + extraRightSpace), we scroll past the right edge
        const targetX = -(viewportWidth + extraRightSpace);
        
        // Kill any existing animations
        ScrollTrigger.getAll().forEach(trigger => {
          if (trigger.vars.trigger === wrapper) {
            trigger.kill();
          }
        });
        
        // Create scroll trigger animation
        // We want to move from x: 0 (showing left side) to x: -viewportWidth (showing right side)
        const scrollAnimation = gsap.to(container, {
          x: targetX, // Move from 0 to -viewportWidth to show rightmost edge
          ease: "none",
          scrollTrigger: {
            trigger: wrapper,
            start: "60% center", // Center the element when pinning starts
            end: () => `+=${scrollDistance}`,
            scrub: true,
            pin: true, // Pin the trigger element (wrapper)
            pinSpacing: true, // This adds space for the pin
            anticipatePin: 1,
            invalidateOnRefresh: true, // Recalculate on resize
          }
        });
        
        // Refresh ScrollTrigger to ensure it recalculates
        ScrollTrigger.refresh();
        
        // Force a small delay and refresh again to ensure everything is calculated
        setTimeout(() => {
          ScrollTrigger.refresh();
        }, 100);
      };
      
      // Initialize after container has fully rendered
      // Use multiple attempts to ensure we get accurate measurements
      let timeoutIds: NodeJS.Timeout[] = [];
      
      const attemptInit = () => {
        const containerRect = container.getBoundingClientRect();
        const expectedWidth = window.innerWidth * 2; // 200vw
        
        // If container hasn't reached expected width yet, wait a bit more
        if (containerRect.width < expectedWidth * 0.95) {
          const timeoutId = setTimeout(attemptInit, 50);
          timeoutIds.push(timeoutId);
          return;
        }
        
        initScrollAnimation();
      };
      
      // Start initialization
      const initialTimeout = setTimeout(attemptInit, 100);
      timeoutIds.push(initialTimeout);
      
      // Also try on next frame
      requestAnimationFrame(() => {
        attemptInit();
      });
      
      return () => {
        timeoutIds.forEach(id => clearTimeout(id));
        ScrollTrigger.getAll().forEach(trigger => {
          if (trigger.vars.trigger === wrapper) {
            trigger.kill();
          }
        });
      };
    }
  }, []);

  // Function to convert SVG coordinates to screen coordinates
  const getScreenCoordinates = (svgX: number, svgY: number) => {
    if (svgDimensions.width === 0 || svgDimensions.height === 0) {
      return { x: 0, y: 0 };
    }
    
    // SVG viewBox is "0 0 1002 484"
    const viewBoxWidth = 1002;
    const viewBoxHeight = 484;
    
    // Scale coordinates relative to the actual viewBox
    const scaleX = svgDimensions.width / viewBoxWidth;
    const scaleY = svgDimensions.height / viewBoxHeight;
    
    return {
      x: svgX * scaleX,
      y: svgY * scaleY
    };
  };

  // Handle city path click
  const handleCityClick = (svgId: string, event: React.MouseEvent<SVGPathElement>) => {
    const cityKey = svgIdToCityKey[svgId];
    if (!cityKey || !cities[cityKey]) return;

    const cityName = cityKeyToDisplayName[cityKey] || cities[cityKey].name;
    setModalCity(cityName);
    setModalCityData(cities[cityKey]);
    
    const artistIds = getArtistIdsForCity(cityKey);
    const artists = artistIds
      .map(id => ARTISTS_DATA.find(artist => artist.id === id))
      .filter(Boolean) as any[];
    setModalArtists(artists);

    // Get click coordinates
    const coords = cityCoords[svgId];
    if (coords) {
      const screenCoords = getScreenCoordinates(coords.x, coords.y);
      
      // Calculate city label position for popup (similar to Map.tsx)
      const isSpecialTopCity = ['Paris', 'Amsterdam', 'Seoul'].includes(cityName);
      const isSpecialBottomCity = ['Lisbon', 'Bangkok'].includes(cityName);
      const isTopLeftCity = ['Vancouver', 'Montreal', 'Paris', 'Taipei', 'Seoul', 'London'].includes(cityName);
      const isTopRightCity = ['New York', 'Tokyo', 'Berlin', 'Amsterdam'].includes(cityName);
      const isTopCity = isTopLeftCity || isTopRightCity;
      const cityY = isSpecialTopCity ? 30 : (isSpecialBottomCity ? svgDimensions.height - 100 : (isTopCity ? 70 : svgDimensions.height - 60));
      const clickCoords = { x: screenCoords.x, y: cityY };
      setModalPosition(clickCoords);
      setRawClickPosition(clickCoords);
    } else {
      // Fallback to click position
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const clickCoords = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top
        };
        setModalPosition(clickCoords);
        setRawClickPosition(clickCoords);
      }
    }
  };

  return (
    <>
      <div ref={scrollWrapperRef} className="svg-scroll-wrapper">
        <div ref={containerRef} className="svg-container" style={{ position: 'relative' }}>
        <svg
          ref={svgRef}
          width="100%"
          height="auto"
          viewBox="0 0 1002 484"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
      <path
        d="M585.365 65.2483C582.384 67.3527 579.446 67.2898 577.222 66.2556C575.99 65.6846 576.486 65.0454 576.491 64.1207C576.493 64.0022 575.923 63.9968 575.97 63.7705C576.064 63.3001 576.735 62.0109 577.119 61.5261C577.335 61.255 581.296 58.8884 581.94 58.6927C584.953 57.7806 587.456 58.2348 588.176 60.0825C588.913 61.9768 587.943 63.4276 585.365 65.2483Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M869.418 73.3605C871.855 74.9945 869.616 79.162 865.5 80.7098C859.34 83.026 855.811 78.4743 861.01 74.6103C863.902 72.461 867.346 71.9726 869.418 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M248.07 186.642C246.885 186.696 246.079 187.588 245.504 188.348C245.264 188.668 245.046 188.989 244.865 189.312C244.711 189.589 244.54 189.889 244.423 190.163C244.391 190.239 244.373 190.328 244.414 190.384C244.449 190.433 244.488 190.436 244.572 190.433C244.797 190.422 245.075 190.372 245.31 190.348C246.034 190.278 246.695 190.314 247.374 190.336C247.575 190.343 247.799 190.391 248.018 190.239C248.203 190.109 248.268 189.881 248.336 189.729C248.652 189.028 249.056 188.204 249.146 187.535C249.221 186.973 248.855 186.606 248.07 186.642ZM242.804 197.751H242.271C242.226 197.739 242.163 197.748 242.109 197.746C240.461 197.688 239.123 197.319 238.214 196.498C236.957 195.365 236.717 193.735 237.636 191.873C237.642 191.862 237.654 191.846 237.645 191.84C237.676 191.781 237.703 191.724 237.735 191.664C238.471 190.293 239.563 189.057 241.209 187.991C243.159 186.727 245.467 185.856 247.621 185.71C248.634 185.642 249.76 185.665 250.616 185.857C252.448 186.267 253.899 187.473 253.706 189.111C253.656 189.556 253.504 190.183 253.227 190.646C252.898 191.199 252.254 191.436 251.588 191.506C249.88 191.689 248.424 191.406 246.846 191.345C246.074 191.314 245.167 191.305 244.325 191.451C243.58 191.578 243.167 191.772 242.833 192.356C242.026 193.769 243.172 194.697 244.766 194.792C245.896 194.86 247.314 194.6 248.55 194.115C249.309 193.817 250.044 193.381 250.728 192.957C251.117 192.715 251.555 192.347 251.995 192.422C252.396 192.49 252.132 192.91 251.959 193.178C250.995 194.681 248.912 196.155 246.921 196.945C245.567 197.482 244.088 197.719 242.804 197.751Z"
        fill="black"
        id="new-york"
        onClick={(e) => handleCityClick('new-york', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M871.621 230.988C870.35 233.48 866.77 235.5 863.624 235.5C860.476 235.5 858.956 233.48 860.227 230.988C861.496 228.496 865.078 226.476 868.224 226.476C871.372 226.476 872.893 228.496 871.621 230.988Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M899.333 91.3551C898.062 93.8474 894.482 95.8674 891.336 95.8674C888.188 95.8674 886.667 93.8474 887.939 91.3551C889.208 88.8629 892.79 86.8429 895.936 86.8429C899.084 86.8429 900.605 88.8629 899.333 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M892.074 105.59C890.803 108.082 887.222 110.102 884.076 110.102C880.929 110.102 879.408 108.082 880.679 105.59C881.949 103.098 885.531 101.078 888.677 101.078C891.824 101.078 893.345 103.098 892.074 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M884.657 120.151C883.385 122.643 879.805 124.663 876.659 124.663C873.512 124.663 871.991 122.643 873.262 120.151C874.531 117.658 878.114 115.638 881.259 115.638C884.407 115.638 885.928 117.658 884.657 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M877.376 134.417C876.105 136.909 872.525 138.929 869.379 138.929C866.231 138.929 864.71 136.909 865.982 134.417C867.251 131.925 870.833 129.905 873.979 129.905C877.127 129.905 878.648 131.925 877.376 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M870.043 148.81C868.772 151.302 865.192 153.322 862.046 153.322C858.898 153.322 857.377 151.302 858.649 148.81C859.918 146.317 863.5 144.297 866.646 144.297C869.794 144.297 871.314 146.317 870.043 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M862.815 162.992C861.544 165.484 857.964 167.504 854.818 167.504C851.67 167.504 850.149 165.484 851.421 162.992C852.69 160.499 856.272 158.479 859.418 158.479C862.566 158.479 864.087 160.499 862.815 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M848.202 191.661C846.931 194.153 843.35 196.173 840.205 196.173C837.057 196.173 835.536 194.153 836.807 191.661C838.077 189.169 841.659 187.149 844.805 187.149C847.952 187.149 849.473 189.169 848.202 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M840.911 205.948C839.64 208.44 836.059 210.46 832.914 210.46C829.766 210.46 828.245 208.44 829.516 205.948C830.786 203.456 834.368 201.436 837.514 201.436C840.661 201.436 842.182 203.456 840.911 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M881.006 91.3551C879.735 93.8474 876.154 95.8674 873.009 95.8674C869.861 95.8674 868.34 93.8474 869.611 91.3551C870.881 88.8629 874.463 86.8429 877.609 86.8429C880.756 86.8429 882.277 88.8629 881.006 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M888.307 77.0363C887.036 79.5285 883.456 81.5485 880.31 81.5485C877.162 81.5485 875.642 79.5285 876.913 77.0363C878.182 74.5441 881.764 72.5241 884.91 72.5241C888.058 72.5241 889.579 74.5441 888.307 77.0363Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M873.747 105.59C872.475 108.082 868.895 110.102 865.749 110.102C862.602 110.102 861.081 108.082 862.352 105.59C863.621 103.098 867.204 101.078 870.349 101.078C873.497 101.078 875.018 103.098 873.747 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M866.329 120.151C865.058 122.643 861.478 124.663 858.332 124.663C855.184 124.663 853.664 122.643 854.935 120.151C856.204 117.658 859.786 115.638 862.932 115.638C866.08 115.638 867.601 117.658 866.329 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M859.049 134.417C857.778 136.909 854.197 138.929 851.052 138.929C847.904 138.929 846.383 136.909 847.654 134.417C848.924 131.925 852.506 129.905 855.652 129.905C858.799 129.905 860.32 131.925 859.049 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M851.716 148.81C850.445 151.302 846.864 153.322 843.718 153.322C840.571 153.322 839.05 151.302 840.321 148.81C841.591 146.317 845.173 144.297 848.319 144.297C851.466 144.297 852.987 146.317 851.716 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M844.488 162.992C843.217 165.484 839.636 167.504 836.491 167.504C833.343 167.504 831.822 165.484 833.093 162.992C834.363 160.499 837.945 158.479 841.091 158.479C844.238 158.479 845.759 160.499 844.488 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M837.145 177.384C835.873 179.876 832.293 181.896 829.147 181.896C826 181.896 824.479 179.876 825.75 177.384C827.019 174.892 830.602 172.872 833.747 172.872C836.895 172.872 838.416 174.892 837.145 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M829.875 191.661C828.603 194.153 825.023 196.173 821.877 196.173C818.73 196.173 817.209 194.153 818.48 191.661C819.75 189.169 823.332 187.149 826.477 187.149C829.625 187.149 831.146 189.169 829.875 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M822.584 205.948C821.312 208.44 817.732 210.46 814.586 210.46C811.439 210.46 809.918 208.44 811.189 205.948C812.459 203.456 816.041 201.436 819.187 201.436C822.334 201.436 823.855 203.456 822.584 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M815.198 220.435C813.927 222.928 810.346 224.948 807.201 224.948C804.053 224.948 802.532 222.928 803.803 220.435C805.073 217.943 808.655 215.923 811.801 215.923C814.949 215.923 816.469 217.943 815.198 220.435Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M808.033 234.502C806.762 236.994 803.182 239.014 800.036 239.014C796.888 239.014 795.367 236.994 796.639 234.502C797.908 232.01 801.49 229.99 804.636 229.99C807.784 229.99 809.305 232.01 808.033 234.502Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M862.794 91.3551C861.523 93.8474 857.943 95.8674 854.797 95.8674C851.649 95.8674 850.128 93.8474 851.4 91.3551C852.669 88.8629 856.251 86.8429 859.397 86.8429C862.545 86.8429 864.066 88.8629 862.794 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M855.535 105.59C854.264 108.082 850.683 110.102 847.538 110.102C844.39 110.102 842.869 108.082 844.14 105.59C845.41 103.098 848.992 101.078 852.138 101.078C855.285 101.078 856.806 103.098 855.535 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M848.118 120.151C846.847 122.643 843.266 124.663 840.12 124.663C836.973 124.663 835.452 122.643 836.723 120.151C837.993 117.658 841.575 115.638 844.721 115.638C847.868 115.638 849.389 117.658 848.118 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M840.848 134.417C839.577 136.909 835.996 138.929 832.85 138.929C829.703 138.929 828.182 136.909 829.453 134.417C830.723 131.925 834.305 129.905 837.451 129.905C840.598 129.905 842.119 131.925 840.848 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M833.504 148.81C832.233 151.302 828.653 153.322 825.507 153.322C822.359 153.322 820.838 151.302 822.11 148.81C823.379 146.317 826.961 144.297 830.107 144.297C833.255 144.297 834.776 146.317 833.504 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M826.276 162.992C825.005 165.484 821.425 167.504 818.279 167.504C815.131 167.504 813.611 165.484 814.882 162.992C816.151 160.499 819.733 158.479 822.879 158.479C826.027 158.479 827.548 160.499 826.276 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M818.943 177.384C817.672 179.876 814.092 181.896 810.946 181.896C807.798 181.896 806.278 179.876 807.549 177.384C808.818 174.892 812.4 172.872 815.546 172.872C818.694 172.872 820.215 174.892 818.943 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M811.663 191.661C810.392 194.153 806.811 196.173 803.666 196.173C800.518 196.173 798.997 194.153 800.268 191.661C801.538 189.169 805.12 187.149 808.266 187.149C811.413 187.149 812.934 189.169 811.663 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M804.372 205.948C803.101 208.44 799.521 210.46 796.375 210.46C793.227 210.46 791.706 208.44 792.977 205.948C794.247 203.456 797.829 201.436 800.975 201.436C804.123 201.436 805.643 203.456 804.372 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M797.039 220.33C795.768 222.822 792.187 224.842 789.042 224.842C785.894 224.842 784.373 222.822 785.644 220.33C786.914 217.838 790.496 215.818 793.642 215.818C796.789 215.818 798.31 217.838 797.039 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M485.159 263.434C483.888 265.926 480.308 267.946 477.162 267.946C474.014 267.946 472.493 265.926 473.765 263.434C475.034 260.942 478.616 258.922 481.762 258.922C484.91 258.922 486.43 260.942 485.159 263.434Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M478.468 277.648C477.197 280.14 473.616 282.16 470.47 282.16C467.323 282.16 465.802 280.14 467.073 277.648C468.343 275.155 471.925 273.135 475.071 273.135C478.218 273.135 479.739 275.155 478.468 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M471.198 291.904C469.927 294.396 466.346 296.416 463.2 296.416C460.053 296.416 458.532 294.396 459.803 291.904C461.073 289.411 464.655 287.391 467.801 287.391C470.948 287.391 472.469 289.411 471.198 291.904Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M463.854 306.307C462.583 308.799 459.003 310.819 455.857 310.819C452.709 310.819 451.188 308.799 452.46 306.307C453.729 303.814 457.311 301.794 460.457 301.794C463.605 301.794 465.126 303.814 463.854 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M456.521 320.688C455.25 323.181 451.67 325.201 448.524 325.201C445.376 325.201 443.855 323.181 445.127 320.688C446.396 318.196 449.978 316.176 453.124 316.176C456.272 316.176 457.793 318.196 456.521 320.688Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M449.315 334.829C448.043 337.321 444.463 339.341 441.317 339.341C438.17 339.341 436.649 337.321 437.92 334.829C439.189 332.336 442.772 330.316 445.917 330.316C449.065 330.316 450.586 332.336 449.315 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M441.908 349.358C440.637 351.85 437.056 353.87 433.911 353.87C430.763 353.87 429.242 351.85 430.513 349.358C431.783 346.866 435.365 344.846 438.511 344.846C441.658 344.846 443.179 346.866 441.908 349.358Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M434.617 363.656C433.346 366.148 429.765 368.168 426.62 368.168C423.472 368.168 421.951 366.148 423.222 363.656C424.492 361.163 428.074 359.143 431.22 359.143C434.367 359.143 435.888 361.163 434.617 363.656Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M427.21 378.185C425.939 380.677 422.359 382.697 419.213 382.697C416.065 382.697 414.544 380.677 415.816 378.185C417.085 375.693 420.667 373.673 423.813 373.673C426.961 373.673 428.482 375.693 427.21 378.185Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M419.951 392.42C418.68 394.912 415.099 396.932 411.953 396.932C408.806 396.932 407.285 394.912 408.556 392.42C409.826 389.927 413.408 387.907 416.554 387.907C419.701 387.907 421.222 389.927 419.951 392.42Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M412.597 406.844C411.326 409.336 407.745 411.356 404.599 411.356C401.452 411.356 399.931 409.336 401.202 406.844C402.472 404.352 406.054 402.332 409.2 402.332C412.347 402.332 413.868 404.352 412.597 406.844Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M456.101 392.42C454.829 394.912 451.249 396.932 448.103 396.932C444.956 396.932 443.435 394.912 444.706 392.42C445.975 389.927 449.558 387.907 452.703 387.907C455.851 387.907 457.372 389.927 456.101 392.42Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M448.746 406.844C447.475 409.336 443.895 411.356 440.749 411.356C437.601 411.356 436.081 409.336 437.352 406.844C438.621 404.352 442.203 402.332 445.349 402.332C448.497 402.332 450.018 404.352 448.746 406.844Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M485.548 334.829C484.277 337.321 480.697 339.341 477.551 339.341C474.403 339.341 472.883 337.321 474.154 334.829C475.423 332.336 479.005 330.316 482.151 330.316C485.299 330.316 486.82 332.336 485.548 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M500.088 306.307C498.817 308.799 495.237 310.819 492.091 310.819C488.943 310.819 487.422 308.799 488.694 306.307C489.963 303.814 493.545 301.794 496.691 301.794C499.839 301.794 501.359 303.814 500.088 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M492.797 320.604C491.526 323.097 487.946 325.117 484.8 325.117C481.652 325.117 480.131 323.097 481.403 320.604C482.672 318.112 486.254 316.092 489.4 316.092C492.548 316.092 494.068 318.112 492.797 320.604Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M507.432 291.904C506.16 294.396 502.58 296.416 499.434 296.416C496.287 296.416 494.766 294.396 496.037 291.904C497.307 289.411 500.889 287.391 504.035 287.391C507.182 287.391 508.703 289.411 507.432 291.904Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M518.026 306.307C516.755 308.799 513.175 310.819 510.029 310.819C506.881 310.819 505.36 308.799 506.632 306.307C507.901 303.814 511.483 301.794 514.629 301.794C517.777 301.794 519.297 303.814 518.026 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M510.756 320.573C509.485 323.065 505.905 325.085 502.759 325.085C499.611 325.085 498.09 323.065 499.362 320.573C500.631 318.081 504.213 316.061 507.359 316.061C510.507 316.061 512.028 318.081 510.756 320.573Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M366.231 320.688C364.96 323.181 361.38 325.201 358.234 325.201C355.086 325.201 353.566 323.181 354.837 320.688C356.106 318.196 359.688 316.176 362.834 316.176C365.982 316.176 367.503 318.196 366.231 320.688Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M373.638 306.159C372.367 308.651 368.787 310.671 365.641 310.671C362.493 310.671 360.972 308.651 362.244 306.159C363.513 303.667 367.095 301.647 370.241 301.647C373.389 301.647 374.909 303.667 373.638 306.159Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M380.866 291.988C379.595 294.48 376.014 296.5 372.869 296.5C369.721 296.5 368.2 294.48 369.471 291.988C370.741 289.495 374.323 287.475 377.469 287.475C380.616 287.475 382.137 289.495 380.866 291.988Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M388.178 277.648C386.907 280.14 383.326 282.16 380.181 282.16C377.033 282.16 375.512 280.14 376.783 277.648C378.053 275.155 381.635 273.135 384.781 273.135C387.928 273.135 389.449 275.155 388.178 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M395.458 263.36C394.187 265.853 390.607 267.873 387.461 267.873C384.313 267.873 382.792 265.853 384.064 263.36C385.333 260.868 388.915 258.848 392.061 258.848C395.209 258.848 396.73 260.868 395.458 263.36Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M402.749 249.073C401.478 251.565 397.898 253.585 394.752 253.585C391.604 253.585 390.083 251.565 391.355 249.073C392.624 246.581 396.206 244.561 399.352 244.561C402.5 244.561 404.02 246.581 402.749 249.073Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M347.704 320.688C346.433 323.181 342.853 325.201 339.707 325.201C336.559 325.201 335.038 323.181 336.31 320.688C337.579 318.196 341.161 316.176 344.307 316.176C347.455 316.176 348.976 318.196 347.704 320.688Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M355.111 306.159C353.84 308.651 350.259 310.671 347.114 310.671C343.966 310.671 342.445 308.651 343.716 306.159C344.986 303.667 348.568 301.647 351.714 301.647C354.861 301.647 356.382 303.667 355.111 306.159Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M362.339 291.988C361.067 294.48 357.487 296.5 354.341 296.5C351.194 296.5 349.673 294.48 350.944 291.988C352.214 289.495 355.796 287.475 358.942 287.475C362.089 287.475 363.61 289.495 362.339 291.988Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M369.651 277.648C368.38 280.14 364.799 282.16 361.653 282.16C358.506 282.16 356.985 280.14 358.256 277.648C359.526 275.155 363.108 273.135 366.254 273.135C369.401 273.135 370.922 275.155 369.651 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M376.942 263.36C375.67 265.853 372.09 267.873 368.944 267.873C365.797 267.873 364.276 265.853 365.547 263.36C366.817 260.868 370.399 258.848 373.544 258.848C376.692 258.848 378.213 260.868 376.942 263.36Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M337.225 306.159C335.954 308.651 332.374 310.671 329.228 310.671C326.08 310.671 324.56 308.651 325.831 306.159C327.1 303.667 330.682 301.647 333.828 301.647C336.976 301.647 338.497 303.667 337.225 306.159Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M344.453 291.988C343.182 294.48 339.602 296.5 336.456 296.5C333.308 296.5 331.787 294.48 333.059 291.988C334.328 289.495 337.91 287.475 341.056 287.475C344.204 287.475 345.725 289.495 344.453 291.988Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M351.765 277.648C350.494 280.14 346.914 282.16 343.768 282.16C340.62 282.16 339.099 280.14 340.371 277.648C341.64 275.155 345.222 273.135 348.368 273.135C351.516 273.135 353.037 275.155 351.765 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M384.191 320.688C382.919 323.181 379.339 325.201 376.193 325.201C373.045 325.201 371.525 323.181 372.796 320.688C374.065 318.196 377.648 316.176 380.793 316.176C383.941 316.176 385.462 318.196 384.191 320.688Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M391.597 306.159C390.326 308.651 386.746 310.671 383.6 310.671C380.452 310.671 378.931 308.651 380.203 306.159C381.472 303.667 385.054 301.647 388.2 301.647C391.348 301.647 392.868 303.667 391.597 306.159Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M398.825 291.988C397.554 294.48 393.973 296.5 390.828 296.5C387.68 296.5 386.159 294.48 387.43 291.988C388.7 289.495 392.282 287.475 395.428 287.475C398.575 287.475 400.096 289.495 398.825 291.988Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M406.137 277.648C404.866 280.14 401.285 282.16 398.14 282.16C394.992 282.16 393.471 280.14 394.742 277.648C396.012 275.155 399.594 273.135 402.74 273.135C405.887 273.135 407.408 275.155 406.137 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M413.417 263.36C412.146 265.853 408.566 267.873 405.42 267.873C402.272 267.873 400.752 265.853 402.023 263.36C403.292 260.868 406.874 258.848 410.02 258.848C413.168 258.848 414.689 260.868 413.417 263.36Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M420.708 249.073C419.437 251.565 415.857 253.585 412.711 253.585C409.563 253.585 408.043 251.565 409.314 249.073C410.583 246.581 414.165 244.561 417.311 244.561C420.459 244.561 421.98 246.581 420.708 249.073Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M402.171 320.688C400.899 323.181 397.319 325.201 394.173 325.201C391.026 325.201 389.505 323.181 390.776 320.688C392.045 318.196 395.628 316.176 398.773 316.176C401.921 316.176 403.442 318.196 402.171 320.688Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M409.577 306.159C408.306 308.651 404.726 310.671 401.58 310.671C398.432 310.671 396.911 308.651 398.183 306.159C399.452 303.667 403.034 301.647 406.18 301.647C409.328 301.647 410.849 303.667 409.577 306.159Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M416.816 291.988C415.544 294.48 411.964 296.5 408.818 296.5C405.671 296.5 404.15 294.48 405.421 291.988C406.691 289.495 410.273 287.475 413.419 287.475C416.566 287.475 418.087 289.495 416.816 291.988Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M424.128 277.648C422.856 280.14 419.276 282.16 416.13 282.16C412.983 282.16 411.462 280.14 412.733 277.648C414.003 275.155 417.585 273.135 420.73 273.135C423.878 273.135 425.399 275.155 424.128 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M431.408 263.36C430.137 265.853 426.557 267.873 423.411 267.873C420.263 267.873 418.742 265.853 420.014 263.36C421.283 260.868 424.865 258.848 428.011 258.848C431.159 258.848 432.679 260.868 431.408 263.36Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M438.689 249.073C437.417 251.565 433.837 253.585 430.691 253.585C427.543 253.585 426.023 251.565 427.294 249.073C428.563 246.581 432.145 244.561 435.291 244.561C438.439 244.561 439.96 246.581 438.689 249.073Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M387.589 349.316C386.317 351.808 382.737 353.828 379.591 353.828C376.444 353.828 374.923 351.808 376.194 349.316C377.464 346.824 381.046 344.804 384.192 344.804C387.339 344.804 388.86 346.824 387.589 349.316Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M394.995 334.776C393.724 337.268 390.144 339.288 386.998 339.288C383.85 339.288 382.33 337.268 383.601 334.776C384.87 332.284 388.452 330.264 391.598 330.264C394.746 330.264 396.267 332.284 394.995 334.776Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M413.228 334.829C411.957 337.321 408.377 339.341 405.231 339.341C402.083 339.341 400.562 337.321 401.834 334.829C403.103 332.336 406.685 330.316 409.831 330.316C412.979 330.316 414.499 332.336 413.228 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M420.635 320.299C419.364 322.792 415.783 324.812 412.637 324.812C409.49 324.812 407.969 322.792 409.24 320.299C410.51 317.807 414.092 315.787 417.238 315.787C420.385 315.787 421.906 317.807 420.635 320.299Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M427.863 306.128C426.591 308.62 423.011 310.64 419.865 310.64C416.718 310.64 415.197 308.62 416.468 306.128C417.737 303.635 421.32 301.615 424.465 301.615C427.613 301.615 429.134 303.635 427.863 306.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M435.175 291.788C433.903 294.28 430.323 296.3 427.177 296.3C424.03 296.3 422.509 294.28 423.78 291.788C425.049 289.296 428.632 287.276 431.777 287.276C434.925 287.276 436.446 289.296 435.175 291.788Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M442.466 277.5C441.194 279.993 437.614 282.013 434.468 282.013C431.321 282.013 429.8 279.993 431.071 277.5C432.34 275.008 435.923 272.988 439.068 272.988C442.216 272.988 443.737 275.008 442.466 277.5Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M449.746 263.213C448.475 265.705 444.894 267.725 441.749 267.725C438.601 267.725 437.08 265.705 438.351 263.213C439.621 260.721 443.203 258.701 446.349 258.701C449.496 258.701 451.017 260.721 449.746 263.213Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M398.636 363.445C397.364 365.938 393.784 367.958 390.638 367.958C387.491 367.958 385.97 365.938 387.241 363.445C388.511 360.953 392.093 358.933 395.239 358.933C398.386 358.933 399.907 360.953 398.636 363.445Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M406.042 348.916C404.771 351.408 401.191 353.428 398.045 353.428C394.897 353.428 393.376 351.408 394.648 348.916C395.917 346.424 399.499 344.404 402.645 344.404C405.793 344.404 407.314 346.424 406.042 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M376.552 406.77C375.281 409.262 371.701 411.282 368.555 411.282C365.407 411.282 363.887 409.262 365.158 406.77C366.427 404.278 370.009 402.258 373.155 402.258C376.303 402.258 377.824 404.278 376.552 406.77Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M383.959 392.241C382.688 394.733 379.108 396.753 375.962 396.753C372.814 396.753 371.293 394.733 372.565 392.241C373.834 389.749 377.416 387.729 380.562 387.729C383.71 387.729 385.23 389.749 383.959 392.241Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M391.187 378.069C389.916 380.561 386.335 382.581 383.189 382.581C380.042 382.581 378.521 380.561 379.792 378.069C381.062 375.577 384.644 373.557 387.79 373.557C390.937 373.557 392.458 375.577 391.187 378.069Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M369.367 420.858C368.095 423.35 364.515 425.37 361.369 425.37C358.222 425.37 356.701 423.35 357.972 420.858C359.242 418.365 362.824 416.345 365.97 416.345C369.117 416.345 370.638 418.365 369.367 420.858Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M431.24 334.829C429.969 337.321 426.388 339.341 423.242 339.341C420.095 339.341 418.574 337.321 419.845 334.829C421.115 332.336 424.697 330.316 427.843 330.316C430.99 330.316 432.511 332.336 431.24 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M438.646 320.299C437.375 322.792 433.795 324.812 430.649 324.812C427.501 324.812 425.981 322.792 427.252 320.299C428.521 317.807 432.103 315.787 435.249 315.787C438.397 315.787 439.918 317.807 438.646 320.299Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M445.874 306.128C444.603 308.62 441.023 310.64 437.877 310.64C434.729 310.64 433.208 308.62 434.48 306.128C435.749 303.635 439.331 301.615 442.477 301.615C445.625 301.615 447.145 303.635 445.874 306.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M453.186 291.788C451.915 294.28 448.335 296.3 445.189 296.3C442.041 296.3 440.52 294.28 441.792 291.788C443.061 289.296 446.643 287.276 449.789 287.276C452.937 287.276 454.458 289.296 453.186 291.788Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M460.467 277.5C459.195 279.993 455.615 282.013 452.469 282.013C449.322 282.013 447.801 279.993 449.072 277.5C450.341 275.008 453.924 272.988 457.069 272.988C460.217 272.988 461.738 275.008 460.467 277.5Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M467.758 263.213C466.486 265.705 462.906 267.725 459.76 267.725C456.613 267.725 455.092 265.705 456.363 263.213C457.633 260.721 461.215 258.701 464.36 258.701C467.508 258.701 469.029 260.721 467.758 263.213Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M416.647 363.445C415.376 365.938 411.796 367.958 408.65 367.958C405.502 367.958 403.981 365.938 405.253 363.445C406.522 360.953 410.104 358.933 413.25 358.933C416.398 358.933 417.919 360.953 416.647 363.445Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M424.054 348.916C422.783 351.408 419.202 353.428 416.057 353.428C412.909 353.428 411.388 351.408 412.659 348.916C413.929 346.424 417.511 344.404 420.657 344.404C423.804 344.404 425.325 346.424 424.054 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M394.564 406.77C393.293 409.262 389.713 411.282 386.567 411.282C383.419 411.282 381.898 409.262 383.17 406.77C384.439 404.278 388.021 402.258 391.167 402.258C394.315 402.258 395.835 404.278 394.564 406.77Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M401.971 392.241C400.7 394.733 397.119 396.753 393.973 396.753C390.826 396.753 389.305 394.733 390.576 392.241C391.846 389.749 395.428 387.729 398.574 387.729C401.721 387.729 403.242 389.749 401.971 392.241Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M409.199 378.069C407.927 380.561 404.347 382.581 401.201 382.581C398.054 382.581 396.533 380.561 397.804 378.069C399.073 375.577 402.656 373.557 405.801 373.557C408.949 373.557 410.47 375.577 409.199 378.069Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M387.378 420.858C386.107 423.35 382.527 425.37 379.381 425.37C376.233 425.37 374.713 423.35 375.984 420.858C377.253 418.365 380.835 416.345 383.981 416.345C387.129 416.345 388.65 418.365 387.378 420.858Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M474.964 249.073C473.693 251.565 470.113 253.585 466.967 253.585C463.819 253.585 462.298 251.565 463.57 249.073C464.839 246.581 468.421 244.561 471.567 244.561C474.715 244.561 476.236 246.581 474.964 249.073Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M503.455 263.434C502.184 265.926 498.603 267.946 495.457 267.946C492.31 267.946 490.789 265.926 492.06 263.434C493.33 260.942 496.912 258.922 500.058 258.922C503.205 258.922 504.726 260.942 503.455 263.434Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M496.764 277.648C495.492 280.14 491.912 282.16 488.766 282.16C485.619 282.16 484.098 280.14 485.369 277.648C486.638 275.155 490.221 273.135 493.366 273.135C496.514 273.135 498.035 275.155 496.764 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M489.494 291.904C488.222 294.396 484.642 296.416 481.496 296.416C478.349 296.416 476.828 294.396 478.099 291.904C479.369 289.411 482.951 287.391 486.097 287.391C489.244 287.391 490.765 289.411 489.494 291.904Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M482.15 306.307C480.879 308.799 477.299 310.819 474.153 310.819C471.005 310.819 469.484 308.799 470.756 306.307C472.025 303.814 475.607 301.794 478.753 301.794C481.901 301.794 483.421 303.814 482.15 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M474.817 320.688C473.546 323.181 469.966 325.201 466.82 325.201C463.672 325.201 462.151 323.181 463.423 320.688C464.692 318.196 468.274 316.176 471.42 316.176C474.568 316.176 476.088 318.196 474.817 320.688Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M467.61 334.829C466.339 337.321 462.759 339.341 459.613 339.341C456.465 339.341 454.944 337.321 456.216 334.829C457.485 332.336 461.067 330.316 464.213 330.316C467.361 330.316 468.882 332.336 467.61 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M460.204 349.358C458.932 351.85 455.352 353.87 452.206 353.87C449.059 353.87 447.538 351.85 448.809 349.358C450.078 346.866 453.661 344.846 456.806 344.846C459.954 344.846 461.475 346.866 460.204 349.358Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M452.913 363.656C451.641 366.148 448.061 368.168 444.915 368.168C441.768 368.168 440.247 366.148 441.518 363.656C442.788 361.163 446.37 359.143 449.515 359.143C452.663 359.143 454.184 361.163 452.913 363.656Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M445.506 378.185C444.235 380.677 440.654 382.697 437.509 382.697C434.361 382.697 432.84 380.677 434.111 378.185C435.381 375.693 438.963 373.673 442.109 373.673C445.256 373.673 446.777 375.693 445.506 378.185Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M510.819 248.999C509.548 251.492 505.968 253.512 502.822 253.512C499.674 253.512 498.154 251.492 499.425 248.999C500.694 246.507 504.276 244.487 507.422 244.487C510.57 244.487 512.091 246.507 510.819 248.999Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M521.887 263.434C520.616 265.926 517.036 267.946 513.89 267.946C510.742 267.946 509.221 265.926 510.493 263.434C511.762 260.942 515.344 258.922 518.49 258.922C521.638 258.922 523.159 260.942 521.887 263.434Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M529.252 248.999C527.981 251.492 524.4 253.512 521.255 253.512C518.107 253.512 516.586 251.492 517.857 248.999C519.127 246.507 522.709 244.487 525.855 244.487C529.002 244.487 530.523 246.507 529.252 248.999Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M539.889 263.434C538.617 265.926 535.037 267.946 531.891 267.946C528.743 267.946 527.223 265.926 528.494 263.434C529.763 260.942 533.346 258.922 536.491 258.922C539.639 258.922 541.16 260.942 539.889 263.434Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M547.243 248.999C545.971 251.492 542.391 253.512 539.245 253.512C536.098 253.512 534.577 251.492 535.848 248.999C537.117 246.507 540.7 244.487 543.845 244.487C546.993 244.487 548.514 246.507 547.243 248.999Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M558.311 263.434C557.039 265.926 553.459 267.946 550.313 267.946C547.166 267.946 545.645 265.926 546.916 263.434C548.185 260.942 551.768 258.922 554.913 258.922C558.061 258.922 559.582 260.942 558.311 263.434Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M565.675 248.999C564.404 251.492 560.824 253.512 557.678 253.512C554.53 253.512 553.009 251.492 554.281 248.999C555.55 246.507 559.132 244.487 562.278 244.487C565.426 244.487 566.946 246.507 565.675 248.999Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M543.718 292.082C542.447 294.575 538.867 296.595 535.721 296.595C532.573 296.595 531.052 294.575 532.324 292.082C533.593 289.59 537.175 287.57 540.321 287.57C543.469 287.57 544.989 289.59 543.718 292.082Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M551.072 277.648C549.801 280.14 546.221 282.16 543.075 282.16C539.927 282.16 538.406 280.14 539.678 277.648C540.947 275.155 544.529 273.135 547.675 273.135C550.823 273.135 552.343 275.155 551.072 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M532.734 277.648C531.463 280.14 527.883 282.16 524.737 282.16C521.589 282.16 520.069 280.14 521.34 277.648C522.609 275.155 526.191 273.135 529.337 273.135C532.485 273.135 534.006 275.155 532.734 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M576.575 263.434C575.303 265.926 571.723 267.946 568.577 267.946C565.43 267.946 563.909 265.926 565.18 263.434C566.45 260.942 570.032 258.922 573.178 258.922C576.325 258.922 577.846 260.942 576.575 263.434Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M569.336 277.648C568.065 280.14 564.485 282.16 561.339 282.16C558.191 282.16 556.671 280.14 557.942 277.648C559.211 275.155 562.793 273.135 565.939 273.135C569.087 273.135 570.608 275.155 569.336 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M851.459 73.3605C853.896 74.9945 851.657 79.162 847.541 80.7098C841.381 83.026 837.852 78.4743 843.051 74.6103C845.941 72.461 849.387 71.9726 851.459 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M844.835 91.3551C843.564 93.8474 839.984 95.8674 836.838 95.8674C833.69 95.8674 832.169 93.8474 833.441 91.3551C834.71 88.8629 838.292 86.8429 841.438 86.8429C844.586 86.8429 846.106 88.8629 844.835 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M837.576 105.59C836.305 108.082 832.724 110.102 829.578 110.102C826.431 110.102 824.91 108.082 826.181 105.59C827.451 103.098 831.033 101.078 834.179 101.078C837.326 101.078 838.847 103.098 837.576 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M830.148 120.151C828.877 122.643 825.297 124.663 822.151 124.663C819.003 124.663 817.482 122.643 818.754 120.151C820.023 117.658 823.605 115.638 826.751 115.638C829.899 115.638 831.419 117.658 830.148 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M822.878 134.417C821.607 136.909 818.027 138.929 814.881 138.929C811.733 138.929 810.212 136.909 811.484 134.417C812.753 131.925 816.335 129.905 819.481 129.905C822.629 129.905 824.15 131.925 822.878 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M815.545 148.81C814.274 151.302 810.694 153.322 807.548 153.322C804.4 153.322 802.879 151.302 804.151 148.81C805.42 146.317 809.002 144.297 812.148 144.297C815.296 144.297 816.817 146.317 815.545 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M808.317 162.992C807.046 165.484 803.466 167.504 800.32 167.504C797.172 167.504 795.652 165.484 796.923 162.992C798.192 160.499 801.774 158.479 804.92 158.479C808.068 158.479 809.589 160.499 808.317 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M800.974 177.384C799.703 179.876 796.122 181.896 792.976 181.896C789.829 181.896 788.308 179.876 789.579 177.384C790.849 174.892 794.431 172.872 797.577 172.872C800.724 172.872 802.245 174.892 800.974 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M793.693 191.661C792.422 194.153 788.842 196.173 785.696 196.173C782.548 196.173 781.028 194.153 782.299 191.661C783.568 189.169 787.15 187.149 790.296 187.149C793.444 187.149 794.965 189.169 793.693 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M786.413 205.948C785.142 208.44 781.561 210.46 778.416 210.46C775.268 210.46 773.747 208.44 775.018 205.948C776.288 203.456 779.87 201.436 783.016 201.436C786.163 201.436 787.684 203.456 786.413 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M779.08 220.33C777.809 222.822 774.228 224.842 771.083 224.842C767.935 224.842 766.414 222.822 767.685 220.33C768.955 217.838 772.537 215.818 775.683 215.818C778.83 215.818 780.351 217.838 779.08 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M771.81 234.586C770.539 237.078 766.958 239.098 763.813 239.098C760.665 239.098 759.144 237.078 760.415 234.586C761.685 232.094 765.267 230.074 768.413 230.074C771.561 230.074 773.081 232.094 771.81 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M764.519 248.884C763.248 251.376 759.667 253.396 756.522 253.396C753.374 253.396 751.853 251.376 753.124 248.884C754.394 246.392 757.976 244.372 761.122 244.372C764.269 244.372 765.79 246.392 764.519 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M757.186 263.266C755.915 265.758 752.335 267.778 749.189 267.778C746.041 267.778 744.52 265.758 745.792 263.266C747.061 260.774 750.643 258.754 753.789 258.754C756.937 258.754 758.457 260.774 757.186 263.266Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M782.478 248.884C781.207 251.376 777.627 253.396 774.481 253.396C771.333 253.396 769.812 251.376 771.084 248.884C772.353 246.392 775.935 244.372 779.081 244.372C782.229 244.372 783.749 246.392 782.478 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M919.975 200.383C918.704 202.875 915.124 204.895 911.978 204.895C908.83 204.895 907.309 202.875 908.581 200.383C909.85 197.89 913.432 195.87 916.578 195.87C919.726 195.87 921.246 197.89 919.975 200.383Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M853.915 243.371C852.644 245.863 849.063 247.883 845.917 247.883C842.77 247.883 841.249 245.863 842.52 243.371C843.79 240.879 847.372 238.859 850.518 238.859C853.665 238.859 855.186 240.879 853.915 243.371Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M870.401 243.371C869.13 245.863 865.549 247.883 862.404 247.883C859.256 247.883 857.735 245.863 859.006 243.371C860.276 240.879 863.858 238.859 867.004 238.859C870.151 238.859 871.672 240.879 870.401 243.371Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M775.145 263.266C773.874 265.758 770.294 267.778 767.148 267.778C764 267.778 762.479 265.758 763.751 263.266C765.02 260.774 768.602 258.754 771.748 258.754C774.896 258.754 776.416 260.774 775.145 263.266Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M902.511 205.896C901.239 208.388 897.659 210.408 894.513 210.408C891.366 210.408 889.845 208.388 891.116 205.896C892.385 203.403 895.968 201.383 899.113 201.383C902.261 201.383 903.782 203.403 902.511 205.896Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M924.299 77.1836C923.028 79.6758 919.448 81.6958 916.302 81.6958C913.154 81.6958 911.633 79.6758 912.905 77.1836C914.174 74.6913 917.756 72.6713 920.902 72.6713C924.05 72.6713 925.57 74.6913 924.299 77.1836Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M917.04 91.4183C915.769 93.9105 912.188 95.9305 909.042 95.9305C905.895 95.9305 904.374 93.9105 905.645 91.4183C906.915 88.9261 910.497 86.9061 913.643 86.9061C916.79 86.9061 918.311 88.9261 917.04 91.4183Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M909.623 105.979C908.351 108.471 904.771 110.491 901.625 110.491C898.478 110.491 896.957 108.471 898.228 105.979C899.497 103.487 903.08 101.467 906.225 101.467C909.373 101.467 910.894 103.487 909.623 105.979Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M902.342 120.245C901.071 122.738 897.491 124.758 894.345 124.758C891.197 124.758 889.676 122.738 890.948 120.245C892.217 117.753 895.799 115.733 898.945 115.733C902.093 115.733 903.613 117.753 902.342 120.245Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M895.009 134.638C893.738 137.13 890.158 139.15 887.012 139.15C883.864 139.15 882.343 137.13 883.615 134.638C884.884 132.146 888.466 130.126 891.612 130.126C894.76 130.126 896.28 132.146 895.009 134.638Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M887.781 148.81C886.51 151.302 882.93 153.322 879.784 153.322C876.636 153.322 875.115 151.302 876.387 148.81C877.656 146.317 881.238 144.297 884.384 144.297C887.532 144.297 889.053 146.317 887.781 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M942.311 77.1836C941.04 79.6758 937.459 81.6958 934.314 81.6958C931.166 81.6958 929.645 79.6758 930.916 77.1836C932.186 74.6913 935.768 72.6713 938.914 72.6713C942.061 72.6713 943.582 74.6913 942.311 77.1836Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M935.051 91.4183C933.78 93.9105 930.2 95.9305 927.054 95.9305C923.906 95.9305 922.386 93.9105 923.657 91.4183C924.926 88.9261 928.508 86.9061 931.654 86.9061C934.802 86.9061 936.323 88.9261 935.051 91.4183Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M927.634 105.979C926.363 108.471 922.783 110.491 919.637 110.491C916.489 110.491 914.969 108.471 916.24 105.979C917.509 103.487 921.091 101.467 924.237 101.467C927.385 101.467 928.906 103.487 927.634 105.979Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M920.354 120.245C919.083 122.738 915.502 124.758 912.356 124.758C909.209 124.758 907.688 122.738 908.959 120.245C910.229 117.753 913.811 115.733 916.957 115.733C920.104 115.733 921.625 117.753 920.354 120.245Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M913.021 134.638C911.75 137.13 908.169 139.15 905.023 139.15C901.876 139.15 900.355 137.13 901.626 134.638C902.896 132.146 906.478 130.126 909.624 130.126C912.771 130.126 914.292 132.146 913.021 134.638Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M905.793 148.81C904.522 151.302 900.941 153.322 897.796 153.322C894.648 153.322 893.127 151.302 894.398 148.81C895.668 146.317 899.25 144.297 902.396 144.297C905.543 144.297 907.064 146.317 905.793 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M953.779 91.4183C952.507 93.9105 948.927 95.9305 945.781 95.9305C942.634 95.9305 941.113 93.9105 942.384 91.4183C943.654 88.9261 947.236 86.9061 950.381 86.9061C953.529 86.9061 955.05 88.9261 953.779 91.4183Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M946.362 105.979C945.09 108.471 941.51 110.491 938.364 110.491C935.217 110.491 933.696 108.471 934.967 105.979C936.236 103.487 939.819 101.467 942.964 101.467C946.112 101.467 947.633 103.487 946.362 105.979Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M939.081 120.245C937.81 122.738 934.23 124.758 931.084 124.758C927.936 124.758 926.415 122.738 927.687 120.245C928.956 117.753 932.538 115.733 935.684 115.733C938.832 115.733 940.352 117.753 939.081 120.245Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M931.748 134.638C930.477 137.13 926.896 139.15 923.75 139.15C920.603 139.15 919.082 137.13 920.353 134.638C921.623 132.146 925.205 130.126 928.351 130.126C931.498 130.126 933.019 132.146 931.748 134.638Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M963.879 105.979C962.607 108.471 959.027 110.491 955.881 110.491C952.734 110.491 951.213 108.471 952.484 105.979C953.754 103.487 957.336 101.467 960.482 101.467C963.629 101.467 965.15 103.487 963.879 105.979Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M956.609 120.245C955.337 122.738 951.757 124.758 948.611 124.758C945.464 124.758 943.943 122.738 945.214 120.245C946.484 117.753 950.066 115.733 953.212 115.733C956.359 115.733 957.88 117.753 956.609 120.245Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M949.265 134.638C947.994 137.13 944.414 139.15 941.268 139.15C938.12 139.15 936.599 137.13 937.871 134.638C939.14 132.146 942.722 130.126 945.868 130.126C949.016 130.126 950.536 132.146 949.265 134.638Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M942.195 148.515C940.924 151.007 937.344 153.027 934.198 153.027C931.05 153.027 929.529 151.007 930.801 148.515C932.07 146.023 935.652 144.003 938.798 144.003C941.946 144.003 943.466 146.023 942.195 148.515Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M935.051 162.529C933.78 165.021 930.2 167.041 927.054 167.041C923.906 167.041 922.386 165.021 923.657 162.529C924.926 160.036 928.508 158.016 931.654 158.016C934.802 158.016 936.323 160.036 935.051 162.529Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M909.349 177.3C908.078 179.792 904.497 181.812 901.352 181.812C898.204 181.812 896.683 179.792 897.954 177.3C899.224 174.808 902.806 172.788 905.952 172.788C909.1 172.788 910.62 174.808 909.349 177.3Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M982.027 105.979C980.756 108.471 977.176 110.491 974.03 110.491C970.882 110.491 969.361 108.471 970.633 105.979C971.902 103.487 975.484 101.467 978.63 101.467C981.778 101.467 983.298 103.487 982.027 105.979Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M974.747 120.245C973.475 122.738 969.895 124.758 966.749 124.758C963.602 124.758 962.081 122.738 963.352 120.245C964.622 117.753 968.204 115.733 971.349 115.733C974.497 115.733 976.018 117.753 974.747 120.245Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M967.414 134.638C966.142 137.13 962.562 139.15 959.416 139.15C956.269 139.15 954.748 137.13 956.019 134.638C957.289 132.146 960.871 130.126 964.016 130.126C967.164 130.126 968.685 132.146 967.414 134.638Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M1000.22 105.979C998.946 108.471 995.366 110.491 992.22 110.491C989.073 110.491 987.552 108.471 988.823 105.979C990.092 103.487 993.675 101.467 996.82 101.467C999.968 101.467 1001.49 103.487 1000.22 105.979Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M992.937 120.245C991.666 122.738 988.086 124.758 984.94 124.758C981.792 124.758 980.271 122.738 981.542 120.245C982.812 117.753 986.394 115.733 989.54 115.733C992.688 115.733 994.208 117.753 992.937 120.245Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M985.604 134.638C984.333 137.13 980.753 139.15 977.607 139.15C974.459 139.15 972.938 137.13 974.21 134.638C975.479 132.146 979.061 130.126 982.207 130.126C985.355 130.126 986.875 132.146 985.604 134.638Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M832.101 73.3605C834.537 74.9945 832.298 79.162 828.183 80.7098C822.022 83.026 818.494 78.4743 823.692 74.6103C826.585 72.461 830.029 71.9726 832.101 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M825.477 91.3551C824.206 93.8474 820.625 95.8674 817.479 95.8674C814.332 95.8674 812.811 93.8474 814.082 91.3551C815.352 88.8629 818.934 86.8429 822.08 86.8429C825.227 86.8429 826.748 88.8629 825.477 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M818.218 105.59C816.946 108.082 813.366 110.102 810.22 110.102C807.073 110.102 805.552 108.082 806.823 105.59C808.092 103.098 811.675 101.078 814.82 101.078C817.968 101.078 819.489 103.098 818.218 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M810.8 120.151C809.529 122.643 805.949 124.663 802.803 124.663C799.655 124.663 798.134 122.643 799.406 120.151C800.675 117.658 804.257 115.638 807.403 115.638C810.551 115.638 812.072 117.658 810.8 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M803.53 134.417C802.259 136.909 798.679 138.929 795.533 138.929C792.385 138.929 790.865 136.909 792.136 134.417C793.405 131.925 796.987 129.905 800.133 129.905C803.281 129.905 804.802 131.925 803.53 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M796.187 148.81C794.916 151.302 791.335 153.322 788.189 153.322C785.042 153.322 783.521 151.302 784.792 148.81C786.062 146.317 789.644 144.297 792.79 144.297C795.937 144.297 797.458 146.317 796.187 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M788.959 162.992C787.688 165.484 784.107 167.504 780.962 167.504C777.814 167.504 776.293 165.484 777.564 162.992C778.834 160.499 782.416 158.479 785.562 158.479C788.709 158.479 790.23 160.499 788.959 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M781.626 177.384C780.355 179.876 776.774 181.896 773.629 181.896C770.481 181.896 768.96 179.876 770.231 177.384C771.501 174.892 775.083 172.872 778.229 172.872C781.376 172.872 782.897 174.892 781.626 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M774.346 191.661C773.074 194.153 769.494 196.173 766.348 196.173C763.201 196.173 761.68 194.153 762.951 191.661C764.22 189.169 767.803 187.149 770.948 187.149C774.096 187.149 775.617 189.169 774.346 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M767.055 205.948C765.783 208.44 762.203 210.46 759.057 210.46C755.91 210.46 754.389 208.44 755.66 205.948C756.93 203.456 760.512 201.436 763.657 201.436C766.805 201.436 768.326 203.456 767.055 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M759.722 220.33C758.45 222.822 754.87 224.842 751.724 224.842C748.577 224.842 747.056 222.822 748.327 220.33C749.596 217.838 753.179 215.818 756.324 215.818C759.472 215.818 760.993 217.838 759.722 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M752.452 234.586C751.18 237.078 747.6 239.098 744.454 239.098C741.307 239.098 739.786 237.078 741.057 234.586C742.327 232.094 745.909 230.074 749.055 230.074C752.202 230.074 753.723 232.094 752.452 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M745.171 248.884C743.9 251.376 740.32 253.396 737.174 253.396C734.026 253.396 732.505 251.376 733.777 248.884C735.046 246.392 738.628 244.372 741.774 244.372C744.922 244.372 746.443 246.392 745.171 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M730.063 278.521C728.792 281.013 725.212 283.033 722.066 283.033C718.918 283.033 717.398 281.013 718.669 278.521C719.938 276.029 723.52 274.009 726.666 274.009C729.814 274.009 731.335 276.029 730.063 278.521Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M722.793 292.777C721.522 295.269 717.942 297.289 714.796 297.289C711.648 297.289 710.128 295.269 711.399 292.777C712.668 290.284 716.25 288.264 719.396 288.264C722.544 288.264 724.065 290.284 722.793 292.777Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M715.502 307.064C714.231 309.556 710.651 311.576 707.505 311.576C704.357 311.576 702.837 309.556 704.108 307.064C705.377 304.572 708.959 302.552 712.105 302.552C715.253 302.552 716.774 304.572 715.502 307.064Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M708.17 321.446C706.898 323.938 703.318 325.958 700.172 325.958C697.024 325.958 695.504 323.938 696.775 321.446C698.044 318.954 701.627 316.934 704.772 316.934C707.92 316.934 709.441 318.954 708.17 321.446Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M727.528 321.446C726.257 323.938 722.676 325.958 719.53 325.958C716.383 325.958 714.862 323.938 716.133 321.446C717.403 318.954 720.985 316.934 724.131 316.934C727.278 316.934 728.799 318.954 727.528 321.446Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M745.371 321.446C744.1 323.938 740.52 325.958 737.374 325.958C734.226 325.958 732.705 323.938 733.977 321.446C735.246 318.954 738.828 316.934 741.974 316.934C745.122 316.934 746.642 318.954 745.371 321.446Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M720.721 334.786C719.45 337.279 715.869 339.299 712.723 339.299C709.576 339.299 708.055 337.279 709.326 334.786C710.596 332.294 714.178 330.274 717.324 330.274C720.471 330.274 721.992 332.294 720.721 334.786Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M753.094 306.307C751.822 308.799 748.242 310.819 745.096 310.819C741.949 310.819 740.428 308.799 741.699 306.307C742.968 303.814 746.551 301.794 749.696 301.794C752.844 301.794 754.365 303.814 753.094 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M771.421 306.307C770.15 308.799 766.569 310.819 763.423 310.819C760.276 310.819 758.755 308.799 760.026 306.307C761.296 303.814 764.878 301.794 768.024 301.794C771.171 301.794 772.692 303.814 771.421 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M846.704 44.7333C849.14 46.3672 846.901 50.5347 842.786 52.0825C836.625 54.3988 833.097 49.847 838.295 45.983C841.188 43.8337 844.632 43.3453 846.704 44.7333Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M840.08 62.7279C838.809 65.2202 835.228 67.2402 832.082 67.2402C828.935 67.2402 827.414 65.2202 828.685 62.7279C829.955 60.2357 833.537 58.2157 836.683 58.2157C839.83 58.2157 841.351 60.2357 840.08 62.7279Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M815.267 73.3605C817.704 74.9945 815.465 79.162 811.35 80.7098C805.189 83.026 801.661 78.4743 806.859 74.6103C809.751 72.461 813.195 71.9726 815.267 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M808.644 91.3551C807.372 93.8474 803.792 95.8674 800.646 95.8674C797.499 95.8674 795.978 93.8474 797.249 91.3551C798.518 88.8629 802.101 86.8429 805.246 86.8429C808.394 86.8429 809.915 88.8629 808.644 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M801.384 105.59C800.113 108.082 796.533 110.102 793.387 110.102C790.239 110.102 788.718 108.082 789.99 105.59C791.259 103.098 794.841 101.078 797.987 101.078C801.135 101.078 802.655 103.098 801.384 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M793.967 120.151C792.696 122.643 789.115 124.663 785.97 124.663C782.822 124.663 781.301 122.643 782.572 120.151C783.842 117.658 787.424 115.638 790.57 115.638C793.717 115.638 795.238 117.658 793.967 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M786.697 134.417C785.426 136.909 781.845 138.929 778.7 138.929C775.552 138.929 774.031 136.909 775.302 134.417C776.572 131.925 780.154 129.905 783.3 129.905C786.447 129.905 787.968 131.925 786.697 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M779.354 148.81C778.082 151.302 774.502 153.322 771.356 153.322C768.209 153.322 766.688 151.302 767.959 148.81C769.228 146.317 772.811 144.297 775.956 144.297C779.104 144.297 780.625 146.317 779.354 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M772.126 162.992C770.854 165.484 767.274 167.504 764.128 167.504C760.981 167.504 759.46 165.484 760.731 162.992C762.001 160.499 765.583 158.479 768.729 158.479C771.876 158.479 773.397 160.499 772.126 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M764.793 177.384C763.521 179.876 759.941 181.896 756.795 181.896C753.648 181.896 752.127 179.876 753.398 177.384C754.668 174.892 758.25 172.872 761.395 172.872C764.543 172.872 766.064 174.892 764.793 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M757.512 191.661C756.241 194.153 752.661 196.173 749.515 196.173C746.367 196.173 744.846 194.153 746.118 191.661C747.387 189.169 750.969 187.149 754.115 187.149C757.263 187.149 758.784 189.169 757.512 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M750.221 205.948C748.95 208.44 745.37 210.46 742.224 210.46C739.076 210.46 737.555 208.44 738.827 205.948C740.096 203.456 743.678 201.436 746.824 201.436C749.972 201.436 751.493 203.456 750.221 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M742.888 220.33C741.617 222.822 738.037 224.842 734.891 224.842C731.743 224.842 730.222 222.822 731.494 220.33C732.763 217.838 736.345 215.818 739.491 215.818C742.639 215.818 744.16 217.838 742.888 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M735.618 234.586C734.347 237.078 730.767 239.098 727.621 239.098C724.473 239.098 722.953 237.078 724.224 234.586C725.493 232.094 729.075 230.074 732.221 230.074C735.369 230.074 736.89 232.094 735.618 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M728.338 248.884C727.067 251.376 723.486 253.396 720.341 253.396C717.193 253.396 715.672 251.376 716.943 248.884C718.213 246.392 721.795 244.372 724.941 244.372C728.088 244.372 729.609 246.392 728.338 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M721.005 263.266C719.734 265.758 716.153 267.778 713.007 267.778C709.86 267.778 708.339 265.758 709.61 263.266C710.88 260.774 714.462 258.754 717.608 258.754C720.755 258.754 722.276 260.774 721.005 263.266Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M713.23 278.521C711.959 281.013 708.378 283.033 705.233 283.033C702.085 283.033 700.564 281.013 701.835 278.521C703.105 276.029 706.687 274.009 709.833 274.009C712.98 274.009 714.501 276.029 713.23 278.521Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M705.96 292.777C704.689 295.269 701.108 297.289 697.963 297.289C694.815 297.289 693.294 295.269 694.565 292.777C695.835 290.284 699.417 288.264 702.563 288.264C705.71 288.264 707.231 290.284 705.96 292.777Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M829.87 44.7333C832.307 46.3672 830.068 50.5347 825.953 52.0825C819.792 54.3988 816.264 49.847 821.462 45.983C824.353 43.8337 827.798 43.3453 829.87 44.7333Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M823.246 62.7279C821.975 65.2202 818.395 67.2402 815.249 67.2402C812.101 67.2402 810.581 65.2202 811.852 62.7279C813.121 60.2357 816.703 58.2157 819.849 58.2157C822.997 58.2157 824.518 60.2357 823.246 62.7279Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M837.182 30.3829C839.619 32.0168 837.38 36.1843 833.265 37.7321C827.104 40.0484 823.576 35.4966 828.774 31.6326C831.666 29.4833 835.11 28.9949 837.182 30.3829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M797.224 73.3605C799.661 74.9945 797.422 79.162 793.306 80.7098C787.146 83.026 783.617 78.4743 788.816 74.6103C791.706 72.461 795.152 71.9726 797.224 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M790.6 91.3551C789.329 93.8474 785.749 95.8674 782.603 95.8674C779.455 95.8674 777.934 93.8474 779.206 91.3551C780.475 88.8629 784.057 86.8429 787.203 86.8429C790.351 86.8429 791.872 88.8629 790.6 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M783.341 105.59C782.07 108.082 778.489 110.102 775.343 110.102C772.196 110.102 770.675 108.082 771.946 105.59C773.216 103.098 776.798 101.078 779.944 101.078C783.091 101.078 784.612 103.098 783.341 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M775.924 120.151C774.652 122.643 771.072 124.663 767.926 124.663C764.779 124.663 763.258 122.643 764.529 120.151C765.799 117.658 769.381 115.638 772.527 115.638C775.674 115.638 777.195 117.658 775.924 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M768.643 134.417C767.372 136.909 763.792 138.929 760.646 138.929C757.498 138.929 755.977 136.909 757.249 134.417C758.518 131.925 762.1 129.905 765.246 129.905C768.394 129.905 769.915 131.925 768.643 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M761.31 148.81C760.039 151.302 756.459 153.322 753.313 153.322C750.165 153.322 748.644 151.302 749.916 148.81C751.185 146.317 754.767 144.297 757.913 144.297C761.061 144.297 762.582 146.317 761.31 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M754.082 162.992C752.811 165.484 749.231 167.504 746.085 167.504C742.937 167.504 741.417 165.484 742.688 162.992C743.957 160.499 747.539 158.479 750.685 158.479C753.833 158.479 755.354 160.499 754.082 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M746.739 177.384C745.468 179.876 741.887 181.896 738.742 181.896C735.594 181.896 734.073 179.876 735.344 177.384C736.614 174.892 740.196 172.872 743.342 172.872C746.489 172.872 748.01 174.892 746.739 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M739.459 191.661C738.187 194.153 734.607 196.173 731.461 196.173C728.313 196.173 726.793 194.153 728.064 191.661C729.333 189.169 732.916 187.149 736.061 187.149C739.209 187.149 740.73 189.169 739.459 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M732.178 205.948C730.907 208.44 727.326 210.46 724.181 210.46C721.033 210.46 719.512 208.44 720.783 205.948C722.053 203.456 725.635 201.436 728.781 201.436C731.928 201.436 733.449 203.456 732.178 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M724.845 220.33C723.574 222.822 719.993 224.842 716.848 224.842C713.7 224.842 712.179 222.822 713.45 220.33C714.72 217.838 718.302 215.818 721.448 215.818C724.595 215.818 726.116 217.838 724.845 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M717.575 234.586C716.304 237.078 712.724 239.098 709.578 239.098C706.43 239.098 704.909 237.078 706.181 234.586C707.45 232.094 711.032 230.074 714.178 230.074C717.326 230.074 718.846 232.094 717.575 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M710.284 248.884C709.013 251.376 705.433 253.396 702.287 253.396C699.139 253.396 697.618 251.376 698.89 248.884C700.159 246.392 703.741 244.372 706.887 244.372C710.035 244.372 711.555 246.392 710.284 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M811.817 44.7333C814.253 46.3672 812.014 50.5347 807.899 52.0825C801.738 54.3988 798.21 49.847 803.408 45.983C806.301 43.8337 809.745 43.3453 811.817 44.7333Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M805.193 62.7279C803.921 65.2202 800.341 67.2402 797.195 67.2402C794.048 67.2402 792.527 65.2202 793.798 62.7279C795.068 60.2357 798.65 58.2157 801.796 58.2157C804.943 58.2157 806.464 60.2357 805.193 62.7279Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M779.276 73.3605C781.712 74.9945 779.473 79.162 775.358 80.7098C769.197 83.026 765.669 78.4743 770.867 74.6103C773.76 72.461 777.204 71.9726 779.276 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M772.652 91.3551C771.381 93.8474 767.8 95.8674 764.654 95.8674C761.507 95.8674 759.986 93.8474 761.257 91.3551C762.527 88.8629 766.109 86.8429 769.255 86.8429C772.402 86.8429 773.923 88.8629 772.652 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M765.392 105.59C764.121 108.082 760.541 110.102 757.395 110.102C754.247 110.102 752.726 108.082 753.998 105.59C755.267 103.098 758.849 101.078 761.995 101.078C765.143 101.078 766.664 103.098 765.392 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M757.965 120.151C756.693 122.643 753.113 124.663 749.967 124.663C746.82 124.663 745.299 122.643 746.57 120.151C747.839 117.658 751.422 115.638 754.567 115.638C757.715 115.638 759.236 117.658 757.965 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M750.695 134.417C749.423 136.909 745.843 138.929 742.697 138.929C739.55 138.929 738.029 136.909 739.3 134.417C740.57 131.925 744.152 129.905 747.298 129.905C750.445 129.905 751.966 131.925 750.695 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M743.362 148.81C742.09 151.302 738.51 153.322 735.364 153.322C732.217 153.322 730.696 151.302 731.967 148.81C733.237 146.317 736.819 144.297 739.964 144.297C743.112 144.297 744.633 146.317 743.362 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M736.134 162.992C734.863 165.484 731.282 167.504 728.137 167.504C724.989 167.504 723.468 165.484 724.739 162.992C726.009 160.499 729.591 158.479 732.737 158.479C735.884 158.479 737.405 160.499 736.134 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M728.79 177.384C727.519 179.876 723.939 181.896 720.793 181.896C717.645 181.896 716.124 179.876 717.396 177.384C718.665 174.892 722.247 172.872 725.393 172.872C728.541 172.872 730.062 174.892 728.79 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M721.51 191.661C720.239 194.153 716.658 196.173 713.513 196.173C710.365 196.173 708.844 194.153 710.115 191.661C711.385 189.169 714.967 187.149 718.113 187.149C721.26 187.149 722.781 189.169 721.51 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M714.23 205.948C712.958 208.44 709.378 210.46 706.232 210.46C703.084 210.46 701.564 208.44 702.835 205.948C704.104 203.456 707.687 201.436 710.832 201.436C713.98 201.436 715.501 203.456 714.23 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M706.896 220.33C705.625 222.822 702.045 224.842 698.899 224.842C695.751 224.842 694.231 222.822 695.502 220.33C696.771 217.838 700.353 215.818 703.499 215.818C706.647 215.818 708.168 217.838 706.896 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M699.627 234.586C698.355 237.078 694.775 239.098 691.629 239.098C688.482 239.098 686.961 237.078 688.232 234.586C689.501 232.094 693.084 230.074 696.229 230.074C699.377 230.074 700.898 232.094 699.627 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M692.336 248.884C691.064 251.376 687.484 253.396 684.338 253.396C681.191 253.396 679.67 251.376 680.941 248.884C682.21 246.392 685.793 244.372 688.938 244.372C692.086 244.372 693.607 246.392 692.336 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M787.244 62.7279C785.973 65.2202 782.393 67.2402 779.247 67.2402C776.099 67.2402 774.578 65.2202 775.85 62.7279C777.119 60.2357 780.701 58.2157 783.847 58.2157C786.995 58.2157 788.515 60.2357 787.244 62.7279Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M760.496 73.3605C762.932 74.9945 760.693 79.162 756.578 80.7098C750.417 83.026 746.889 78.4743 752.087 74.6103C754.978 72.461 758.424 71.9726 760.496 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M753.883 91.3551C752.611 93.8474 749.031 95.8674 745.885 95.8674C742.738 95.8674 741.217 93.8474 742.488 91.3551C743.757 88.8629 747.34 86.8429 750.485 86.8429C753.633 86.8429 755.154 88.8629 753.883 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M746.623 105.59C745.352 108.082 741.772 110.102 738.626 110.102C735.478 110.102 733.957 108.082 735.229 105.59C736.498 103.098 740.08 101.078 743.226 101.078C746.374 101.078 747.894 103.098 746.623 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M739.195 120.151C737.924 122.643 734.344 124.663 731.198 124.663C728.05 124.663 726.53 122.643 727.801 120.151C729.07 117.658 732.652 115.638 735.798 115.638C738.946 115.638 740.467 117.658 739.195 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M731.926 134.417C730.654 136.909 727.074 138.929 723.928 138.929C720.781 138.929 719.26 136.909 720.531 134.417C721.8 131.925 725.383 129.905 728.528 129.905C731.676 129.905 733.197 131.925 731.926 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M724.582 148.81C723.311 151.302 719.73 153.322 716.585 153.322C713.437 153.322 711.916 151.302 713.187 148.81C714.457 146.317 718.039 144.297 721.185 144.297C724.332 144.297 725.853 146.317 724.582 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M717.354 162.992C716.083 165.484 712.503 167.504 709.357 167.504C706.209 167.504 704.688 165.484 705.96 162.992C707.229 160.499 710.811 158.479 713.957 158.479C717.105 158.479 718.625 160.499 717.354 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M710.021 177.384C708.75 179.876 705.17 181.896 702.024 181.896C698.876 181.896 697.355 179.876 698.627 177.384C699.896 174.892 703.478 172.872 706.624 172.872C709.772 172.872 711.292 174.892 710.021 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M702.741 191.661C701.469 194.153 697.889 196.173 694.743 196.173C691.596 196.173 690.075 194.153 691.346 191.661C692.616 189.169 696.198 187.149 699.344 187.149C702.491 187.149 704.012 189.169 702.741 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M695.45 205.948C694.179 208.44 690.598 210.46 687.452 210.46C684.305 210.46 682.784 208.44 684.055 205.948C685.325 203.456 688.907 201.436 692.053 201.436C695.2 201.436 696.721 203.456 695.45 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M688.117 220.33C686.845 222.822 683.265 224.842 680.119 224.842C676.972 224.842 675.451 222.822 676.722 220.33C677.992 217.838 681.574 215.818 684.72 215.818C687.867 215.818 689.388 217.838 688.117 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M680.857 234.586C679.586 237.078 676.006 239.098 672.86 239.098C669.712 239.098 668.192 237.078 669.463 234.586C670.732 232.094 674.314 230.074 677.46 230.074C680.608 230.074 682.129 232.094 680.857 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M673.566 248.884C672.295 251.376 668.715 253.396 665.569 253.396C662.421 253.396 660.901 251.376 662.172 248.884C663.441 246.392 667.023 244.372 670.169 244.372C673.317 244.372 674.838 246.392 673.566 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M768.475 62.7279C767.204 65.2202 763.623 67.2402 760.478 67.2402C757.33 67.2402 755.809 65.2202 757.08 62.7279C758.35 60.2357 761.932 58.2157 765.078 58.2157C768.225 58.2157 769.746 60.2357 768.475 62.7279Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M666.57 262.592C665.299 265.085 661.718 267.105 658.573 267.105C655.425 267.105 653.904 265.085 655.175 262.592C656.445 260.1 660.027 258.08 663.173 258.08C666.321 258.08 667.841 260.1 666.57 262.592Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M743.768 73.3605C746.204 74.9945 743.965 79.162 739.85 80.7098C733.689 83.026 730.161 78.4743 735.359 74.6103C738.25 72.461 741.696 71.9726 743.768 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M737.144 91.3551C735.873 93.8474 732.292 95.8674 729.146 95.8674C725.999 95.8674 724.478 93.8474 725.749 91.3551C727.019 88.8629 730.601 86.8429 733.747 86.8429C736.894 86.8429 738.415 88.8629 737.144 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M729.884 105.59C728.613 108.082 725.033 110.102 721.887 110.102C718.739 110.102 717.219 108.082 718.49 105.59C719.759 103.098 723.341 101.078 726.487 101.078C729.635 101.078 731.156 103.098 729.884 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M722.467 120.151C721.196 122.643 717.616 124.663 714.47 124.663C711.322 124.663 709.801 122.643 711.073 120.151C712.342 117.658 715.924 115.638 719.07 115.638C722.218 115.638 723.739 117.658 722.467 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M715.187 134.417C713.916 136.909 710.335 138.929 707.19 138.929C704.042 138.929 702.521 136.909 703.792 134.417C705.062 131.925 708.644 129.905 711.79 129.905C714.937 129.905 716.458 131.925 715.187 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M707.854 148.81C706.583 151.302 703.002 153.322 699.856 153.322C696.709 153.322 695.188 151.302 696.459 148.81C697.729 146.317 701.311 144.297 704.457 144.297C707.604 144.297 709.125 146.317 707.854 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M700.626 162.992C699.355 165.484 695.774 167.504 692.629 167.504C689.481 167.504 687.96 165.484 689.231 162.992C690.501 160.499 694.083 158.479 697.229 158.479C700.376 158.479 701.897 160.499 700.626 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M693.282 177.384C692.011 179.876 688.431 181.896 685.285 181.896C682.137 181.896 680.617 179.876 681.888 177.384C683.157 174.892 686.739 172.872 689.885 172.872C693.033 172.872 694.554 174.892 693.282 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M686.013 191.661C684.741 194.153 681.161 196.173 678.015 196.173C674.868 196.173 673.347 194.153 674.618 191.661C675.887 189.169 679.47 187.149 682.615 187.149C685.763 187.149 687.284 189.169 686.013 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M678.722 205.948C677.45 208.44 673.87 210.46 670.724 210.46C667.577 210.46 666.056 208.44 667.327 205.948C668.596 203.456 672.179 201.436 675.324 201.436C678.472 201.436 679.993 203.456 678.722 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M671.389 220.33C670.117 222.822 666.537 224.842 663.391 224.842C660.244 224.842 658.723 222.822 659.994 220.33C661.263 217.838 664.846 215.818 667.991 215.818C671.139 215.818 672.66 217.838 671.389 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M664.119 234.586C662.847 237.078 659.267 239.098 656.121 239.098C652.974 239.098 651.453 237.078 652.724 234.586C653.994 232.094 657.576 230.074 660.722 230.074C663.869 230.074 665.39 232.094 664.119 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M656.838 248.884C655.567 251.376 651.987 253.396 648.841 253.396C645.693 253.396 644.172 251.376 645.444 248.884C646.713 246.392 650.295 244.372 653.441 244.372C656.589 244.372 658.109 246.392 656.838 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M724.999 73.3605C727.435 74.9945 725.196 79.162 721.081 80.7098C714.92 83.026 711.392 78.4743 716.59 74.6103C719.483 72.461 722.927 71.9726 724.999 73.3605Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M718.375 91.3551C717.103 93.8474 713.523 95.8674 710.377 95.8674C707.23 95.8674 705.709 93.8474 706.98 91.3551C708.25 88.8629 711.832 86.8429 714.977 86.8429C718.125 86.8429 719.646 88.8629 718.375 91.3551Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M711.115 105.59C709.844 108.082 706.264 110.102 703.118 110.102C699.97 110.102 698.449 108.082 699.721 105.59C700.99 103.098 704.572 101.078 707.718 101.078C710.866 101.078 712.387 103.098 711.115 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M703.688 120.151C702.416 122.643 698.836 124.663 695.69 124.663C692.543 124.663 691.022 122.643 692.293 120.151C693.562 117.658 697.145 115.638 700.29 115.638C703.438 115.638 704.959 117.658 703.688 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M696.418 134.417C695.146 136.909 691.566 138.929 688.42 138.929C685.273 138.929 683.752 136.909 685.023 134.417C686.293 131.925 689.875 129.905 693.02 129.905C696.168 129.905 697.689 131.925 696.418 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M689.085 148.81C687.813 151.302 684.233 153.322 681.087 153.322C677.94 153.322 676.419 151.302 677.69 148.81C678.96 146.317 682.542 144.297 685.687 144.297C688.835 144.297 690.356 146.317 689.085 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M681.857 162.992C680.586 165.484 677.005 167.504 673.859 167.504C670.712 167.504 669.191 165.484 670.462 162.992C671.732 160.499 675.314 158.479 678.46 158.479C681.607 158.479 683.128 160.499 681.857 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M674.513 177.384C673.242 179.876 669.662 181.896 666.516 181.896C663.368 181.896 661.847 179.876 663.119 177.384C664.388 174.892 667.97 172.872 671.116 172.872C674.264 172.872 675.785 174.892 674.513 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M667.233 191.661C665.962 194.153 662.381 196.173 659.235 196.173C656.088 196.173 654.567 194.153 655.838 191.661C657.108 189.169 660.69 187.149 663.836 187.149C666.983 187.149 668.504 189.169 667.233 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M659.952 205.948C658.681 208.44 655.101 210.46 651.955 210.46C648.807 210.46 647.287 208.44 648.558 205.948C649.827 203.456 653.409 201.436 656.555 201.436C659.703 201.436 661.224 203.456 659.952 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M652.619 220.33C651.348 222.822 647.768 224.842 644.622 224.842C641.474 224.842 639.954 222.822 641.225 220.33C642.494 217.838 646.076 215.818 649.222 215.818C652.37 215.818 653.891 217.838 652.619 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M645.35 234.586C644.078 237.078 640.498 239.098 637.352 239.098C634.204 239.098 632.684 237.078 633.955 234.586C635.224 232.094 638.807 230.074 641.952 230.074C645.1 230.074 646.621 232.094 645.35 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M638.058 248.884C636.787 251.376 633.207 253.396 630.061 253.396C626.913 253.396 625.393 251.376 626.664 248.884C627.933 246.392 631.515 244.372 634.661 244.372C637.809 244.372 639.33 246.392 638.058 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M649.526 263.213C648.255 265.705 644.675 267.725 641.529 267.725C638.381 267.725 636.86 265.705 638.132 263.213C639.401 260.721 642.983 258.701 646.129 258.701C649.277 258.701 650.798 260.721 649.526 263.213Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M642.235 277.5C640.964 279.993 637.384 282.013 634.238 282.013C631.09 282.013 629.57 279.993 630.841 277.5C632.11 275.008 635.692 272.988 638.838 272.988C641.986 272.988 643.507 275.008 642.235 277.5Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M630.757 263.213C629.486 265.705 625.906 267.725 622.76 267.725C619.612 267.725 618.091 265.705 619.362 263.213C620.632 260.721 624.214 258.701 627.36 258.701C630.508 258.701 632.028 260.721 630.757 263.213Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M693.451 105.59C692.18 108.082 688.599 110.102 685.453 110.102C682.306 110.102 680.785 108.082 682.056 105.59C683.326 103.098 686.908 101.078 690.054 101.078C693.201 101.078 694.722 103.098 693.451 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M686.034 120.151C684.762 122.643 681.182 124.663 678.036 124.663C674.889 124.663 673.368 122.643 674.639 120.151C675.908 117.658 679.491 115.638 682.636 115.638C685.784 115.638 687.305 117.658 686.034 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M678.753 134.417C677.482 136.909 673.902 138.929 670.756 138.929C667.608 138.929 666.087 136.909 667.359 134.417C668.628 131.925 672.21 129.905 675.356 129.905C678.504 129.905 680.024 131.925 678.753 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M671.42 148.81C670.149 151.302 666.569 153.322 663.423 153.322C660.275 153.322 658.754 151.302 660.026 148.81C661.295 146.317 664.877 144.297 668.023 144.297C671.171 144.297 672.691 146.317 671.42 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M664.192 162.992C662.921 165.484 659.341 167.504 656.195 167.504C653.047 167.504 651.526 165.484 652.798 162.992C654.067 160.499 657.649 158.479 660.795 158.479C663.943 158.479 665.464 160.499 664.192 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M656.849 177.384C655.578 179.876 651.997 181.896 648.851 181.896C645.704 181.896 644.183 179.876 645.454 177.384C646.724 174.892 650.306 172.872 653.452 172.872C656.599 172.872 658.12 174.892 656.849 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M649.568 191.661C648.297 194.153 644.717 196.173 641.571 196.173C638.423 196.173 636.903 194.153 638.174 191.661C639.443 189.169 643.025 187.149 646.171 187.149C649.319 187.149 650.84 189.169 649.568 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M642.288 205.948C641.017 208.44 637.436 210.46 634.291 210.46C631.143 210.46 629.622 208.44 630.893 205.948C632.163 203.456 635.745 201.436 638.891 201.436C642.038 201.436 643.559 203.456 642.288 205.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M634.955 220.33C633.684 222.822 630.103 224.842 626.957 224.842C623.81 224.842 622.289 222.822 623.56 220.33C624.83 217.838 628.412 215.818 631.558 215.818C634.705 215.818 636.226 217.838 634.955 220.33Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M627.685 234.586C626.414 237.078 622.833 239.098 619.688 239.098C616.54 239.098 615.019 237.078 616.29 234.586C617.56 232.094 621.142 230.074 624.288 230.074C627.435 230.074 628.956 232.094 627.685 234.586Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M620.394 248.884C619.123 251.376 615.542 253.396 612.397 253.396C609.249 253.396 607.728 251.376 608.999 248.884C610.269 246.392 613.851 244.372 616.997 244.372C620.144 244.372 621.665 246.392 620.394 248.884Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M667.38 119.909C666.109 122.401 662.529 124.421 659.383 124.421C656.235 124.421 654.714 122.401 655.986 119.909C657.255 117.416 660.837 115.396 663.983 115.396C667.131 115.396 668.651 117.416 667.38 119.909Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M659.952 134.47C658.681 136.962 655.101 138.982 651.955 138.982C648.807 138.982 647.287 136.962 648.558 134.47C649.827 131.977 653.409 129.957 656.555 129.957C659.703 129.957 661.224 131.977 659.952 134.47Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M652.683 148.736C651.411 151.228 647.831 153.248 644.685 153.248C641.537 153.248 640.017 151.228 641.288 148.736C642.557 146.244 646.14 144.224 649.285 144.224C652.433 144.224 653.954 146.244 652.683 148.736Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M645.35 163.128C644.078 165.621 640.498 167.641 637.352 167.641C634.204 167.641 632.684 165.621 633.955 163.128C635.224 160.636 638.807 158.616 641.952 158.616C645.1 158.616 646.621 160.636 645.35 163.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M638.122 177.31C636.85 179.803 633.27 181.823 630.124 181.823C626.977 181.823 625.456 179.803 626.727 177.31C627.997 174.818 631.579 172.798 634.725 172.798C637.872 172.798 639.393 174.818 638.122 177.31Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M630.778 191.703C629.507 194.195 625.927 196.215 622.781 196.215C619.633 196.215 618.112 194.195 619.383 191.703C620.653 189.211 624.235 187.191 627.381 187.191C630.529 187.191 632.049 189.211 630.778 191.703Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M623.498 205.98C622.226 208.472 618.646 210.492 615.5 210.492C612.353 210.492 610.832 208.472 612.103 205.98C613.373 203.488 616.955 201.468 620.101 201.468C623.248 201.468 624.769 203.488 623.498 205.98Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M616.217 220.267C614.946 222.759 611.366 224.779 608.22 224.779C605.072 224.779 603.551 222.759 604.823 220.267C606.092 217.775 609.674 215.755 612.82 215.755C615.968 215.755 617.489 217.775 616.217 220.267Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M608.884 234.649C607.613 237.141 604.033 239.161 600.887 239.161C597.739 239.161 596.218 237.141 597.49 234.649C598.759 232.157 602.341 230.137 605.487 230.137C608.635 230.137 610.155 232.157 608.884 234.649Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M601.614 248.905C600.343 251.397 596.763 253.417 593.617 253.417C590.469 253.417 588.948 251.397 590.22 248.905C591.489 246.413 595.071 244.393 598.217 244.393C601.365 244.393 602.886 246.413 601.614 248.905Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M594.323 263.203C593.052 265.695 589.472 267.715 586.326 267.715C583.178 267.715 581.658 265.695 582.929 263.203C584.198 260.71 587.78 258.69 590.926 258.69C594.074 258.69 595.595 260.71 594.323 263.203Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M657.049 105.59C655.777 108.082 652.197 110.102 649.051 110.102C645.904 110.102 644.383 108.082 645.654 105.59C646.924 103.098 650.506 101.078 653.652 101.078C656.799 101.078 658.32 103.098 657.049 105.59Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M645.928 91.0606C644.657 93.5528 641.077 95.5728 637.931 95.5728C634.783 95.5728 633.262 93.5528 634.534 91.0606C635.803 88.5683 639.385 86.5483 642.531 86.5483C645.679 86.5483 647.199 88.5683 645.928 91.0606Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M672.083 76.0999C670.812 78.5922 667.231 80.6122 664.086 80.6122C660.938 80.6122 659.417 78.5922 660.688 76.0999C661.958 73.6077 665.54 71.5877 668.686 71.5877C671.833 71.5877 673.354 73.6077 672.083 76.0999Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M689.716 76.0999C688.445 78.5922 684.864 80.6122 681.718 80.6122C678.571 80.6122 677.05 78.5922 678.321 76.0999C679.591 73.6077 683.173 71.5877 686.319 71.5877C689.466 71.5877 690.987 73.6077 689.716 76.0999Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M649.874 120.151C648.602 122.643 645.022 124.663 641.876 124.663C638.728 124.663 637.208 122.643 638.479 120.151C639.748 117.658 643.33 115.638 646.476 115.638C649.624 115.638 651.145 117.658 649.874 120.151Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M642.593 134.417C641.322 136.909 637.741 138.929 634.596 138.929C631.448 138.929 629.927 136.909 631.198 134.417C632.468 131.925 636.05 129.905 639.196 129.905C642.343 129.905 643.864 131.925 642.593 134.417Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M635.26 148.81C633.989 151.302 630.408 153.322 627.263 153.322C624.115 153.322 622.594 151.302 623.865 148.81C625.135 146.317 628.717 144.297 631.863 144.297C635.01 144.297 636.531 146.317 635.26 148.81Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M628.032 162.992C626.761 165.484 623.181 167.504 620.035 167.504C616.887 167.504 615.366 165.484 616.638 162.992C617.907 160.499 621.489 158.479 624.635 158.479C627.783 158.479 629.303 160.499 628.032 162.992Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M620.689 177.384C619.417 179.876 615.837 181.896 612.691 181.896C609.544 181.896 608.023 179.876 609.294 177.384C610.563 174.892 614.146 172.872 617.291 172.872C620.439 172.872 621.96 174.892 620.689 177.384Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M613.419 191.661C612.147 194.153 608.567 196.173 605.421 196.173C602.274 196.173 600.753 194.153 602.024 191.661C603.294 189.169 606.876 187.149 610.022 187.149C613.169 187.149 614.69 189.169 613.419 191.661Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M631.22 119.909C629.949 122.401 626.368 124.421 623.223 124.421C620.075 124.421 618.554 122.401 619.825 119.909C621.095 117.416 624.677 115.396 627.823 115.396C630.97 115.396 632.491 117.416 631.22 119.909Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M623.792 134.47C622.521 136.962 618.941 138.982 615.795 138.982C612.647 138.982 611.126 136.962 612.398 134.47C613.667 131.977 617.249 129.957 620.395 129.957C623.543 129.957 625.064 131.977 623.792 134.47Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M616.522 148.736C615.251 151.228 611.671 153.248 608.525 153.248C605.377 153.248 603.857 151.228 605.128 148.736C606.397 146.244 609.979 144.224 613.125 144.224C616.273 144.224 617.794 146.244 616.522 148.736Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M609.189 163.128C607.918 165.621 604.338 167.641 601.192 167.641C598.044 167.641 596.523 165.621 597.795 163.128C599.064 160.636 602.646 158.616 605.792 158.616C608.94 158.616 610.461 160.636 609.189 163.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M601.962 177.31C600.69 179.803 597.11 181.823 593.964 181.823C590.816 181.823 589.296 179.803 590.567 177.31C591.836 174.818 595.419 172.798 598.564 172.798C601.712 172.798 603.233 174.818 601.962 177.31Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M594.618 191.703C593.347 194.195 589.766 196.215 586.621 196.215C583.473 196.215 581.952 194.195 583.223 191.703C584.493 189.211 588.075 187.191 591.221 187.191C594.368 187.191 595.889 189.211 594.618 191.703Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M587.401 205.864C586.129 208.356 582.549 210.376 579.403 210.376C576.256 210.376 574.735 208.356 576.006 205.864C577.276 203.372 580.858 201.352 584.003 201.352C587.151 201.352 588.672 203.372 587.401 205.864Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M580.057 220.257C578.786 222.749 575.206 224.769 572.06 224.769C568.912 224.769 567.391 222.749 568.663 220.257C569.932 217.764 573.514 215.744 576.66 215.744C579.808 215.744 581.328 217.764 580.057 220.257Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M598.837 220.257C597.566 222.749 593.985 224.769 590.839 224.769C587.692 224.769 586.171 222.749 587.442 220.257C588.712 217.764 592.294 215.744 595.44 215.744C598.587 215.744 600.108 217.764 598.837 220.257Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M591.556 234.533C590.285 237.026 586.705 239.046 583.559 239.046C580.411 239.046 578.891 237.026 580.162 234.533C581.431 232.041 585.013 230.021 588.159 230.021C591.307 230.021 592.828 232.041 591.556 234.533Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M613.156 119.909C611.884 122.401 608.304 124.421 605.158 124.421C602.011 124.421 600.49 122.401 601.761 119.909C603.031 117.416 606.613 115.396 609.758 115.396C612.906 115.396 614.427 117.416 613.156 119.909Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M605.738 134.47C604.467 136.962 600.887 138.982 597.741 138.982C594.593 138.982 593.073 136.962 594.344 134.47C595.613 131.977 599.195 129.957 602.341 129.957C605.489 129.957 607.01 131.977 605.738 134.47Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M598.458 148.736C597.187 151.228 593.607 153.248 590.461 153.248C587.313 153.248 585.792 151.228 587.063 148.736C588.333 146.244 591.915 144.224 595.061 144.224C598.209 144.224 599.729 146.244 598.458 148.736Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M591.125 163.128C589.854 165.621 586.273 167.641 583.128 167.641C579.98 167.641 578.459 165.621 579.73 163.128C581 160.636 584.582 158.616 587.728 158.616C590.875 158.616 592.396 160.636 591.125 163.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M583.897 177.31C582.626 179.803 579.046 181.823 575.9 181.823C572.752 181.823 571.231 179.803 572.503 177.31C573.772 174.818 577.354 172.798 580.5 172.798C583.648 172.798 585.168 174.818 583.897 177.31Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M576.554 191.703C575.282 194.195 571.702 196.215 568.556 196.215C565.409 196.215 563.888 194.195 565.159 191.703C566.429 189.211 570.011 187.191 573.156 187.191C576.304 187.191 577.825 189.211 576.554 191.703Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M569.336 205.864C568.065 208.356 564.485 210.376 561.339 210.376C558.191 210.376 556.671 208.356 557.942 205.864C559.211 203.372 562.793 201.352 565.939 201.352C569.087 201.352 570.608 203.372 569.336 205.864Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M561.993 220.257C560.722 222.749 557.141 224.769 553.995 224.769C550.848 224.769 549.327 222.749 550.598 220.257C551.868 217.764 555.45 215.744 558.596 215.744C561.743 215.744 563.264 217.764 561.993 220.257Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M554.902 234.176C553.631 236.668 550.05 238.688 546.904 238.688C543.757 238.688 542.236 236.668 543.507 234.176C544.777 231.683 548.359 229.663 551.505 229.663C554.652 229.663 556.173 231.683 554.902 234.176Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M596.491 119.909C595.219 122.401 591.639 124.421 588.493 124.421C585.346 124.421 583.825 122.401 585.096 119.909C586.366 117.416 589.948 115.396 593.093 115.396C596.241 115.396 597.762 117.416 596.491 119.909Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M589.074 134.47C587.802 136.962 584.222 138.982 581.076 138.982C577.928 138.982 576.408 136.962 577.679 134.47C578.948 131.977 582.531 129.957 585.676 129.957C588.824 129.957 590.345 131.977 589.074 134.47Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M581.793 148.736C580.522 151.228 576.942 153.248 573.796 153.248C570.648 153.248 569.127 151.228 570.399 148.736C571.668 146.244 575.25 144.224 578.396 144.224C581.544 144.224 583.064 146.244 581.793 148.736Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M574.46 163.128C573.189 165.621 569.608 167.641 566.463 167.641C563.315 167.641 561.794 165.621 563.065 163.128C564.335 160.636 567.917 158.616 571.063 158.616C574.21 158.616 575.731 160.636 574.46 163.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M567.232 177.31C565.961 179.803 562.381 181.823 559.235 181.823C556.087 181.823 554.566 179.803 555.838 177.31C557.107 174.818 560.689 172.798 563.835 172.798C566.983 172.798 568.503 174.818 567.232 177.31Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M559.889 191.703C558.617 194.195 555.037 196.215 551.891 196.215C548.744 196.215 547.223 194.195 548.494 191.703C549.764 189.211 553.346 187.191 556.492 187.191C559.639 187.191 561.16 189.211 559.889 191.703Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M552.671 205.864C551.4 208.356 547.82 210.376 544.674 210.376C541.526 210.376 540.006 208.356 541.277 205.864C542.546 203.372 546.128 201.352 549.274 201.352C552.422 201.352 553.943 203.372 552.671 205.864Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M578.426 119.909C577.155 122.401 573.575 124.421 570.429 124.421C567.281 124.421 565.761 122.401 567.032 119.909C568.301 117.416 571.883 115.396 575.029 115.396C578.177 115.396 579.698 117.416 578.426 119.909Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M571.009 134.47C569.738 136.962 566.158 138.982 563.012 138.982C559.864 138.982 558.343 136.962 559.615 134.47C560.884 131.977 564.466 129.957 567.612 129.957C570.76 129.957 572.28 131.977 571.009 134.47Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M563.739 148.736C562.468 151.228 558.888 153.248 555.742 153.248C552.594 153.248 551.073 151.228 552.345 148.736C553.614 146.244 557.196 144.224 560.342 144.224C563.49 144.224 565.011 146.244 563.739 148.736Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M556.396 163.128C555.125 165.621 551.544 167.641 548.398 167.641C545.251 167.641 543.73 165.621 545.001 163.128C546.271 160.636 549.853 158.616 552.999 158.616C556.146 158.616 557.667 160.636 556.396 163.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M549.168 177.31C547.897 179.803 544.316 181.823 541.17 181.823C538.023 181.823 536.502 179.803 537.773 177.31C539.043 174.818 542.625 172.798 545.771 172.798C548.918 172.798 550.439 174.818 549.168 177.31Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M541.824 191.703C540.553 194.195 536.973 196.215 533.827 196.215C530.679 196.215 529.159 194.195 530.43 191.703C531.699 189.211 535.281 187.191 538.427 187.191C541.575 187.191 543.096 189.211 541.824 191.703Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M534.607 205.864C533.336 208.356 529.755 210.376 526.61 210.376C523.462 210.376 521.941 208.356 523.212 205.864C524.482 203.372 528.064 201.352 531.21 201.352C534.358 201.352 535.878 203.372 534.607 205.864Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M527.274 220.257C526.003 222.749 522.422 224.769 519.277 224.769C516.129 224.769 514.608 222.749 515.879 220.257C517.149 217.764 520.731 215.744 523.877 215.744C527.024 215.744 528.545 217.764 527.274 220.257Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M500.351 234.649C499.08 237.141 495.5 239.161 492.354 239.161C489.206 239.161 487.685 237.141 488.957 234.649C490.226 232.157 493.808 230.137 496.954 230.137C500.102 230.137 501.623 232.157 500.351 234.649Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M536.859 163.128C535.587 165.621 532.007 167.641 528.861 167.641C525.714 167.641 524.193 165.621 525.464 163.128C526.733 160.636 530.316 158.616 533.461 158.616C536.609 158.616 538.13 160.636 536.859 163.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M529.631 177.31C528.359 179.803 524.779 181.823 521.633 181.823C518.486 181.823 516.965 179.803 518.236 177.31C519.506 174.818 523.088 172.798 526.234 172.798C529.381 172.798 530.902 174.818 529.631 177.31Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M522.287 191.703C521.016 194.195 517.436 196.215 514.29 196.215C511.142 196.215 509.621 194.195 510.893 191.703C512.162 189.211 515.744 187.191 518.89 187.191C522.038 187.191 523.558 189.211 522.287 191.703Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M515.07 205.864C513.799 208.356 510.218 210.376 507.072 210.376C503.925 210.376 502.404 208.356 503.675 205.864C504.945 203.372 508.527 201.352 511.673 201.352C514.82 201.352 516.341 203.372 515.07 205.864Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M573.471 91.3131C572.2 93.8053 568.62 95.8253 565.474 95.8253C562.326 95.8253 560.805 93.8053 562.076 91.3131C563.346 88.8208 566.928 86.8008 570.074 86.8008C573.222 86.8008 574.742 88.8208 573.471 91.3131Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M566.243 105.485C564.972 107.977 561.392 109.997 558.246 109.997C555.098 109.997 553.577 107.977 554.849 105.485C556.118 102.992 559.7 100.972 562.846 100.972C565.994 100.972 567.515 102.992 566.243 105.485Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M558.9 119.877C557.628 122.369 554.048 124.389 550.902 124.389C547.755 124.389 546.234 122.369 547.505 119.877C548.775 117.385 552.357 115.365 555.503 115.365C558.65 115.365 560.171 117.385 558.9 119.877Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M593.019 91.3131C591.748 93.8053 588.167 95.8253 585.021 95.8253C581.874 95.8253 580.353 93.8053 581.624 91.3131C582.894 88.8208 586.476 86.8008 589.622 86.8008C592.769 86.8008 594.29 88.8208 593.019 91.3131Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M585.791 105.485C584.52 107.977 580.939 109.997 577.794 109.997C574.646 109.997 573.125 107.977 574.396 105.485C575.666 102.992 579.248 100.972 582.394 100.972C585.541 100.972 587.062 102.992 585.791 105.485Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M603.845 105.485C602.573 107.977 598.993 109.997 595.847 109.997C592.7 109.997 591.179 107.977 592.45 105.485C593.72 102.992 597.302 100.972 600.448 100.972C603.595 100.972 605.116 102.992 603.845 105.485Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M540.099 119.909C538.828 122.401 535.247 124.421 532.102 124.421C528.954 124.421 527.433 122.401 528.704 119.909C529.974 117.416 533.556 115.396 536.702 115.396C539.849 115.396 541.37 117.416 540.099 119.909Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M522.15 119.877C520.879 122.369 517.299 124.389 514.153 124.389C511.005 124.389 509.485 122.369 510.756 119.877C512.025 117.385 515.607 115.365 518.753 115.365C521.901 115.365 523.422 117.385 522.15 119.877Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M547.453 105.485C546.182 107.977 542.601 109.997 539.456 109.997C536.308 109.997 534.787 107.977 536.058 105.485C537.328 102.992 540.91 100.972 544.056 100.972C547.203 100.972 548.724 102.992 547.453 105.485Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M525.465 148.61C524.193 151.102 520.613 153.122 517.467 153.122C514.319 153.122 512.799 151.102 514.07 148.61C515.339 146.117 518.921 144.097 522.067 144.097C525.215 144.097 526.736 146.117 525.465 148.61Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M532.819 134.175C531.547 136.667 527.967 138.687 524.821 138.687C521.674 138.687 520.153 136.667 521.424 134.175C522.693 131.683 526.276 129.663 529.421 129.663C532.569 129.663 534.09 131.683 532.819 134.175Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M507.505 148.61C506.234 151.102 502.654 153.122 499.508 153.122C496.36 153.122 494.839 151.102 496.111 148.61C497.38 146.117 500.962 144.097 504.108 144.097C507.256 144.097 508.777 146.117 507.505 148.61Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M514.87 134.175C513.599 136.667 510.018 138.687 506.873 138.687C503.725 138.687 502.204 136.667 503.475 134.175C504.745 131.683 508.327 129.663 511.473 129.663C514.62 129.663 516.141 131.683 514.87 134.175Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M490.009 148.61C488.738 151.102 485.158 153.122 482.012 153.122C478.864 153.122 477.343 151.102 478.615 148.61C479.884 146.117 483.466 144.097 486.612 144.097C489.76 144.097 491.28 146.117 490.009 148.61Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M497.374 134.175C496.102 136.667 492.522 138.687 489.376 138.687C486.229 138.687 484.708 136.667 485.979 134.175C487.249 131.683 490.831 129.663 493.977 129.663C497.124 129.663 498.645 131.683 497.374 134.175Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M467.915 191.945C466.644 194.437 463.064 196.457 459.918 196.457C456.77 196.457 455.25 194.437 456.521 191.945C457.79 189.453 461.372 187.433 464.518 187.433C467.666 187.433 469.187 189.453 467.915 191.945Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M475.28 177.51C474.009 180.003 470.428 182.023 467.283 182.023C464.135 182.023 462.614 180.003 463.885 177.51C465.155 175.018 468.737 172.998 471.883 172.998C475.03 172.998 476.551 175.018 475.28 177.51Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M485.412 191.945C484.14 194.437 480.56 196.457 477.414 196.457C474.267 196.457 472.746 194.437 474.017 191.945C475.286 189.453 478.869 187.433 482.014 187.433C485.162 187.433 486.683 189.453 485.412 191.945Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M492.776 177.51C491.505 180.003 487.925 182.023 484.779 182.023C481.631 182.023 480.11 180.003 481.382 177.51C482.651 175.018 486.233 172.998 489.379 172.998C492.527 172.998 494.047 175.018 492.776 177.51Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M471.345 219.552C470.074 222.044 466.494 224.064 463.348 224.064C460.2 224.064 458.679 222.044 459.951 219.552C461.22 217.059 464.802 215.039 467.948 215.039C471.096 215.039 472.616 217.059 471.345 219.552Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M478.699 205.117C477.428 207.609 473.848 209.629 470.702 209.629C467.554 209.629 466.033 207.609 467.305 205.117C468.574 202.625 472.156 200.605 475.302 200.605C478.45 200.605 479.971 202.625 478.699 205.117Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M503.371 191.945C502.099 194.437 498.519 196.457 495.373 196.457C492.226 196.457 490.705 194.437 491.976 191.945C493.246 189.453 496.828 187.433 499.973 187.433C503.121 187.433 504.642 189.453 503.371 191.945Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M496.658 205.117C495.387 207.609 491.807 209.629 488.661 209.629C485.513 209.629 483.993 207.609 485.264 205.117C486.533 202.625 490.115 200.605 493.261 200.605C496.409 200.605 497.93 202.625 496.658 205.117Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M449.178 191.945C447.907 194.437 444.326 196.457 441.18 196.457C438.033 196.457 436.512 194.437 437.783 191.945C439.053 189.453 442.635 187.433 445.781 187.433C448.928 187.433 450.449 189.453 449.178 191.945Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M463.865 163.128C462.594 165.621 459.013 167.641 455.868 167.641C452.72 167.641 451.199 165.621 452.47 163.128C453.74 160.636 457.322 158.616 460.468 158.616C463.615 158.616 465.136 160.636 463.865 163.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M446.632 163.128C445.36 165.621 441.78 167.641 438.634 167.641C435.487 167.641 433.966 165.621 435.237 163.128C436.507 160.636 440.089 158.616 443.235 158.616C446.382 158.616 447.903 160.636 446.632 163.128Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M439.299 177.51C438.027 180.003 434.447 182.023 431.301 182.023C428.154 182.023 426.633 180.003 427.904 177.51C429.174 175.018 432.756 172.998 435.901 172.998C439.049 172.998 440.57 175.018 439.299 177.51Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M443.675 134.175C442.404 136.667 438.824 138.687 435.678 138.687C432.53 138.687 431.01 136.667 432.281 134.175C433.55 131.683 437.132 129.663 440.278 129.663C443.426 129.663 444.947 131.683 443.675 134.175Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M424.727 134.175C423.456 136.667 419.876 138.687 416.73 138.687C413.582 138.687 412.062 136.667 413.333 134.175C414.602 131.683 418.184 129.663 421.33 129.663C424.478 129.663 425.999 131.683 424.727 134.175Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M439.541 120.382C438.269 122.874 434.689 124.894 431.543 124.894C428.396 124.894 426.875 122.874 428.146 120.382C429.416 117.89 432.998 115.87 436.143 115.87C439.291 115.87 440.812 117.89 439.541 120.382Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M439.326 30.0672C441.763 31.7012 439.524 35.8687 435.408 37.4165C429.248 39.7327 425.719 35.181 430.917 31.317C433.81 29.1677 437.254 28.6793 439.326 30.0672Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M432.702 48.0619C431.431 50.5541 427.851 52.5741 424.705 52.5741C421.557 52.5741 420.036 50.5541 421.308 48.0619C422.577 45.5696 426.159 43.5496 429.305 43.5496C432.453 43.5496 433.973 45.5696 432.702 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M425.453 62.3071C424.182 64.7993 420.602 66.8194 417.456 66.8194C414.308 66.8194 412.787 64.7993 414.059 62.3071C415.328 59.8149 418.91 57.7949 422.056 57.7949C425.204 57.7949 426.725 59.8149 425.453 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M418.026 76.8574C416.754 79.3497 413.174 81.3697 410.028 81.3697C406.881 81.3697 405.36 79.3497 406.631 76.8574C407.9 74.3652 411.483 72.3452 414.628 72.3452C417.776 72.3452 419.297 74.3652 418.026 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M410.756 91.1237C409.484 93.6159 405.904 95.6359 402.758 95.6359C399.611 95.6359 398.09 93.6159 399.361 91.1237C400.631 88.6315 404.213 86.6115 407.359 86.6115C410.506 86.6115 412.027 88.6315 410.756 91.1237Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M447.305 19.4346C446.034 21.9269 442.454 23.9469 439.308 23.9469C436.16 23.9469 434.639 21.9269 435.911 19.4346C437.18 16.9424 440.762 14.9224 443.908 14.9224C447.056 14.9224 448.576 16.9424 447.305 19.4346Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M403.707 104.948C402.435 107.44 398.855 109.46 395.709 109.46C392.562 109.46 391.041 107.44 392.312 104.948C393.582 102.456 397.164 100.436 400.31 100.436C403.457 100.436 404.978 102.456 403.707 104.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M450.735 48.0619C449.464 50.5541 445.883 52.5741 442.737 52.5741C439.59 52.5741 438.069 50.5541 439.34 48.0619C440.61 45.5696 444.192 43.5496 447.338 43.5496C450.485 43.5496 452.006 45.5696 450.735 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M443.486 62.3071C442.215 64.7993 438.634 66.8194 435.489 66.8194C432.341 66.8194 430.82 64.7993 432.091 62.3071C433.361 59.8149 436.943 57.7949 440.089 57.7949C443.236 57.7949 444.757 59.8149 443.486 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M421.304 30.0672C423.74 31.7012 421.501 35.8687 417.386 37.4165C411.225 39.7327 407.697 35.181 412.895 31.317C415.788 29.1677 419.232 28.6793 421.304 30.0672Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M414.68 48.0619C413.409 50.5541 409.828 52.5741 406.683 52.5741C403.535 52.5741 402.014 50.5541 403.285 48.0619C404.555 45.5696 408.137 43.5496 411.283 43.5496C414.43 43.5496 415.951 45.5696 414.68 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M407.421 62.3071C406.149 64.7993 402.569 66.8194 399.423 66.8194C396.276 66.8194 394.755 64.7993 396.026 62.3071C397.295 59.8149 400.878 57.7949 404.023 57.7949C407.171 57.7949 408.692 59.8149 407.421 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M400.003 76.8574C398.732 79.3497 395.152 81.3697 392.006 81.3697C388.858 81.3697 387.338 79.3497 388.609 76.8574C389.878 74.3652 393.46 72.3452 396.606 72.3452C399.754 72.3452 401.275 74.3652 400.003 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M392.723 91.1237C391.452 93.6159 387.871 95.6359 384.726 95.6359C381.578 95.6359 380.057 93.6159 381.328 91.1237C382.598 88.6315 386.18 86.6115 389.326 86.6115C392.473 86.6115 393.994 88.6315 392.723 91.1237Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M429.272 19.4346C428.001 21.9269 424.421 23.9469 421.275 23.9469C418.127 23.9469 416.607 21.9269 417.878 19.4346C419.147 16.9424 422.729 14.9224 425.875 14.9224C429.023 14.9224 430.544 16.9424 429.272 19.4346Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M385.684 104.948C384.413 107.44 380.833 109.46 377.687 109.46C374.539 109.46 373.019 107.44 374.29 104.948C375.559 102.456 379.141 100.436 382.287 100.436C385.435 100.436 386.956 102.456 385.684 104.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M436.553 5.1789C435.282 7.67113 431.701 9.69113 428.555 9.69113C425.408 9.69113 423.887 7.67113 425.158 5.1789C426.428 2.68666 430.01 0.66666 433.156 0.66666C436.303 0.66666 437.824 2.68666 436.553 5.1789Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M403.25 30.0672C405.687 31.7012 403.448 35.8687 399.332 37.4165C393.172 39.7327 389.643 35.181 394.842 31.317C397.734 29.1677 401.178 28.6793 403.25 30.0672Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M396.626 48.0619C395.355 50.5541 391.775 52.5741 388.629 52.5741C385.481 52.5741 383.96 50.5541 385.232 48.0619C386.501 45.5696 390.083 43.5496 393.229 43.5496C396.377 43.5496 397.897 45.5696 396.626 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M389.367 62.3071C388.096 64.7993 384.515 66.8194 381.369 66.8194C378.222 66.8194 376.701 64.7993 377.972 62.3071C379.242 59.8149 382.824 57.7949 385.97 57.7949C389.117 57.7949 390.638 59.8149 389.367 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M381.95 76.8574C380.678 79.3497 377.098 81.3697 373.952 81.3697C370.805 81.3697 369.284 79.3497 370.555 76.8574C371.824 74.3652 375.407 72.3452 378.552 72.3452C381.7 72.3452 383.221 74.3652 381.95 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M374.669 91.1237C373.398 93.6159 369.818 95.6359 366.672 95.6359C363.524 95.6359 362.003 93.6159 363.275 91.1237C364.544 88.6315 368.126 86.6115 371.272 86.6115C374.42 86.6115 375.94 88.6315 374.669 91.1237Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M411.219 19.4346C409.947 21.9269 406.367 23.9469 403.221 23.9469C400.074 23.9469 398.553 21.9269 399.824 19.4346C401.093 16.9424 404.676 14.9224 407.821 14.9224C410.969 14.9224 412.49 16.9424 411.219 19.4346Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M367.62 104.948C366.349 107.44 362.769 109.46 359.623 109.46C356.475 109.46 354.954 107.44 356.226 104.948C357.495 102.456 361.077 100.436 364.223 100.436C367.371 100.436 368.891 102.456 367.62 104.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M385.228 30.0672C387.664 31.7012 385.425 35.8687 381.31 37.4165C375.149 39.7327 371.621 35.181 376.819 31.317C379.712 29.1677 383.156 28.6793 385.228 30.0672Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M378.604 48.0619C377.333 50.5541 373.752 52.5741 370.607 52.5741C367.459 52.5741 365.938 50.5541 367.209 48.0619C368.479 45.5696 372.061 43.5496 375.207 43.5496C378.354 43.5496 379.875 45.5696 378.604 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M371.345 62.3071C370.073 64.7993 366.493 66.8194 363.347 66.8194C360.2 66.8194 358.679 64.7993 359.95 62.3071C361.219 59.8149 364.802 57.7949 367.947 57.7949C371.095 57.7949 372.616 59.8149 371.345 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M363.917 76.8574C362.646 79.3497 359.065 81.3697 355.919 81.3697C352.772 81.3697 351.251 79.3497 352.522 76.8574C353.792 74.3652 357.374 72.3452 360.52 72.3452C363.667 72.3452 365.188 74.3652 363.917 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M356.647 91.1237C355.376 93.6159 351.795 95.6359 348.65 95.6359C345.502 95.6359 343.981 93.6159 345.252 91.1237C346.522 88.6315 350.104 86.6115 353.25 86.6115C356.397 86.6115 357.918 88.6315 356.647 91.1237Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M393.196 19.4346C391.925 21.9269 388.345 23.9469 385.199 23.9469C382.051 23.9469 380.531 21.9269 381.802 19.4346C383.071 16.9424 386.653 14.9224 389.799 14.9224C392.947 14.9224 394.468 16.9424 393.196 19.4346Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M349.598 104.948C348.327 107.44 344.746 109.46 341.601 109.46C338.453 109.46 336.932 107.44 338.203 104.948C339.473 102.456 343.055 100.436 346.201 100.436C349.348 100.436 350.869 102.456 349.598 104.948Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M418.489 5.1789C417.217 7.67113 413.637 9.69113 410.491 9.69113C407.344 9.69113 405.823 7.67113 407.094 5.1789C408.363 2.68666 411.946 0.66666 415.091 0.66666C418.239 0.66666 419.76 2.68666 418.489 5.1789Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M400.466 5.1789C399.195 7.67113 395.615 9.69113 392.469 9.69113C389.321 9.69113 387.8 7.67113 389.072 5.1789C390.341 2.68666 393.923 0.66666 397.069 0.66666C400.217 0.66666 401.738 2.68666 400.466 5.1789Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M359.835 120.235C358.564 122.727 354.983 124.747 351.837 124.747C348.69 124.747 347.169 122.727 348.44 120.235C349.71 117.743 353.292 115.723 356.438 115.723C359.585 115.723 361.106 117.743 359.835 120.235Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M341.813 120.235C340.541 122.727 336.961 124.747 333.815 124.747C330.668 124.747 329.147 122.727 330.418 120.235C331.687 117.743 335.27 115.723 338.415 115.723C341.563 115.723 343.084 117.743 341.813 120.235Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M334.764 134.049C333.492 136.541 329.912 138.561 326.766 138.561C323.619 138.561 322.098 136.541 323.369 134.049C324.638 131.557 328.221 129.537 331.366 129.537C334.514 129.537 336.035 131.557 334.764 134.049Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M367.279 30.0672C369.716 31.7012 367.477 35.8687 363.361 37.4165C357.201 39.7327 353.673 35.181 358.871 31.317C361.763 29.1677 365.207 28.6793 367.279 30.0672Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M360.655 48.0619C359.384 50.5541 355.804 52.5741 352.658 52.5741C349.51 52.5741 347.99 50.5541 349.261 48.0619C350.53 45.5696 354.112 43.5496 357.258 43.5496C360.406 43.5496 361.927 45.5696 360.655 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M353.396 62.3071C352.125 64.7993 348.544 66.8194 345.399 66.8194C342.251 66.8194 340.73 64.7993 342.001 62.3071C343.271 59.8149 346.853 57.7949 349.999 57.7949C353.146 57.7949 354.667 59.8149 353.396 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M345.968 76.8574C344.697 79.3497 341.117 81.3697 337.971 81.3697C334.823 81.3697 333.302 79.3497 334.574 76.8574C335.843 74.3652 339.425 72.3452 342.571 72.3452C345.719 72.3452 347.24 74.3652 345.968 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M338.698 91.1237C337.427 93.6159 333.847 95.6359 330.701 95.6359C327.553 95.6359 326.033 93.6159 327.304 91.1237C328.573 88.6315 332.155 86.6115 335.301 86.6115C338.449 86.6115 339.97 88.6315 338.698 91.1237Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M375.248 19.4346C373.977 21.9269 370.396 23.9469 367.25 23.9469C364.103 23.9469 362.582 21.9269 363.853 19.4346C365.123 16.9424 368.705 14.9224 371.851 14.9224C374.998 14.9224 376.519 16.9424 375.248 19.4346Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M349.015 30.0672C351.452 31.7012 349.213 35.8687 345.097 37.4165C338.937 39.7327 335.408 35.181 340.607 31.317C343.499 29.1677 346.943 28.6793 349.015 30.0672Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M342.391 48.0619C341.12 50.5541 337.54 52.5741 334.394 52.5741C331.246 52.5741 329.725 50.5541 330.997 48.0619C332.266 45.5696 335.848 43.5496 338.994 43.5496C342.142 43.5496 343.662 45.5696 342.391 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M335.132 62.3071C333.861 64.7993 330.28 66.8194 327.134 66.8194C323.987 66.8194 322.466 64.7993 323.737 62.3071C325.007 59.8149 328.589 57.7949 331.735 57.7949C334.882 57.7949 336.403 59.8149 335.132 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M324.159 48.0619C322.887 50.5541 319.307 52.5741 316.161 52.5741C313.014 52.5741 311.493 50.5541 312.764 48.0619C314.033 45.5696 317.616 43.5496 320.761 43.5496C323.909 43.5496 325.43 45.5696 324.159 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M294.612 30.0672C297.048 31.7012 294.809 35.8687 290.694 37.4165C284.533 39.7327 281.005 35.181 286.203 31.317C289.096 29.1677 292.54 28.6793 294.612 30.0672Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M287.988 48.0619C286.717 50.5541 283.136 52.5741 279.99 52.5741C276.843 52.5741 275.322 50.5541 276.593 48.0619C277.863 45.5696 281.445 43.5496 284.591 43.5496C287.738 43.5496 289.259 45.5696 287.988 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M280.729 62.3071C279.457 64.7993 275.877 66.8194 272.731 66.8194C269.584 66.8194 268.063 64.7993 269.334 62.3071C270.603 59.8149 274.186 57.7949 277.331 57.7949C280.479 57.7949 282 59.8149 280.729 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M273.301 76.8574C272.03 79.3497 268.449 81.3697 265.303 81.3697C262.156 81.3697 260.635 79.3497 261.906 76.8574C263.176 74.3652 266.758 72.3452 269.904 72.3452C273.051 72.3452 274.572 74.3652 273.301 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M276.527 30.0672C278.963 31.7012 276.724 35.8687 272.609 37.4165C266.448 39.7327 262.92 35.181 268.118 31.317C271.011 29.1677 274.454 28.6793 276.527 30.0672Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M269.903 48.0619C268.631 50.5541 265.051 52.5741 261.905 52.5741C258.758 52.5741 257.237 50.5541 258.508 48.0619C259.777 45.5696 263.36 43.5496 266.505 43.5496C269.653 43.5496 271.174 45.5696 269.903 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M262.643 62.3071C261.372 64.7993 257.792 66.8194 254.646 66.8194C251.498 66.8194 249.977 64.7993 251.249 62.3071C252.518 59.8149 256.1 57.7949 259.246 57.7949C262.394 57.7949 263.914 59.8149 262.643 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M255.216 76.8574C253.944 79.3497 250.364 81.3697 247.218 81.3697C244.07 81.3697 242.55 79.3497 243.821 76.8574C245.09 74.3652 248.673 72.3452 251.818 72.3452C254.966 72.3452 256.487 74.3652 255.216 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M247.904 91.1974C246.632 93.6896 243.052 95.7096 239.906 95.7096C236.759 95.7096 235.238 93.6896 236.509 91.1974C237.778 88.7051 241.361 86.6851 244.506 86.6851C247.654 86.6851 249.175 88.7051 247.904 91.1974Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M240.655 105.432C239.383 107.924 235.803 109.944 232.657 109.944C229.51 109.944 227.989 107.924 229.26 105.432C230.529 102.94 234.112 100.92 237.257 100.92C240.405 100.92 241.926 102.94 240.655 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M233.227 119.993C231.956 122.485 228.375 124.505 225.23 124.505C222.082 124.505 220.561 122.485 221.832 119.993C223.102 117.501 226.684 115.481 229.83 115.481C232.977 115.481 234.498 117.501 233.227 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M258.687 105.432C257.416 107.924 253.836 109.944 250.69 109.944C247.542 109.944 246.021 107.924 247.293 105.432C248.562 102.94 252.144 100.92 255.29 100.92C258.438 100.92 259.959 102.94 258.687 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M251.26 119.993C249.988 122.485 246.408 124.505 243.262 124.505C240.115 124.505 238.594 122.485 239.865 119.993C241.135 117.501 244.717 115.481 247.862 115.481C251.01 115.481 252.531 117.501 251.26 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M269.135 119.993C267.863 122.485 264.283 124.505 261.137 124.505C257.99 124.505 256.469 122.485 257.74 119.993C259.009 117.501 262.592 115.481 265.737 115.481C268.885 115.481 270.406 117.501 269.135 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M251.891 48.0619C250.62 50.5541 247.039 52.5741 243.894 52.5741C240.746 52.5741 239.225 50.5541 240.496 48.0619C241.766 45.5696 245.348 43.5496 248.494 43.5496C251.641 43.5496 253.162 45.5696 251.891 48.0619Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M244.642 62.3071C243.371 64.7993 239.79 66.8194 236.645 66.8194C233.497 66.8194 231.976 64.7993 233.247 62.3071C234.517 59.8149 238.099 57.7949 241.245 57.7949C244.392 57.7949 245.913 59.8149 244.642 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M237.214 76.8574C235.943 79.3497 232.363 81.3697 229.217 81.3697C226.069 81.3697 224.548 79.3497 225.82 76.8574C227.089 74.3652 230.671 72.3452 233.817 72.3452C236.965 72.3452 238.486 74.3652 237.214 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M229.902 91.1974C228.631 93.6896 225.051 95.7096 221.905 95.7096C218.757 95.7096 217.236 93.6896 218.508 91.1974C219.777 88.7051 223.359 86.6851 226.505 86.6851C229.653 86.6851 231.174 88.7051 229.902 91.1974Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M222.643 105.432C221.372 107.924 217.791 109.944 214.646 109.944C211.498 109.944 209.977 107.924 211.248 105.432C212.518 102.94 216.1 100.92 219.246 100.92C222.393 100.92 223.914 102.94 222.643 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M215.226 119.993C213.954 122.485 210.374 124.505 207.228 124.505C204.081 124.505 202.56 122.485 203.831 119.993C205.101 117.501 208.683 115.481 211.829 115.481C214.976 115.481 216.497 117.501 215.226 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M207.924 134.322C206.653 136.815 203.073 138.835 199.927 138.835C196.779 138.835 195.258 136.815 196.53 134.322C197.799 131.83 201.381 129.81 204.527 129.81C207.675 129.81 209.196 131.83 207.924 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M200.497 148.873C199.225 151.365 195.645 153.385 192.499 153.385C189.352 153.385 187.831 151.365 189.102 148.873C190.371 146.38 193.954 144.36 197.099 144.36C200.247 144.36 201.768 146.38 200.497 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M226.336 62.3071C225.065 64.7993 221.484 66.8194 218.338 66.8194C215.191 66.8194 213.67 64.7993 214.941 62.3071C216.211 59.8149 219.793 57.7949 222.939 57.7949C226.086 57.7949 227.607 59.8149 226.336 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M218.908 76.8574C217.637 79.3497 214.056 81.3697 210.911 81.3697C207.763 81.3697 206.242 79.3497 207.513 76.8574C208.783 74.3652 212.365 72.3452 215.511 72.3452C218.658 72.3452 220.179 74.3652 218.908 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M211.596 91.1974C210.325 93.6896 206.744 95.7096 203.599 95.7096C200.451 95.7096 198.93 93.6896 200.201 91.1974C201.471 88.7051 205.053 86.6851 208.199 86.6851C211.346 86.6851 212.867 88.7051 211.596 91.1974Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M204.337 105.432C203.065 107.924 199.485 109.944 196.339 109.944C193.192 109.944 191.671 107.924 192.942 105.432C194.212 102.94 197.794 100.92 200.94 100.92C204.087 100.92 205.608 102.94 204.337 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M196.92 119.993C195.648 122.485 192.068 124.505 188.922 124.505C185.774 124.505 184.254 122.485 185.525 119.993C186.794 117.501 190.377 115.481 193.522 115.481C196.67 115.481 198.191 117.501 196.92 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M189.618 134.322C188.347 136.815 184.766 138.835 181.621 138.835C178.473 138.835 176.952 136.815 178.223 134.322C179.493 131.83 183.075 129.81 186.221 129.81C189.368 129.81 190.889 131.83 189.618 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M182.19 148.873C180.919 151.365 177.339 153.385 174.193 153.385C171.045 153.385 169.524 151.365 170.796 148.873C172.065 146.38 175.647 144.36 178.793 144.36C181.941 144.36 183.462 146.38 182.19 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M208.84 62.3071C207.568 64.7993 203.988 66.8194 200.842 66.8194C197.695 66.8194 196.174 64.7993 197.445 62.3071C198.714 59.8149 202.297 57.7949 205.442 57.7949C208.59 57.7949 210.111 59.8149 208.84 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M201.422 76.8574C200.151 79.3497 196.571 81.3697 193.425 81.3697C190.277 81.3697 188.757 79.3497 190.028 76.8574C191.297 74.3652 194.879 72.3452 198.025 72.3452C201.173 72.3452 202.694 74.3652 201.422 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M194.11 91.1974C192.839 93.6896 189.259 95.7096 186.113 95.7096C182.965 95.7096 181.445 93.6896 182.716 91.1974C183.985 88.7051 187.567 86.6851 190.713 86.6851C193.861 86.6851 195.382 88.7051 194.11 91.1974Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M186.851 105.432C185.58 107.924 181.999 109.944 178.854 109.944C175.706 109.944 174.185 107.924 175.456 105.432C176.726 102.94 180.308 100.92 183.454 100.92C186.601 100.92 188.122 102.94 186.851 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M179.434 119.993C178.163 122.485 174.582 124.505 171.436 124.505C168.289 124.505 166.768 122.485 168.039 119.993C169.309 117.501 172.891 115.481 176.037 115.481C179.184 115.481 180.705 117.501 179.434 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M172.122 134.322C170.851 136.815 167.27 138.835 164.124 138.835C160.977 138.835 159.456 136.815 160.727 134.322C161.997 131.83 165.579 129.81 168.725 129.81C171.872 129.81 173.393 131.83 172.122 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M164.705 148.873C163.433 151.365 159.853 153.385 156.707 153.385C153.56 153.385 152.039 151.365 153.31 148.873C154.58 146.38 158.162 144.36 161.307 144.36C164.455 144.36 165.976 146.38 164.705 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M190.533 62.3071C189.262 64.7993 185.682 66.8194 182.536 66.8194C179.388 66.8194 177.867 64.7993 179.139 62.3071C180.408 59.8149 183.99 57.7949 187.136 57.7949C190.284 57.7949 191.805 59.8149 190.533 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M183.116 76.8574C181.845 79.3497 178.265 81.3697 175.119 81.3697C171.971 81.3697 170.45 79.3497 171.722 76.8574C172.991 74.3652 176.573 72.3452 179.719 72.3452C182.867 72.3452 184.387 74.3652 183.116 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M175.804 91.1974C174.533 93.6896 170.953 95.7096 167.807 95.7096C164.659 95.7096 163.138 93.6896 164.41 91.1974C165.679 88.7051 169.261 86.6851 172.407 86.6851C175.555 86.6851 177.075 88.7051 175.804 91.1974Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M168.545 105.432C167.274 107.924 163.693 109.944 160.547 109.944C157.4 109.944 155.879 107.924 157.15 105.432C158.42 102.94 162.002 100.92 165.148 100.92C168.295 100.92 169.816 102.94 168.545 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M161.128 119.993C159.856 122.485 156.276 124.505 153.13 124.505C149.983 124.505 148.462 122.485 149.733 119.993C151.002 117.501 154.585 115.481 157.73 115.481C160.878 115.481 162.399 117.501 161.128 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M153.816 134.322C152.544 136.815 148.964 138.835 145.818 138.835C142.671 138.835 141.15 136.815 142.421 134.322C143.69 131.83 147.273 129.81 150.418 129.81C153.566 129.81 155.087 131.83 153.816 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M146.398 148.873C145.127 151.365 141.547 153.385 138.401 153.385C135.253 153.385 133.733 151.365 135.004 148.873C136.273 146.38 139.855 144.36 143.001 144.36C146.149 144.36 147.67 146.38 146.398 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M172.153 62.3071C170.882 64.7993 167.302 66.8194 164.156 66.8194C161.008 66.8194 159.488 64.7993 160.759 62.3071C162.028 59.8149 165.61 57.7949 168.756 57.7949C171.904 57.7949 173.425 59.8149 172.153 62.3071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M164.726 76.8574C163.454 79.3497 159.874 81.3697 156.728 81.3697C153.581 81.3697 152.06 79.3497 153.331 76.8574C154.601 74.3652 158.183 72.3452 161.329 72.3452C164.476 72.3452 165.997 74.3652 164.726 76.8574Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M139.181 91.3236C137.91 93.8158 134.329 95.8358 131.184 95.8358C128.036 95.8358 126.515 93.8158 127.786 91.3236C129.056 88.8314 132.638 86.8114 135.784 86.8114C138.932 86.8114 140.452 88.8314 139.181 91.3236Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M150.154 105.432C148.883 107.924 145.303 109.944 142.157 109.944C139.009 109.944 137.488 107.924 138.76 105.432C140.029 102.94 143.611 100.92 146.757 100.92C149.905 100.92 151.426 102.94 150.154 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M142.737 119.993C141.466 122.485 137.886 124.505 134.74 124.505C131.592 124.505 130.071 122.485 131.343 119.993C132.612 117.501 136.194 115.481 139.34 115.481C142.488 115.481 144.008 117.501 142.737 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M135.436 134.322C134.164 136.815 130.584 138.835 127.438 138.835C124.291 138.835 122.77 136.815 124.041 134.322C125.311 131.83 128.893 129.81 132.038 129.81C135.186 129.81 136.707 131.83 135.436 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M128.008 148.873C126.737 151.365 123.156 153.385 120.011 153.385C116.863 153.385 115.342 151.365 116.613 148.873C117.883 146.38 121.465 144.36 124.611 144.36C127.758 144.36 129.279 146.38 128.008 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M132.427 105.432C131.155 107.924 127.575 109.944 124.429 109.944C121.282 109.944 119.761 107.924 121.032 105.432C122.302 102.94 125.884 100.92 129.029 100.92C132.177 100.92 133.698 102.94 132.427 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M124.999 119.993C123.728 122.485 120.147 124.505 117.002 124.505C113.854 124.505 112.333 122.485 113.604 119.993C114.874 117.501 118.456 115.481 121.602 115.481C124.749 115.481 126.27 117.501 124.999 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M117.698 134.322C116.426 136.815 112.846 138.835 109.7 138.835C106.552 138.835 105.032 136.815 106.303 134.322C107.572 131.83 111.154 129.81 114.3 129.81C117.448 129.81 118.969 131.83 117.698 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M110.28 148.873C109.009 151.365 105.429 153.385 102.283 153.385C99.1353 153.385 97.6144 151.365 98.8857 148.873C100.155 146.38 103.737 144.36 106.883 144.36C110.031 144.36 111.552 146.38 110.28 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M114.12 105.432C112.849 107.924 109.269 109.944 106.123 109.944C102.975 109.944 101.455 107.924 102.726 105.432C103.995 102.94 107.577 100.92 110.723 100.92C113.871 100.92 115.392 102.94 114.12 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M106.693 119.993C105.421 122.485 101.841 124.505 98.6953 124.505C95.5477 124.505 94.0269 122.485 95.2981 119.993C96.5676 117.501 100.15 115.481 103.296 115.481C106.443 115.481 107.964 117.501 106.693 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M99.3912 134.322C98.12 136.815 94.5396 138.835 91.3938 138.835C88.2462 138.835 86.7254 136.815 87.9966 134.322C89.2661 131.83 92.8482 129.81 95.994 129.81C99.1417 129.81 100.662 131.83 99.3912 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M91.974 148.873C90.7028 151.365 87.1224 153.385 83.9766 153.385C80.829 153.385 79.3082 151.365 80.5794 148.873C81.8489 146.38 85.431 144.36 88.5768 144.36C91.7244 144.36 93.2453 146.38 91.974 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M95.73 105.432C94.4588 107.924 90.8784 109.944 87.7326 109.944C84.585 109.944 83.0641 107.924 84.3354 105.432C85.6049 102.94 89.187 100.92 92.3328 100.92C95.4804 100.92 97.0013 102.94 95.73 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M88.3128 119.993C87.0415 122.485 83.4612 124.505 80.3154 124.505C77.1678 124.505 75.6469 122.485 76.9182 119.993C78.1877 117.501 81.7698 115.481 84.9156 115.481C88.0632 115.481 89.5841 117.501 88.3128 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M81.0008 134.322C79.7295 136.815 76.1492 138.835 73.0034 138.835C69.8558 138.835 68.3349 136.815 69.6062 134.322C70.8756 131.83 74.4578 129.81 77.6036 129.81C80.7512 129.81 82.272 131.83 81.0008 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M73.5836 148.873C72.3124 151.365 68.732 153.385 65.5862 153.385C62.4386 153.385 60.9178 151.365 62.189 148.873C63.4585 146.38 67.0406 144.36 70.1864 144.36C73.334 144.36 74.8549 146.38 73.5836 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M78.2864 105.432C77.0152 107.924 73.4348 109.944 70.289 109.944C67.1414 109.944 65.6206 107.924 66.8918 105.432C68.1613 102.94 71.7434 100.92 74.8892 100.92C78.0368 100.92 79.5577 102.94 78.2864 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M70.8587 119.993C69.5874 122.485 66.0071 124.505 62.8613 124.505C59.7137 124.505 58.1928 122.485 59.4641 119.993C60.7336 117.501 64.3157 115.481 67.4615 115.481C70.6091 115.481 72.13 117.501 70.8587 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M63.5572 134.322C62.286 136.815 58.7057 138.835 55.5598 138.835C52.4122 138.835 50.8914 136.815 52.1626 134.322C53.4321 131.83 57.0142 129.81 60.1601 129.81C63.3077 129.81 64.8285 131.83 63.5572 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M56.1295 148.873C54.8583 151.365 51.2779 153.385 48.1321 153.385C44.9845 153.385 43.4637 151.365 44.7349 148.873C46.0044 146.38 49.5865 144.36 52.7323 144.36C55.8799 144.36 57.4008 146.38 56.1295 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M59.9802 105.432C58.7089 107.924 55.1286 109.944 51.9827 109.944C48.8351 109.944 47.3143 107.924 48.5856 105.432C49.855 102.94 53.4371 100.92 56.583 100.92C59.7306 100.92 61.2514 102.94 59.9802 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M52.5524 119.993C51.2812 122.485 47.7008 124.505 44.555 124.505C41.4074 124.505 39.8866 122.485 41.1578 119.993C42.4273 117.501 46.0094 115.481 49.1552 115.481C52.3028 115.481 53.8237 117.501 52.5524 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M45.251 134.322C43.9797 136.815 40.3994 138.835 37.2536 138.835C34.1059 138.835 32.5851 136.815 33.8564 134.322C35.1258 131.83 38.708 129.81 41.8538 129.81C45.0014 129.81 46.5222 131.83 45.251 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M37.8233 148.873C36.552 151.365 32.9717 153.385 29.8258 153.385C26.6782 153.385 25.1574 151.365 26.4286 148.873C27.6981 146.38 31.2802 144.36 34.4261 144.36C37.5737 144.36 39.0945 146.38 37.8233 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M41.5897 105.432C40.3184 107.924 36.7381 109.944 33.5923 109.944C30.4447 109.944 28.9238 107.924 30.1951 105.432C31.4646 102.94 35.0467 100.92 38.1925 100.92C41.3401 100.92 42.861 102.94 41.5897 105.432Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M34.162 119.993C32.8907 122.485 29.3104 124.505 26.1646 124.505C23.017 124.505 21.4961 122.485 22.7674 119.993C24.0369 117.501 27.619 115.481 30.7648 115.481C33.9124 115.481 35.4333 117.501 34.162 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M26.8605 134.322C25.5893 136.815 22.0089 138.835 18.8631 138.835C15.7155 138.835 14.1947 136.815 15.4659 134.322C16.7354 131.83 20.3175 129.81 23.4633 129.81C26.6109 129.81 28.1318 131.83 26.8605 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M19.4433 148.873C18.1721 151.365 14.5917 153.385 11.4459 153.385C8.29832 153.385 6.77748 151.365 8.04873 148.873C9.31819 146.38 12.9003 144.36 16.0461 144.36C19.1938 144.36 20.7146 146.38 19.4433 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M15.9504 119.993C14.6792 122.485 11.0988 124.505 7.95299 124.505C4.80538 124.505 3.28454 122.485 4.5558 119.993C5.82526 117.501 9.40739 115.481 12.5532 115.481C15.7008 115.481 17.2217 117.501 15.9504 119.993Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M85.4616 91.3446C84.1904 93.8369 80.61 95.8569 77.4642 95.8569C74.3166 95.8569 72.7958 93.8369 74.067 91.3446C75.3365 88.8524 78.9186 86.8324 82.0644 86.8324C85.2121 86.8324 86.7329 88.8524 85.4616 91.3446Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M67.1659 91.3446C65.8946 93.8369 62.3143 95.8569 59.1685 95.8569C56.0209 95.8569 54.5 93.8369 55.7713 91.3446C57.0407 88.8524 60.6229 86.8324 63.7687 86.8324C66.9163 86.8324 68.4371 88.8524 67.1659 91.3446Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M48.7754 91.3446C47.5042 93.8369 43.9239 95.8569 40.778 95.8569C37.6304 95.8569 36.1096 93.8369 37.3808 91.3446C38.6503 88.8524 42.2324 86.8324 45.3783 86.8324C48.5259 86.8324 50.0467 88.8524 48.7754 91.3446Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M12.5101 162.476C11.2388 164.968 7.6585 166.988 4.51269 166.988C1.36507 166.988 -0.155765 164.968 1.11549 162.476C2.38495 159.984 5.96709 157.964 9.1129 157.964C12.2605 157.964 13.7814 159.984 12.5101 162.476Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M66.3979 162.971C65.1266 165.463 61.5463 167.483 58.4005 167.483C55.2529 167.483 53.732 165.463 55.0033 162.971C56.2727 160.478 59.8549 158.458 63.0007 158.458C66.1483 158.458 67.6691 160.478 66.3979 162.971Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M103.031 163.097C101.76 165.589 98.1799 167.609 95.034 167.609C91.8864 167.609 90.3656 165.589 91.6369 163.097C92.9063 160.605 96.4884 158.585 99.6343 158.585C102.782 158.585 104.303 160.605 103.031 163.097Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M95.6038 177.647C94.3325 180.139 90.7522 182.159 87.6063 182.159C84.4587 182.159 82.9379 180.139 84.2091 177.647C85.4786 175.155 89.0607 173.135 92.2066 173.135C95.3542 173.135 96.875 175.155 95.6038 177.647Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M84.7252 163.097C83.4539 165.589 79.8736 167.609 76.7278 167.609C73.5802 167.609 72.0593 165.589 73.3306 163.097C74.6 160.605 78.1822 158.585 81.328 158.585C84.4756 158.585 85.9964 160.605 84.7252 163.097Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M88.439 191.713C87.1678 194.206 83.5874 196.226 80.4416 196.226C77.294 196.226 75.7732 194.206 77.0444 191.713C78.3139 189.221 81.896 187.201 85.0418 187.201C88.1894 187.201 89.7103 189.221 88.439 191.713Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M120.833 162.96C119.561 165.452 115.981 167.472 112.835 167.472C109.688 167.472 108.167 165.452 109.438 162.96C110.708 160.468 114.29 158.448 117.436 158.448C120.583 158.448 122.104 160.468 120.833 162.96Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M113.405 177.521C112.134 180.013 108.553 182.033 105.408 182.033C102.26 182.033 100.739 180.013 102.01 177.521C103.28 175.029 106.862 173.009 110.008 173.009C113.155 173.009 114.676 175.029 113.405 177.521Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M106.104 191.84C104.832 194.332 101.252 196.352 98.1061 196.352C94.9585 196.352 93.4377 194.332 94.7089 191.84C95.9784 189.348 99.5605 187.328 102.706 187.328C105.854 187.328 107.375 189.348 106.104 191.84Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M98.6864 206.401C97.4151 208.893 93.8348 210.913 90.689 210.913C87.5413 210.913 86.0205 208.893 87.2918 206.401C88.5612 203.908 92.1434 201.888 95.2892 201.888C98.4368 201.888 99.9576 203.908 98.6864 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M91.6479 220.193C90.3767 222.686 86.7963 224.706 83.6505 224.706C80.5029 224.706 78.9821 222.686 80.2533 220.193C81.5228 217.701 85.1049 215.681 88.2507 215.681C91.3983 215.681 92.9192 217.701 91.6479 220.193Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M138.844 162.96C137.573 165.452 133.993 167.472 130.847 167.472C127.699 167.472 126.179 165.452 127.45 162.96C128.719 160.468 132.301 158.448 135.447 158.448C138.595 158.448 140.116 160.468 138.844 162.96Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M131.417 177.521C130.145 180.013 126.565 182.033 123.419 182.033C120.272 182.033 118.751 180.013 120.022 177.521C121.292 175.029 124.874 173.009 128.019 173.009C131.167 173.009 132.688 175.029 131.417 177.521Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M124.115 191.84C122.844 194.332 119.264 196.352 116.118 196.352C112.97 196.352 111.449 194.332 112.721 191.84C113.99 189.348 117.572 187.328 120.718 187.328C123.866 187.328 125.387 189.348 124.115 191.84Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M116.687 206.401C115.416 208.893 111.836 210.913 108.69 210.913C105.542 210.913 104.022 208.893 105.293 206.401C106.562 203.908 110.144 201.888 113.29 201.888C116.438 201.888 117.959 203.908 116.687 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M109.66 220.193C108.388 222.686 104.808 224.706 101.662 224.706C98.5146 224.706 96.9937 222.686 98.265 220.193C99.5345 217.701 103.117 215.681 106.262 215.681C109.41 215.681 110.931 217.701 109.66 220.193Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M102.242 234.754C100.971 237.246 97.3908 239.266 94.245 239.266C91.0974 239.266 89.5765 237.246 90.8478 234.754C92.1172 232.262 95.6994 230.242 98.8452 230.242C101.993 230.242 103.514 232.262 102.242 234.754Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M95.0777 248.789C93.8065 251.281 90.2261 253.301 87.0803 253.301C83.9327 253.301 82.4119 251.281 83.6831 248.789C84.9526 246.297 88.5347 244.277 91.6805 244.277C94.8281 244.277 96.349 246.297 95.0777 248.789Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M157.519 162.96C156.248 165.452 152.667 167.472 149.521 167.472C146.374 167.472 144.853 165.452 146.124 162.96C147.394 160.468 150.976 158.448 154.122 158.448C157.269 158.448 158.79 160.468 157.519 162.96Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M150.102 177.521C148.83 180.013 145.25 182.033 142.104 182.033C138.957 182.033 137.436 180.013 138.707 177.521C139.977 175.029 143.559 173.009 146.705 173.009C149.852 173.009 151.373 175.029 150.102 177.521Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M142.8 191.84C141.529 194.332 137.949 196.352 134.803 196.352C131.655 196.352 130.134 194.332 131.406 191.84C132.675 189.348 136.257 187.328 139.403 187.328C142.551 187.328 144.072 189.348 142.8 191.84Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M135.373 206.401C134.101 208.893 130.521 210.913 127.375 210.913C124.228 210.913 122.707 208.893 123.978 206.401C125.247 203.908 128.83 201.888 131.975 201.888C135.123 201.888 136.644 203.908 135.373 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M128.345 220.193C127.073 222.686 123.493 224.706 120.347 224.706C117.2 224.706 115.679 222.686 116.95 220.193C118.219 217.701 121.802 215.681 124.947 215.681C128.095 215.681 129.616 217.701 128.345 220.193Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M120.927 234.754C119.656 237.246 116.076 239.266 112.93 239.266C109.782 239.266 108.262 237.246 109.533 234.754C110.802 232.262 114.384 230.242 117.53 230.242C120.678 230.242 122.199 232.262 120.927 234.754Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M113.763 248.789C112.491 251.281 108.911 253.301 105.765 253.301C102.618 253.301 101.097 251.281 102.368 248.789C103.638 246.297 107.22 244.277 110.366 244.277C113.513 244.277 115.034 246.297 113.763 248.789Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M106.282 263.466C105.011 265.958 101.431 267.978 98.285 267.978C95.1374 267.978 93.6165 265.958 94.8878 263.466C96.1573 260.973 99.7394 258.953 102.885 258.953C106.033 258.953 107.554 260.973 106.282 263.466Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M175.015 162.96C173.744 165.452 170.164 167.472 167.018 167.472C163.87 167.472 162.349 165.452 163.62 162.96C164.89 160.468 168.472 158.448 171.618 158.448C174.766 158.448 176.286 160.468 175.015 162.96Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M167.587 177.521C166.316 180.013 162.736 182.033 159.59 182.033C156.442 182.033 154.922 180.013 156.193 177.521C157.462 175.029 161.044 173.009 164.19 173.009C167.338 173.009 168.859 175.029 167.587 177.521Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M160.286 191.84C159.015 194.332 155.434 196.352 152.289 196.352C149.141 196.352 147.62 194.332 148.891 191.84C150.161 189.348 153.743 187.328 156.889 187.328C160.036 187.328 161.557 189.348 160.286 191.84Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M152.869 206.401C151.597 208.893 148.017 210.913 144.871 210.913C141.724 210.913 140.203 208.893 141.474 206.401C142.744 203.908 146.326 201.888 149.472 201.888C152.619 201.888 154.14 203.908 152.869 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M145.83 220.193C144.559 222.686 140.979 224.706 137.833 224.706C134.685 224.706 133.164 222.686 134.436 220.193C135.705 217.701 139.287 215.681 142.433 215.681C145.581 215.681 147.102 217.701 145.83 220.193Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M138.413 234.754C137.142 237.246 133.561 239.266 130.416 239.266C127.268 239.266 125.747 237.246 127.018 234.754C128.288 232.262 131.87 230.242 135.016 230.242C138.163 230.242 139.684 232.262 138.413 234.754Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M131.259 248.789C129.988 251.281 126.407 253.301 123.261 253.301C120.114 253.301 118.593 251.281 119.864 248.789C121.134 246.297 124.716 244.277 127.862 244.277C131.009 244.277 132.53 246.297 131.259 248.789Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M123.768 263.466C122.497 265.958 118.916 267.978 115.771 267.978C112.623 267.978 111.102 265.958 112.373 263.466C113.643 260.973 117.225 258.953 120.371 258.953C123.518 258.953 125.039 260.973 123.768 263.466Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M116.54 277.648C115.269 280.14 111.689 282.16 108.543 282.16C105.395 282.16 103.874 280.14 105.146 277.648C106.415 275.155 109.997 273.135 113.143 273.135C116.291 273.135 117.811 275.155 116.54 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M134.92 277.648C133.649 280.14 130.069 282.16 126.923 282.16C123.775 282.16 122.254 280.14 123.526 277.648C124.795 275.155 128.377 273.135 131.523 273.135C134.671 273.135 136.191 275.155 134.92 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M152.953 277.648C151.682 280.14 148.101 282.16 144.955 282.16C141.808 282.16 140.287 280.14 141.558 277.648C142.828 275.155 146.41 273.135 149.556 273.135C152.703 273.135 154.224 275.155 152.953 277.648Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M195.205 263.466C193.933 265.958 190.353 267.978 187.207 267.978C184.06 267.978 182.539 265.958 183.81 263.466C185.079 260.973 188.662 258.953 191.807 258.953C194.955 258.953 196.476 260.973 195.205 263.466Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M185.883 177.521C184.612 180.013 181.032 182.033 177.886 182.033C174.738 182.033 173.217 180.013 174.489 177.521C175.758 175.029 179.34 173.009 182.486 173.009C185.634 173.009 187.154 175.029 185.883 177.521Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M178.571 191.84C177.3 194.332 173.72 196.352 170.574 196.352C167.426 196.352 165.905 194.332 167.177 191.84C168.446 189.348 172.028 187.328 175.174 187.328C178.322 187.328 179.842 189.348 178.571 191.84Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M171.154 206.401C169.883 208.893 166.302 210.913 163.156 210.913C160.009 210.913 158.488 208.893 159.759 206.401C161.029 203.908 164.611 201.888 167.757 201.888C170.904 201.888 172.425 203.908 171.154 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M164.126 220.193C162.855 222.686 159.274 224.706 156.129 224.706C152.981 224.706 151.46 222.686 152.731 220.193C154.001 217.701 157.583 215.681 160.729 215.681C163.876 215.681 165.397 217.701 164.126 220.193Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M156.698 234.754C155.427 237.246 151.847 239.266 148.701 239.266C145.553 239.266 144.032 237.246 145.304 234.754C146.573 232.262 150.155 230.242 153.301 230.242C156.449 230.242 157.97 232.262 156.698 234.754Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M196.541 191.84C195.269 194.332 191.689 196.352 188.543 196.352C185.396 196.352 183.875 194.332 185.146 191.84C186.416 189.348 189.998 187.328 193.144 187.328C196.291 187.328 197.812 189.348 196.541 191.84Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M189.113 206.401C187.842 208.893 184.261 210.913 181.116 210.913C177.968 210.913 176.447 208.893 177.718 206.401C178.988 203.908 182.57 201.888 185.716 201.888C188.863 201.888 190.384 203.908 189.113 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M182.085 220.193C180.814 222.686 177.234 224.706 174.088 224.706C170.94 224.706 169.419 222.686 170.691 220.193C171.96 217.701 175.542 215.681 178.688 215.681C181.836 215.681 183.356 217.701 182.085 220.193Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M174.668 234.754C173.397 237.246 169.816 239.266 166.671 239.266C163.523 239.266 162.002 237.246 163.273 234.754C164.543 232.262 168.125 230.242 171.271 230.242C174.418 230.242 175.939 232.262 174.668 234.754Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M214.552 191.84C213.281 194.332 209.701 196.352 206.555 196.352C203.407 196.352 201.887 194.332 203.158 191.84C204.427 189.348 208.009 187.328 211.155 187.328C214.303 187.328 215.824 189.348 214.552 191.84Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M207.135 206.401C205.864 208.893 202.284 210.913 199.138 210.913C195.99 210.913 194.469 208.893 195.741 206.401C197.01 203.908 200.592 201.888 203.738 201.888C206.886 201.888 208.406 203.908 207.135 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M200.097 220.193C198.826 222.686 195.245 224.706 192.099 224.706C188.952 224.706 187.431 222.686 188.702 220.193C189.972 217.701 193.554 215.681 196.7 215.681C199.847 215.681 201.368 217.701 200.097 220.193Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M192.68 234.754C191.408 237.246 187.828 239.266 184.682 239.266C181.535 239.266 180.014 237.246 181.285 234.754C182.554 232.262 186.137 230.242 189.282 230.242C192.43 230.242 193.951 232.262 192.68 234.754Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M185.389 249.042C184.117 251.534 180.537 253.554 177.391 253.554C174.244 253.554 172.723 251.534 173.994 249.042C175.263 246.549 178.846 244.529 181.991 244.529C185.139 244.529 186.66 246.549 185.389 249.042Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M243.885 134.322C242.613 136.815 239.033 138.835 235.887 138.835C232.74 138.835 231.219 136.815 232.49 134.322C233.759 131.83 237.342 129.81 240.487 129.81C243.635 129.81 245.156 131.83 243.885 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M236.457 148.873C235.186 151.365 231.605 153.385 228.459 153.385C225.312 153.385 223.791 151.365 225.062 148.873C226.332 146.38 229.914 144.36 233.06 144.36C236.207 144.36 237.728 146.38 236.457 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M229.429 162.665C228.158 165.158 224.577 167.178 221.431 167.178C218.284 167.178 216.763 165.158 218.034 162.665C219.304 160.173 222.886 158.153 226.032 158.153C229.179 158.153 230.7 160.173 229.429 162.665Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M222.012 177.226C220.74 179.719 217.16 181.739 214.014 181.739C210.867 181.739 209.346 179.719 210.617 177.226C211.887 174.734 215.469 172.714 218.614 172.714C221.762 172.714 223.283 174.734 222.012 177.226Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M261.938 134.322C260.667 136.815 257.087 138.835 253.941 138.835C250.793 138.835 249.272 136.815 250.544 134.322C251.813 131.83 255.395 129.81 258.541 129.81C261.689 129.81 263.21 131.83 261.938 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M254.511 148.873C253.239 151.365 249.659 153.385 246.513 153.385C243.366 153.385 241.845 151.365 243.116 148.873C244.385 146.38 247.968 144.36 251.113 144.36C254.261 144.36 255.782 146.38 254.511 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M247.483 162.665C246.211 165.158 242.631 167.178 239.485 167.178C236.338 167.178 234.817 165.158 236.088 162.665C237.358 160.173 240.94 158.153 244.085 158.153C247.233 158.153 248.754 160.173 247.483 162.665Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M240.055 177.226C238.784 179.719 235.203 181.739 232.058 181.739C228.91 181.739 227.389 179.719 228.66 177.226C229.93 174.734 233.512 172.714 236.658 172.714C239.805 172.714 241.326 174.734 240.055 177.226Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M281.044 134.322C279.773 136.815 276.193 138.835 273.047 138.835C269.899 138.835 268.378 136.815 269.65 134.322C270.919 131.83 274.501 129.81 277.647 129.81C280.795 129.81 282.315 131.83 281.044 134.322Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M273.627 148.873C272.356 151.365 268.775 153.385 265.63 153.385C262.482 153.385 260.961 151.365 262.232 148.873C263.502 146.38 267.084 144.36 270.23 144.36C273.377 144.36 274.898 146.38 273.627 148.873Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M266.599 162.665C265.328 165.158 261.747 167.178 258.602 167.178C255.454 167.178 253.933 165.158 255.204 162.665C256.474 160.173 260.056 158.153 263.202 158.153C266.349 158.153 267.87 160.173 266.599 162.665Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M259.171 177.226C257.9 179.719 254.32 181.739 251.174 181.739C248.026 181.739 246.505 179.719 247.777 177.226C249.046 174.734 252.628 172.714 255.774 172.714C258.922 172.714 260.443 174.734 259.171 177.226Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M283.653 162.665C282.382 165.158 278.802 167.178 275.656 167.178C272.508 167.178 270.987 165.158 272.259 162.665C273.528 160.173 277.11 158.153 280.256 158.153C283.404 158.153 284.925 160.173 283.653 162.665Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M231.722 191.84C230.451 194.332 226.871 196.352 223.725 196.352C220.577 196.352 219.057 194.332 220.328 191.84C221.597 189.348 225.179 187.328 228.325 187.328C231.473 187.328 232.994 189.348 231.722 191.84Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M224.295 206.401C223.023 208.893 219.443 210.913 216.297 210.913C213.15 210.913 211.629 208.893 212.9 206.401C214.17 203.908 217.752 201.888 220.898 201.888C224.045 201.888 225.566 203.908 224.295 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M243.39 206.401C242.119 208.893 238.538 210.913 235.393 210.913C232.245 210.913 230.724 208.893 231.995 206.401C233.265 203.908 236.847 201.888 239.993 201.888C243.14 201.888 244.661 203.908 243.39 206.401Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M261.002 349.316C259.731 351.808 256.15 353.828 253.005 353.828C249.857 353.828 248.336 351.808 249.607 349.316C250.877 346.824 254.459 344.804 257.605 344.804C260.752 344.804 262.273 346.824 261.002 349.316Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M268.409 334.776C267.137 337.268 263.557 339.288 260.411 339.288C257.264 339.288 255.743 337.268 257.014 334.776C258.284 332.284 261.866 330.264 265.011 330.264C268.159 330.264 269.68 332.284 268.409 334.776Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M250.376 334.829C249.105 337.321 245.524 339.341 242.378 339.341C239.231 339.341 237.71 337.321 238.981 334.829C240.251 332.336 243.833 330.316 246.979 330.316C250.126 330.316 251.647 332.336 250.376 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M235.784 363.445C234.512 365.938 230.932 367.958 227.786 367.958C224.638 367.958 223.118 365.938 224.389 363.445C225.658 360.953 229.24 358.933 232.386 358.933C235.534 358.933 237.055 360.953 235.784 363.445Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M243.19 348.916C241.919 351.408 238.339 353.428 235.193 353.428C232.045 353.428 230.524 351.408 231.796 348.916C233.065 346.424 236.647 344.404 239.793 344.404C242.941 344.404 244.461 346.424 243.19 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M228.335 378.069C227.063 380.561 223.483 382.581 220.337 382.581C217.19 382.581 215.669 380.561 216.94 378.069C218.21 375.577 221.792 373.557 224.938 373.557C228.085 373.557 229.606 375.577 228.335 378.069Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M232.259 334.829C230.988 337.321 227.407 339.341 224.262 339.341C221.114 339.341 219.593 337.321 220.864 334.829C222.134 332.336 225.716 330.316 228.862 330.316C232.009 330.316 233.53 332.336 232.259 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M239.666 320.299C238.394 322.792 234.814 324.812 231.668 324.812C228.521 324.812 227 322.792 228.271 320.299C229.541 317.807 233.123 315.787 236.268 315.787C239.416 315.787 240.937 317.807 239.666 320.299Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M217.667 363.445C216.395 365.938 212.815 367.958 209.669 367.958C206.522 367.958 205.001 365.938 206.272 363.445C207.541 360.953 211.124 358.933 214.269 358.933C217.417 358.933 218.938 360.953 217.667 363.445Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M225.073 348.916C223.802 351.408 220.222 353.428 217.076 353.428C213.928 353.428 212.407 351.408 213.679 348.916C214.948 346.424 218.53 344.404 221.676 344.404C224.824 344.404 226.345 346.424 225.073 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M202.99 392.241C201.719 394.733 198.138 396.753 194.993 396.753C191.845 396.753 190.324 394.733 191.595 392.241C192.865 389.749 196.447 387.729 199.593 387.729C202.74 387.729 204.261 389.749 202.99 392.241Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M210.218 378.069C208.947 380.561 205.366 382.581 202.22 382.581C199.073 382.581 197.552 380.561 198.823 378.069C200.093 375.577 203.675 373.557 206.821 373.557C209.968 373.557 211.489 375.577 210.218 378.069Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M214.552 334.829C213.281 337.321 209.701 339.341 206.555 339.341C203.407 339.341 201.887 337.321 203.158 334.829C204.427 332.336 208.009 330.316 211.155 330.316C214.303 330.316 215.824 332.336 214.552 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M221.959 320.299C220.688 322.792 217.108 324.812 213.962 324.812C210.814 324.812 209.293 322.792 210.564 320.299C211.834 317.807 215.416 315.787 218.562 315.787C221.71 315.787 223.23 317.807 221.959 320.299Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M199.96 363.445C198.689 365.938 195.108 367.958 191.963 367.958C188.815 367.958 187.294 365.938 188.565 363.445C189.835 360.953 193.417 358.933 196.563 358.933C199.71 358.933 201.231 360.953 199.96 363.445Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M207.377 348.916C206.106 351.408 202.526 353.428 199.38 353.428C196.232 353.428 194.711 351.408 195.983 348.916C197.252 346.424 200.834 344.404 203.98 344.404C207.128 344.404 208.648 346.424 207.377 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M185.283 392.241C184.012 394.733 180.432 396.753 177.286 396.753C174.138 396.753 172.618 394.733 173.889 392.241C175.158 389.749 178.74 387.729 181.886 387.729C185.034 387.729 186.555 389.749 185.283 392.241Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M192.511 378.069C191.24 380.561 187.66 382.581 184.514 382.581C181.366 382.581 179.845 380.561 181.117 378.069C182.386 375.577 185.968 373.557 189.114 373.557C192.262 373.557 193.783 375.577 192.511 378.069Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M229.103 306.307C227.831 308.799 224.251 310.819 221.105 310.819C217.958 310.819 216.437 308.799 217.708 306.307C218.978 303.814 222.56 301.794 225.706 301.794C228.853 301.794 230.374 303.814 229.103 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M177.877 406.77C176.605 409.262 173.025 411.282 169.879 411.282C166.732 411.282 165.211 409.262 166.482 406.77C167.752 404.278 171.334 402.258 174.48 402.258C177.627 402.258 179.148 404.278 177.877 406.77Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M196.194 334.829C194.922 337.321 191.342 339.341 188.196 339.341C185.049 339.341 183.528 337.321 184.799 334.829C186.068 332.336 189.651 330.316 192.796 330.316C195.944 330.316 197.465 332.336 196.194 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M203.611 320.299C202.339 322.792 198.759 324.812 195.613 324.812C192.466 324.812 190.945 322.792 192.216 320.299C193.486 317.807 197.068 315.787 200.214 315.787C203.361 315.787 204.882 317.807 203.611 320.299Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M181.612 363.445C180.34 365.938 176.76 367.958 173.614 367.958C170.467 367.958 168.946 365.938 170.217 363.445C171.486 360.953 175.069 358.933 178.214 358.933C181.362 358.933 182.883 360.953 181.612 363.445Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M189.018 348.916C187.747 351.408 184.167 353.428 181.021 353.428C177.873 353.428 176.352 351.408 177.624 348.916C178.893 346.424 182.475 344.404 185.621 344.404C188.769 344.404 190.29 346.424 189.018 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M166.925 392.241C165.653 394.733 162.073 396.753 158.927 396.753C155.78 396.753 154.259 394.733 155.53 392.241C156.799 389.749 160.382 387.729 163.527 387.729C166.675 387.729 168.196 389.749 166.925 392.241Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M174.152 378.069C172.881 380.561 169.301 382.581 166.155 382.581C163.007 382.581 161.487 380.561 162.758 378.069C164.027 375.577 167.609 373.557 170.755 373.557C173.903 373.557 175.424 375.577 174.152 378.069Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M210.744 306.307C209.473 308.799 205.892 310.819 202.746 310.819C199.599 310.819 198.078 308.799 199.349 306.307C200.619 303.814 204.201 301.794 207.347 301.794C210.494 301.794 212.015 303.814 210.744 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M159.518 406.77C158.247 409.262 154.666 411.282 151.52 411.282C148.373 411.282 146.852 409.262 148.123 406.77C149.393 404.278 152.975 402.258 156.121 402.258C159.268 402.258 160.789 404.278 159.518 406.77Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M152.343 420.858C151.071 423.35 147.491 425.37 144.345 425.37C141.198 425.37 139.677 423.35 140.948 420.858C142.218 418.365 145.8 416.345 148.945 416.345C152.093 416.345 153.614 418.365 152.343 420.858Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M177.866 334.829C176.595 337.321 173.015 339.341 169.869 339.341C166.721 339.341 165.2 337.321 166.472 334.829C167.741 332.336 171.323 330.316 174.469 330.316C177.617 330.316 179.137 332.336 177.866 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M185.273 320.299C184.002 322.792 180.421 324.812 177.276 324.812C174.128 324.812 172.607 322.792 173.878 320.299C175.148 317.807 178.73 315.787 181.876 315.787C185.023 315.787 186.544 317.807 185.273 320.299Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M163.274 363.445C162.003 365.938 158.422 367.958 155.276 367.958C152.129 367.958 150.608 365.938 151.879 363.445C153.149 360.953 156.731 358.933 159.877 358.933C163.024 358.933 164.545 360.953 163.274 363.445Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M170.681 348.916C169.409 351.408 165.829 353.428 162.683 353.428C159.535 353.428 158.015 351.408 159.286 348.916C160.555 346.424 164.137 344.404 167.283 344.404C170.431 344.404 171.952 346.424 170.681 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M148.597 392.241C147.326 394.733 143.746 396.753 140.6 396.753C137.452 396.753 135.931 394.733 137.203 392.241C138.472 389.749 142.054 387.729 145.2 387.729C148.348 387.729 149.869 389.749 148.597 392.241Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M155.825 378.069C154.554 380.561 150.974 382.581 147.828 382.581C144.68 382.581 143.159 380.561 144.43 378.069C145.7 375.577 149.282 373.557 152.428 373.557C155.576 373.557 157.096 375.577 155.825 378.069Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M192.417 306.307C191.145 308.799 187.565 310.819 184.419 310.819C181.272 310.819 179.751 308.799 181.022 306.307C182.291 303.814 185.874 301.794 189.019 301.794C192.167 301.794 193.688 303.814 192.417 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M141.191 406.77C139.919 409.262 136.339 411.282 133.193 411.282C130.046 411.282 128.525 409.262 129.796 406.77C131.065 404.278 134.648 402.258 137.793 402.258C140.941 402.258 142.462 404.278 141.191 406.77Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M134.005 420.858C132.734 423.35 129.153 425.37 126.007 425.37C122.86 425.37 121.339 423.35 122.61 420.858C123.88 418.365 127.462 416.345 130.608 416.345C133.755 416.345 135.276 418.365 134.005 420.858Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M158.824 334.829C157.552 337.321 153.972 339.341 150.826 339.341C147.678 339.341 146.158 337.321 147.429 334.829C148.698 332.336 152.281 330.316 155.426 330.316C158.574 330.316 160.095 332.336 158.824 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M166.23 320.299C164.959 322.792 161.379 324.812 158.233 324.812C155.085 324.812 153.564 322.792 154.836 320.299C156.105 317.807 159.687 315.787 162.833 315.787C165.981 315.787 167.501 317.807 166.23 320.299Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M144.231 363.445C142.96 365.938 139.38 367.958 136.234 367.958C133.086 367.958 131.565 365.938 132.836 363.445C134.106 360.953 137.688 358.933 140.834 358.933C143.982 358.933 145.502 360.953 144.231 363.445Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M151.638 348.916C150.366 351.408 146.786 353.428 143.64 353.428C140.493 353.428 138.972 351.408 140.243 348.916C141.513 346.424 145.095 344.404 148.241 344.404C151.388 344.404 152.909 346.424 151.638 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M173.363 306.307C172.092 308.799 168.512 310.819 165.366 310.819C162.218 310.819 160.697 308.799 161.969 306.307C163.238 303.814 166.82 301.794 169.966 301.794C173.114 301.794 174.635 303.814 173.363 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M142.579 334.829C141.308 337.321 137.728 339.341 134.582 339.341C131.434 339.341 129.913 337.321 131.185 334.829C132.454 332.336 136.036 330.316 139.182 330.316C142.33 330.316 143.851 332.336 142.579 334.829Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M135.404 348.916C134.133 351.408 130.553 353.428 127.407 353.428C124.259 353.428 122.738 351.408 124.01 348.916C125.279 346.424 128.861 344.404 132.007 344.404C135.155 344.404 136.675 346.424 135.404 348.916Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M157.13 306.307C155.858 308.799 152.278 310.819 149.132 310.819C145.985 310.819 144.464 308.799 145.735 306.307C147.004 303.814 150.587 301.794 153.732 301.794C156.88 301.794 158.401 303.814 157.13 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M119.423 449.474C118.152 451.966 114.571 453.986 111.425 453.986C108.278 453.986 106.757 451.966 108.028 449.474C109.298 446.982 112.88 444.962 116.026 444.962C119.173 444.962 120.694 446.982 119.423 449.474Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M126.651 435.292C125.379 437.784 121.799 439.804 118.653 439.804C115.506 439.804 113.985 437.784 115.256 435.292C116.526 432.8 120.108 430.78 123.254 430.78C126.401 430.78 127.922 432.8 126.651 435.292Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M112.006 464.003C110.734 466.496 107.154 468.516 104.008 468.516C100.861 468.516 99.3399 466.496 100.611 464.003C101.881 461.511 105.463 459.491 108.609 459.491C111.756 459.491 113.277 461.511 112.006 464.003Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M104.736 478.28C103.465 480.773 99.8842 482.793 96.7384 482.793C93.5908 482.793 92.07 480.773 93.3412 478.28C94.6107 475.788 98.1928 473.768 101.339 473.768C104.486 473.768 106.007 475.788 104.736 478.28Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M115.898 420.858C114.627 423.35 111.047 425.37 107.901 425.37C104.753 425.37 103.233 423.35 104.504 420.858C105.773 418.365 109.355 416.345 112.501 416.345C115.649 416.345 117.17 418.365 115.898 420.858Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M101.306 449.474C100.035 451.966 96.4544 453.986 93.3086 453.986C90.161 453.986 88.6402 451.966 89.9114 449.474C91.1809 446.982 94.763 444.962 97.9088 444.962C101.056 444.962 102.577 446.982 101.306 449.474Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M108.544 435.292C107.273 437.784 103.693 439.804 100.547 439.804C97.3994 439.804 95.8785 437.784 97.1498 435.292C98.4193 432.8 102.001 430.78 105.147 430.78C108.295 430.78 109.816 432.8 108.544 435.292Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M93.8994 464.003C92.6281 466.496 89.0478 468.516 85.9019 468.516C82.7543 468.516 81.2335 466.496 82.5047 464.003C83.7742 461.511 87.3563 459.491 90.5022 459.491C93.6498 459.491 95.1706 461.511 93.8994 464.003Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M825.603 306.307C824.332 308.799 820.752 310.819 817.606 310.819C814.458 310.819 812.937 308.799 814.209 306.307C815.478 303.814 819.06 301.794 822.206 301.794C825.354 301.794 826.874 303.814 825.603 306.307Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M796.408 363.582C795.137 366.074 791.556 368.094 788.41 368.094C785.263 368.094 783.742 366.074 785.013 363.582C786.283 361.09 789.865 359.07 793.011 359.07C796.158 359.07 797.679 361.09 796.408 363.582Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M788.991 378.122C787.719 380.614 784.139 382.634 780.993 382.634C777.846 382.634 776.325 380.614 777.596 378.122C778.865 375.63 782.448 373.61 785.593 373.61C788.741 373.61 790.262 375.63 788.991 378.122Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M781.731 392.357C780.46 394.849 776.88 396.869 773.734 396.869C770.586 396.869 769.065 394.849 770.337 392.357C771.606 389.864 775.188 387.844 778.334 387.844C781.482 387.844 783.002 389.864 781.731 392.357Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M774.314 406.907C773.043 409.399 769.462 411.419 766.317 411.419C763.169 411.419 761.648 409.399 762.919 406.907C764.189 404.415 767.771 402.395 770.917 402.395C774.064 402.395 775.585 404.415 774.314 406.907Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M814.325 363.582C813.054 366.074 809.473 368.094 806.327 368.094C803.18 368.094 801.659 366.074 802.93 363.582C804.2 361.09 807.782 359.07 810.928 359.07C814.075 359.07 815.596 361.09 814.325 363.582Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M806.908 378.122C805.636 380.614 802.056 382.634 798.91 382.634C795.763 382.634 794.242 380.614 795.513 378.122C796.782 375.63 800.365 373.61 803.51 373.61C806.658 373.61 808.179 375.63 806.908 378.122Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M799.648 392.357C798.377 394.849 794.797 396.869 791.651 396.869C788.503 396.869 786.982 394.849 788.254 392.357C789.523 389.864 793.105 387.844 796.251 387.844C799.399 387.844 800.919 389.864 799.648 392.357Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M792.231 406.907C790.96 409.399 787.379 411.419 784.234 411.419C781.086 411.419 779.565 409.399 780.836 406.907C782.106 404.415 785.688 402.395 788.834 402.395C791.981 402.395 793.502 404.415 792.231 406.907Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M832.231 363.582C830.96 366.074 827.38 368.094 824.234 368.094C821.086 368.094 819.565 366.074 820.837 363.582C822.106 361.09 825.688 359.07 828.834 359.07C831.982 359.07 833.503 361.09 832.231 363.582Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M824.825 378.122C823.553 380.614 819.973 382.634 816.827 382.634C813.68 382.634 812.159 380.614 813.43 378.122C814.7 375.63 818.282 373.61 821.427 373.61C824.575 373.61 826.096 375.63 824.825 378.122Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M836.271 320.52C835 323.012 831.42 325.032 828.274 325.032C825.126 325.032 823.605 323.012 824.877 320.52C826.146 318.028 829.728 316.008 832.874 316.008C836.022 316.008 837.543 318.028 836.271 320.52Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M828.854 335.071C827.583 337.563 824.002 339.583 820.857 339.583C817.709 339.583 816.188 337.563 817.459 335.071C818.729 332.578 822.311 330.558 825.457 330.558C828.605 330.558 830.125 332.578 828.854 335.071Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M821.595 349.305C820.323 351.797 816.743 353.817 813.597 353.817C810.45 353.817 808.929 351.797 810.2 349.305C811.47 346.813 815.052 344.793 818.197 344.793C821.345 344.793 822.866 346.813 821.595 349.305Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M854.188 320.52C852.917 323.012 849.337 325.032 846.191 325.032C843.043 325.032 841.522 323.012 842.794 320.52C844.063 318.028 847.645 316.008 850.791 316.008C853.939 316.008 855.46 318.028 854.188 320.52Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M778.396 363.582C777.125 366.074 773.544 368.094 770.399 368.094C767.251 368.094 765.73 366.074 767.001 363.582C768.271 361.09 771.853 359.07 774.999 359.07C778.147 359.07 779.667 361.09 778.396 363.582Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M770.979 378.122C769.708 380.614 766.127 382.634 762.981 382.634C759.834 382.634 758.313 380.614 759.584 378.122C760.854 375.63 764.436 373.61 767.582 373.61C770.729 373.61 772.25 375.63 770.979 378.122Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M763.72 392.357C762.448 394.849 758.868 396.869 755.722 396.869C752.575 396.869 751.054 394.849 752.325 392.357C753.594 389.864 757.177 387.844 760.322 387.844C763.47 387.844 764.991 389.864 763.72 392.357Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M756.302 406.907C755.031 409.399 751.451 411.419 748.305 411.419C745.157 411.419 743.636 409.399 744.908 406.907C746.177 404.415 749.759 402.395 752.905 402.395C756.053 402.395 757.574 404.415 756.302 406.907Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M785.571 349.305C784.3 351.797 780.72 353.817 777.574 353.817C774.426 353.817 772.905 351.797 774.177 349.305C775.446 346.813 779.028 344.793 782.174 344.793C785.322 344.793 786.843 346.813 785.571 349.305Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M767.623 349.305C766.352 351.797 762.771 353.817 759.625 353.817C756.478 353.817 754.957 351.797 756.228 349.305C757.498 346.813 761.08 344.793 764.226 344.793C767.373 344.793 768.894 346.813 767.623 349.305Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M742.257 363.582C740.986 366.074 737.405 368.094 734.26 368.094C731.112 368.094 729.591 366.074 730.862 363.582C732.132 361.09 735.714 359.07 738.86 359.07C742.007 359.07 743.528 361.09 742.257 363.582Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M734.85 378.122C733.579 380.614 729.999 382.634 726.853 382.634C723.705 382.634 722.184 380.614 723.456 378.122C724.725 375.63 728.307 373.61 731.453 373.61C734.601 373.61 736.122 375.63 734.85 378.122Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M727.591 392.357C726.32 394.849 722.739 396.869 719.594 396.869C716.446 396.869 714.925 394.849 716.196 392.357C717.466 389.864 721.048 387.844 724.194 387.844C727.341 387.844 728.862 389.864 727.591 392.357Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M760.174 363.582C758.903 366.074 755.322 368.094 752.177 368.094C749.029 368.094 747.508 366.074 748.779 363.582C750.049 361.09 753.631 359.07 756.777 359.07C759.924 359.07 761.445 361.09 760.174 363.582Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M752.767 378.122C751.496 380.614 747.916 382.634 744.77 382.634C741.622 382.634 740.101 380.614 741.373 378.122C742.642 375.63 746.224 373.61 749.37 373.61C752.518 373.61 754.039 375.63 752.767 378.122Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M745.497 392.357C744.226 394.849 740.646 396.869 737.5 396.869C734.352 396.869 732.832 394.849 734.103 392.357C735.372 389.864 738.954 387.844 742.1 387.844C745.248 387.844 746.769 389.864 745.497 392.357Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M724.245 363.582C722.974 366.074 719.394 368.094 716.248 368.094C713.1 368.094 711.579 366.074 712.851 363.582C714.12 361.09 717.702 359.07 720.848 359.07C723.996 359.07 725.517 361.09 724.245 363.582Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M716.839 378.122C715.567 380.614 711.987 382.634 708.841 382.634C705.694 382.634 704.173 380.614 705.444 378.122C706.714 375.63 710.296 373.61 713.441 373.61C716.589 373.61 718.11 375.63 716.839 378.122Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M709.579 392.357C708.308 394.849 704.728 396.869 701.582 396.869C698.434 396.869 696.913 394.849 698.185 392.357C699.454 389.864 703.036 387.844 706.182 387.844C709.33 387.844 710.851 389.864 709.579 392.357Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M698.827 378.122C697.556 380.614 693.975 382.634 690.83 382.634C687.682 382.634 686.161 380.614 687.432 378.122C688.702 375.63 692.284 373.61 695.43 373.61C698.577 373.61 700.098 375.63 698.827 378.122Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M691.568 392.357C690.296 394.849 686.716 396.869 683.57 396.869C680.423 396.869 678.902 394.849 680.173 392.357C681.442 389.864 685.025 387.844 688.17 387.844C691.318 387.844 692.839 389.864 691.568 392.357Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M702.33 406.57C701.059 409.062 697.479 411.082 694.333 411.082C691.185 411.082 689.665 409.062 690.936 406.57C692.205 404.078 695.787 402.058 698.933 402.058C702.081 402.058 703.602 404.078 702.33 406.57Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M684.319 406.57C683.047 409.062 679.467 411.082 676.321 411.082C673.174 411.082 671.653 409.062 672.924 406.57C674.194 404.078 677.776 402.058 680.921 402.058C684.069 402.058 685.59 404.078 684.319 406.57Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M749.232 420.763C747.961 423.255 744.381 425.275 741.235 425.275C738.087 425.275 736.566 423.255 737.838 420.763C739.107 418.271 742.689 416.251 745.835 416.251C748.983 416.251 750.504 418.271 749.232 420.763Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M865.035 406.591C863.764 409.083 860.184 411.104 857.038 411.104C853.89 411.104 852.369 409.083 853.641 406.591C854.91 404.099 858.492 402.079 861.638 402.079C864.786 402.079 866.306 404.099 865.035 406.591Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M839.543 420.51C838.272 423.003 834.692 425.023 831.546 425.023C828.398 425.023 826.877 423.003 828.149 420.51C829.418 418.018 833 415.998 836.146 415.998C839.294 415.998 840.815 418.018 839.543 420.51Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M832.347 434.629C831.076 437.122 827.495 439.142 824.35 439.142C821.202 439.142 819.681 437.122 820.952 434.629C822.222 432.137 825.804 430.117 828.95 430.117C832.097 430.117 833.618 432.137 832.347 434.629Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M406.453 205.98C405.181 208.472 401.601 210.492 398.455 210.492C395.308 210.492 393.787 208.472 395.058 205.98C396.328 203.488 399.91 201.468 403.056 201.468C406.203 201.468 407.724 203.488 406.453 205.98Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M442.402 205.98C441.131 208.472 437.551 210.492 434.405 210.492C431.257 210.492 429.737 208.472 431.008 205.98C432.277 203.488 435.859 201.468 439.005 201.468C442.153 201.468 443.674 203.488 442.402 205.98Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M460.761 205.98C459.49 208.472 455.91 210.492 452.764 210.492C449.616 210.492 448.095 208.472 449.367 205.98C450.636 203.488 454.218 201.468 457.364 201.468C460.512 201.468 462.033 203.488 460.761 205.98Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M417.489 219.552C416.218 222.044 412.637 224.064 409.492 224.064C406.344 224.064 404.823 222.044 406.094 219.552C407.364 217.059 410.946 215.039 414.092 215.039C417.239 215.039 418.76 217.059 417.489 219.552Z"
        stroke="#3D71A3"
        stroke-width="1.33333"
        stroke-miterlimit="10"
      />
      <path
        d="M432.985 143.707C431.8 143.76 430.993 144.655 430.419 145.412C430.178 145.732 429.961 146.053 429.78 146.377C429.625 146.653 429.455 146.953 429.338 147.228C429.306 147.303 429.288 147.393 429.329 147.449C429.363 147.497 429.402 147.501 429.487 147.497C429.711 147.486 429.99 147.436 430.225 147.413C430.948 147.343 431.609 147.378 432.288 147.4C432.489 147.407 432.713 147.456 432.933 147.303C433.117 147.174 433.182 146.946 433.25 146.793C433.566 146.093 433.97 145.269 434.06 144.599C434.136 144.037 433.769 143.671 432.985 143.707ZM427.718 154.816H427.185C427.14 154.803 427.077 154.812 427.023 154.81C425.375 154.753 424.037 154.383 423.129 153.562C421.872 152.429 421.631 150.799 422.551 148.937C422.556 148.926 422.569 148.91 422.56 148.905C422.59 148.845 422.617 148.788 422.649 148.729C423.386 147.357 424.477 146.122 426.124 145.055C428.074 143.791 430.381 142.92 432.536 142.775C433.548 142.706 434.674 142.73 435.531 142.922C437.362 143.331 438.813 144.538 438.621 146.175C438.571 146.621 438.418 147.247 438.141 147.711C437.813 148.264 437.168 148.501 436.502 148.571C434.795 148.754 433.338 148.47 431.76 148.409C430.988 148.379 430.081 148.37 429.239 148.515C428.494 148.643 428.081 148.836 427.747 149.42C426.941 150.833 428.086 151.761 429.681 151.857C430.81 151.925 432.229 151.664 433.464 151.18C434.224 150.882 434.958 150.445 435.642 150.022C436.032 149.779 436.47 149.411 436.91 149.486C437.31 149.555 437.046 149.975 436.874 150.242C435.91 151.745 433.827 153.219 431.835 154.009C430.482 154.546 429.002 154.783 427.718 154.816Z"
        fill="black"
        id="london"
        onClick={(e) => handleCityClick('london', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M453.521 172.239C452.336 172.293 451.53 173.185 450.955 173.945C450.715 174.265 450.498 174.586 450.316 174.909C450.162 175.186 449.991 175.486 449.875 175.76C449.842 175.836 449.824 175.925 449.866 175.981C449.9 176.03 449.939 176.033 450.024 176.03C450.248 176.019 450.526 175.969 450.762 175.945C451.485 175.875 452.146 175.911 452.825 175.933C453.026 175.94 453.25 175.988 453.469 175.836C453.654 175.706 453.719 175.478 453.787 175.326C454.103 174.625 454.507 173.801 454.597 173.132C454.672 172.57 454.306 172.203 453.521 172.239ZM448.255 183.348H447.722C447.677 183.336 447.614 183.345 447.56 183.343C445.912 183.285 444.574 182.916 443.665 182.095C442.409 180.962 442.168 179.332 443.087 177.47C443.093 177.459 443.105 177.443 443.096 177.437C443.127 177.378 443.154 177.321 443.186 177.261C443.922 175.89 445.014 174.654 446.66 173.588C448.61 172.324 450.918 171.453 453.072 171.307C454.085 171.239 455.211 171.262 456.067 171.454C457.899 171.864 459.35 173.07 459.158 174.708C459.107 175.153 458.955 175.78 458.678 176.243C458.35 176.796 457.705 177.033 457.039 177.103C455.331 177.286 453.875 177.003 452.297 176.942C451.525 176.911 450.618 176.902 449.776 177.048C449.031 177.175 448.618 177.369 448.284 177.953C447.477 179.366 448.623 180.294 450.217 180.389C451.347 180.457 452.765 180.197 454.001 179.712C454.76 179.414 455.495 178.978 456.179 178.554C456.568 178.312 457.006 177.944 457.446 178.019C457.847 178.087 457.583 178.507 457.41 178.775C456.446 180.278 454.363 181.752 452.372 182.542C451.018 183.079 449.539 183.316 448.255 183.348Z"
        fill="black"
        id="paris"
        onClick={(e) => handleCityClick('paris', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M498.024 157.878C496.839 157.932 496.033 158.824 495.459 159.584C495.218 159.904 495.001 160.225 494.819 160.548C494.665 160.825 494.494 161.125 494.378 161.399C494.345 161.475 494.327 161.564 494.369 161.62C494.403 161.669 494.442 161.672 494.527 161.669C494.751 161.658 495.029 161.608 495.265 161.584C495.988 161.514 496.649 161.55 497.328 161.572C497.529 161.579 497.753 161.627 497.972 161.475C498.157 161.345 498.222 161.117 498.29 160.965C498.606 160.264 499.01 159.44 499.1 158.771C499.175 158.209 498.809 157.842 498.024 157.878ZM492.758 168.987H492.225C492.18 168.975 492.117 168.984 492.063 168.982C490.415 168.924 489.077 168.555 488.169 167.734C486.912 166.601 486.671 164.971 487.59 163.109C487.596 163.098 487.608 163.082 487.599 163.076C487.63 163.017 487.657 162.96 487.689 162.9C488.425 161.529 489.517 160.293 491.164 159.227C493.114 157.963 495.421 157.092 497.576 156.946C498.588 156.878 499.714 156.901 500.571 157.094C502.402 157.503 503.853 158.71 503.661 160.347C503.609 160.792 503.458 161.419 503.181 161.882C502.853 162.435 502.208 162.672 501.542 162.742C499.834 162.925 498.378 162.642 496.8 162.581C496.028 162.55 495.121 162.541 494.279 162.687C493.534 162.814 493.121 163.008 492.787 163.592C491.981 165.005 493.126 165.933 494.721 166.028C495.85 166.096 497.269 165.836 498.504 165.351C499.263 165.053 499.998 164.617 500.682 164.193C501.072 163.951 501.51 163.583 501.95 163.658C502.35 163.726 502.086 164.146 501.914 164.414C500.949 165.917 498.867 167.391 496.875 168.181C495.521 168.718 494.042 168.955 492.758 168.987Z"
        fill="black"
        id="amsterdam"
        onClick={(e) => handleCityClick('amsterdam', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M508.693 172.239C507.508 172.293 506.701 173.185 506.127 173.945C505.886 174.265 505.669 174.586 505.488 174.909C505.333 175.186 505.163 175.486 505.046 175.76C505.014 175.836 504.996 175.925 505.037 175.981C505.071 176.03 505.111 176.033 505.195 176.03C505.419 176.019 505.698 175.969 505.933 175.945C506.657 175.875 507.317 175.911 507.996 175.933C508.197 175.94 508.422 175.988 508.641 175.836C508.826 175.706 508.89 175.478 508.958 175.326C509.274 174.625 509.678 173.801 509.768 173.132C509.844 172.57 509.477 172.203 508.693 172.239ZM503.426 183.348H502.893C502.848 183.336 502.785 183.345 502.731 183.343C501.083 183.285 499.745 182.916 498.837 182.095C497.58 180.962 497.339 179.332 498.259 177.47C498.264 177.459 498.277 177.443 498.268 177.437C498.298 177.378 498.325 177.321 498.357 177.261C499.094 175.89 500.185 174.654 501.832 173.588C503.782 172.324 506.089 171.453 508.244 171.307C509.256 171.239 510.382 171.262 511.239 171.454C513.07 171.864 514.521 173.07 514.329 174.708C514.277 175.153 514.126 175.78 513.85 176.243C513.521 176.796 512.876 177.033 512.21 177.103C510.503 177.286 509.046 177.003 507.468 176.942C506.696 176.911 505.789 176.902 504.947 177.048C504.202 177.175 503.789 177.369 503.455 177.953C502.649 179.366 503.794 180.294 505.389 180.389C506.518 180.457 507.937 180.197 509.172 179.712C509.932 179.414 510.666 178.978 511.35 178.554C511.74 178.312 512.178 177.944 512.618 178.019C513.018 178.087 512.754 178.507 512.582 178.775C511.618 180.278 509.535 181.752 507.544 182.542C506.19 183.079 504.71 183.316 503.426 183.348Z"
        fill="black"
        id="berlin"
        onClick={(e) => handleCityClick('berlin', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M533.69 229.462C532.505 229.516 531.699 230.408 531.124 231.168C530.884 231.487 530.666 231.809 530.485 232.132C530.331 232.409 530.16 232.708 530.043 232.983C530.011 233.059 529.993 233.148 530.034 233.204C530.069 233.252 530.108 233.256 530.192 233.252C530.417 233.242 530.695 233.191 530.93 233.168C531.654 233.098 532.315 233.134 532.993 233.155C533.195 233.163 533.419 233.211 533.638 233.059C533.823 232.929 533.888 232.701 533.956 232.549C534.272 231.848 534.676 231.024 534.766 230.354C534.841 229.792 534.475 229.426 533.69 229.462ZM528.424 240.571H527.891C527.846 240.559 527.783 240.568 527.729 240.566C526.081 240.508 524.743 240.138 523.834 239.318C522.577 238.185 522.337 236.554 523.256 234.692C523.262 234.682 523.274 234.666 523.265 234.66C523.296 234.601 523.323 234.543 523.355 234.484C524.091 233.112 525.183 231.877 526.829 230.81C528.779 229.546 531.087 228.676 533.241 228.53C534.254 228.462 535.38 228.485 536.236 228.677C538.068 229.087 539.519 230.293 539.326 231.931C539.274 232.376 539.124 233.003 538.847 233.466C538.518 234.019 537.874 234.256 537.208 234.326C535.5 234.509 534.044 234.226 532.466 234.165C531.694 234.134 530.787 234.125 529.945 234.271C529.199 234.398 528.786 234.592 528.453 235.175C527.646 236.589 528.792 237.517 530.386 237.612C531.516 237.68 532.934 237.42 534.17 236.935C534.929 236.637 535.663 236.201 536.348 235.777C536.737 235.535 537.175 235.166 537.615 235.242C538.016 235.31 537.752 235.73 537.579 235.998C536.615 237.501 534.532 238.975 532.541 239.765C531.187 240.302 529.708 240.539 528.424 240.571Z"
        fill="black"
        id="istanbul"
        onClick={(e) => handleCityClick('istanbul', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M913.892 157.794C912.707 157.848 911.901 158.742 911.327 159.5C911.086 159.819 910.869 160.141 910.687 160.464C910.533 160.741 910.362 161.04 910.246 161.315C910.213 161.391 910.195 161.48 910.237 161.536C910.271 161.584 910.31 161.588 910.395 161.584C910.619 161.574 910.897 161.523 911.133 161.5C911.856 161.43 912.517 161.466 913.196 161.487C913.397 161.495 913.621 161.543 913.84 161.391C914.025 161.261 914.09 161.033 914.158 160.881C914.474 160.18 914.878 159.356 914.968 158.686C915.043 158.124 914.677 157.758 913.892 157.794ZM908.626 168.903H908.093C908.048 168.891 907.985 168.9 907.931 168.898C906.283 168.84 904.945 168.47 904.037 167.65C902.78 166.517 902.539 164.886 903.458 163.024C903.464 163.014 903.476 162.998 903.467 162.992C903.498 162.933 903.525 162.875 903.557 162.816C904.293 161.444 905.385 160.209 907.032 159.142C908.982 157.878 911.289 157.008 913.443 156.862C914.456 156.794 915.582 156.817 916.438 157.009C918.27 157.419 919.721 158.625 919.529 160.263C919.477 160.708 919.326 161.335 919.049 161.798C918.721 162.351 918.076 162.588 917.41 162.658C915.702 162.841 914.246 162.558 912.668 162.497C911.896 162.466 910.989 162.457 910.147 162.603C909.402 162.73 908.989 162.924 908.655 163.507C907.849 164.921 908.994 165.849 910.589 165.944C911.718 166.012 913.136 165.752 914.372 165.267C915.131 164.969 915.866 164.533 916.55 164.109C916.939 163.867 917.378 163.498 917.817 163.574C918.218 163.642 917.954 164.062 917.782 164.33C916.817 165.833 914.734 167.307 912.743 168.097C911.389 168.634 909.91 168.871 908.626 168.903Z"
        fill="black"
        id="seoul"
        onClick={(e) => handleCityClick('seoul', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M886.749 215.375C885.564 215.428 884.757 216.321 884.183 217.08C883.942 217.4 883.725 217.721 883.544 218.045C883.389 218.321 883.219 218.621 883.102 218.896C883.07 218.971 883.052 219.061 883.093 219.117C883.127 219.165 883.167 219.169 883.251 219.165C883.475 219.154 883.754 219.104 883.989 219.081C884.713 219.011 885.373 219.046 886.052 219.068C886.253 219.075 886.478 219.124 886.697 218.971C886.882 218.842 886.946 218.614 887.014 218.461C887.33 217.761 887.734 216.937 887.824 216.267C887.9 215.705 887.533 215.339 886.749 215.375ZM881.482 226.484H880.949C880.904 226.471 880.841 226.48 880.787 226.478C879.139 226.421 877.801 226.051 876.893 225.23C875.636 224.097 875.395 222.467 876.315 220.605C876.32 220.594 876.333 220.578 876.324 220.573C876.354 220.513 876.381 220.456 876.413 220.397C877.15 219.025 878.241 217.79 879.888 216.723C881.838 215.459 884.145 214.588 886.3 214.443C887.313 214.374 888.438 214.398 889.295 214.59C891.126 214.999 892.577 216.206 892.385 217.843C892.333 218.289 892.182 218.915 891.906 219.379C891.577 219.932 890.932 220.169 890.266 220.239C888.559 220.422 887.102 220.138 885.524 220.077C884.752 220.047 883.845 220.038 883.003 220.183C882.258 220.311 881.845 220.504 881.511 221.088C880.705 222.501 881.85 223.429 883.445 223.525C884.574 223.593 885.993 223.332 887.228 222.848C887.988 222.55 888.722 222.113 889.406 221.69C889.796 221.447 890.234 221.079 890.674 221.154C891.074 221.223 890.81 221.643 890.638 221.91C889.674 223.413 887.591 224.887 885.6 225.677C884.246 226.214 882.766 226.451 881.482 226.484Z"
        fill="black"
        id="tokyo"
        onClick={(e) => handleCityClick('tokyo', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M852.672 172.229C851.487 172.282 850.68 173.175 850.106 173.934C849.865 174.254 849.648 174.575 849.467 174.899C849.312 175.175 849.142 175.475 849.025 175.75C848.993 175.825 848.975 175.915 849.016 175.971C849.05 176.019 849.09 176.023 849.174 176.019C849.398 176.008 849.677 175.958 849.912 175.935C850.636 175.865 851.296 175.9 851.975 175.922C852.176 175.929 852.401 175.978 852.62 175.825C852.805 175.696 852.869 175.468 852.937 175.315C853.253 174.615 853.657 173.791 853.747 173.121C853.823 172.559 853.456 172.193 852.672 172.229ZM847.405 183.338H846.872C846.827 183.325 846.764 183.334 846.71 183.332C845.062 183.275 843.724 182.905 842.816 182.084C841.559 180.951 841.318 179.321 842.238 177.459C842.243 177.448 842.256 177.432 842.247 177.427C842.277 177.367 842.304 177.31 842.336 177.251C843.073 175.879 844.164 174.644 845.811 173.577C847.761 172.313 850.068 171.442 852.223 171.297C853.235 171.228 854.361 171.252 855.218 171.444C857.049 171.853 858.5 173.06 858.308 174.697C858.256 175.143 858.105 175.769 857.829 176.233C857.5 176.786 856.855 177.023 856.189 177.093C854.482 177.276 853.025 176.992 851.447 176.931C850.675 176.901 849.768 176.892 848.926 177.037C848.181 177.165 847.768 177.358 847.434 177.942C846.628 179.355 847.773 180.283 849.368 180.379C850.497 180.447 851.916 180.186 853.151 179.702C853.911 179.404 854.645 178.967 855.329 178.544C855.719 178.301 856.157 177.933 856.597 178.008C856.997 178.077 856.733 178.497 856.561 178.764C855.597 180.267 853.514 181.741 851.523 182.531C850.169 183.068 848.689 183.305 847.405 183.338Z"
        fill="black"
        id="shanghai"
        onClick={(e) => handleCityClick('shanghai', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M81.3885 229.683C80.2034 229.737 79.3972 230.629 78.8226 231.389C78.582 231.708 78.3648 232.03 78.1834 232.353C78.029 232.629 77.8584 232.929 77.7417 233.204C77.7094 233.279 77.6914 233.369 77.7327 233.425C77.7668 233.473 77.8063 233.477 77.8907 233.473C78.1152 233.463 78.3935 233.412 78.6287 233.389C79.3523 233.319 80.0131 233.355 80.6918 233.376C80.8929 233.384 81.1173 233.432 81.3364 233.279C81.5213 233.15 81.586 232.922 81.6542 232.769C81.9702 232.069 82.3742 231.245 82.464 230.575C82.5394 230.013 82.1731 229.647 81.3885 229.683ZM76.1221 240.792H75.5888C75.5439 240.779 75.4811 240.788 75.4272 240.787C73.7789 240.729 72.4412 240.359 71.5327 239.539C70.2758 238.406 70.0352 236.775 70.9545 234.913C70.9599 234.903 70.9724 234.886 70.9635 234.881C70.994 234.822 71.0209 234.764 71.0532 234.705C71.7894 233.333 72.8811 232.098 74.5276 231.031C76.4776 229.767 78.7849 228.896 80.9396 228.751C81.9523 228.683 83.0781 228.706 83.9346 228.898C85.766 229.308 87.2169 230.514 87.0247 232.152C86.9727 232.597 86.8218 233.224 86.5453 233.687C86.2167 234.24 85.5721 234.477 84.906 234.547C83.1984 234.73 81.7422 234.447 80.1639 234.385C79.3918 234.355 78.4851 234.346 77.6429 234.491C76.8978 234.619 76.4848 234.813 76.1508 235.396C75.3446 236.809 76.4902 237.738 78.0846 237.833C79.2141 237.901 80.6325 237.641 81.8679 237.156C82.6274 236.858 83.3618 236.422 84.0459 235.998C84.4355 235.755 84.8736 235.387 85.3136 235.463C85.714 235.531 85.45 235.951 85.2776 236.219C84.3134 237.722 82.2306 239.196 80.2393 239.986C78.8855 240.523 77.4059 240.76 76.1221 240.792Z"
        fill="black"
        id="los-angeles"
        onClick={(e) => handleCityClick('los-angeles', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M142.799 286.916C141.614 286.97 140.807 287.863 140.233 288.622C139.992 288.942 139.775 289.263 139.594 289.586C139.439 289.863 139.269 290.163 139.152 290.437C139.12 290.513 139.102 290.603 139.143 290.658C139.177 290.707 139.216 290.71 139.301 290.707C139.525 290.696 139.804 290.646 140.039 290.622C140.762 290.552 141.423 290.588 142.102 290.61C142.303 290.617 142.527 290.665 142.747 290.513C142.931 290.384 142.996 290.156 143.064 290.003C143.38 289.303 143.784 288.478 143.874 287.809C143.95 287.247 143.583 286.88 142.799 286.916ZM137.532 298.025H136.999C136.954 298.013 136.891 298.022 136.837 298.02C135.189 297.963 133.851 297.593 132.943 296.772C131.686 295.639 131.445 294.009 132.365 292.147C132.37 292.136 132.383 292.12 132.374 292.114C132.404 292.055 132.431 291.998 132.463 291.938C133.2 290.567 134.291 289.331 135.938 288.265C137.888 287.001 140.195 286.13 142.35 285.984C143.362 285.916 144.488 285.94 145.345 286.132C147.176 286.541 148.627 287.748 148.435 289.385C148.383 289.831 148.232 290.457 147.955 290.92C147.627 291.473 146.982 291.71 146.316 291.78C144.609 291.964 143.152 291.68 141.574 291.619C140.802 291.588 139.895 291.579 139.053 291.725C138.308 291.852 137.895 292.046 137.561 292.63C136.755 294.043 137.9 294.971 139.495 295.066C140.624 295.135 142.043 294.874 143.278 294.389C144.038 294.091 144.772 293.655 145.456 293.231C145.846 292.989 146.284 292.621 146.724 292.696C147.124 292.764 146.86 293.185 146.688 293.452C145.724 294.955 143.641 296.429 141.649 297.219C140.296 297.756 138.816 297.993 137.532 298.025Z"
        fill="black"
        id="mexico-city"
        onClick={(e) => handleCityClick('mexico-city', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M421.812 201.035C420.626 201.089 419.82 201.983 419.246 202.74C419.005 203.06 418.788 203.381 418.606 203.705C418.452 203.981 418.281 204.281 418.165 204.556C418.132 204.631 418.114 204.721 418.156 204.777C418.19 204.825 418.229 204.829 418.314 204.825C418.538 204.814 418.817 204.764 419.052 204.741C419.775 204.671 420.436 204.707 421.115 204.728C421.316 204.735 421.54 204.784 421.759 204.631C421.944 204.502 422.009 204.274 422.077 204.121C422.393 203.421 422.797 202.597 422.887 201.927C422.962 201.365 422.596 200.999 421.812 201.035ZM416.545 212.144H416.012C415.967 212.131 415.904 212.14 415.85 212.138C414.202 212.081 412.864 211.711 411.956 210.89C410.699 209.757 410.458 208.127 411.378 206.265C411.383 206.254 411.395 206.238 411.386 206.233C411.417 206.174 411.444 206.116 411.476 206.057C412.212 204.685 413.304 203.45 414.951 202.383C416.901 201.119 419.208 200.248 421.363 200.103C422.375 200.035 423.501 200.058 424.358 200.25C426.189 200.659 427.64 201.866 427.448 203.504C427.397 203.949 427.245 204.576 426.968 205.039C426.64 205.592 425.995 205.829 425.329 205.899C423.621 206.082 422.165 205.798 420.587 205.737C419.815 205.707 418.908 205.698 418.066 205.843C417.321 205.971 416.908 206.165 416.574 206.748C415.768 208.161 416.913 209.09 418.508 209.185C419.637 209.253 421.056 208.993 422.291 208.508C423.05 208.21 423.785 207.773 424.469 207.35C424.859 207.107 425.297 206.739 425.737 206.815C426.137 206.883 425.873 207.303 425.701 207.57C424.736 209.073 422.654 210.548 420.662 211.338C419.308 211.874 417.829 212.111 416.545 212.144Z"
        fill="black"
        id="barcelona"
        onClick={(e) => handleCityClick('barcelona', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M396.688 214.48C395.503 214.534 394.696 215.427 394.122 216.186C393.881 216.506 393.664 216.827 393.483 217.15C393.328 217.427 393.158 217.727 393.041 218.001C393.009 218.077 392.991 218.167 393.032 218.222C393.066 218.271 393.106 218.274 393.19 218.271C393.414 218.26 393.693 218.21 393.928 218.186C394.652 218.116 395.312 218.152 395.991 218.174C396.192 218.181 396.417 218.229 396.636 218.077C396.821 217.948 396.885 217.719 396.953 217.567C397.269 216.867 397.673 216.042 397.763 215.373C397.839 214.811 397.472 214.444 396.688 214.48ZM391.421 225.589H390.888C390.843 225.577 390.78 225.586 390.726 225.584C389.078 225.527 387.74 225.157 386.832 224.336C385.575 223.203 385.334 221.573 386.254 219.711C386.259 219.7 386.272 219.684 386.263 219.678C386.293 219.619 386.32 219.562 386.352 219.502C387.089 218.131 388.18 216.895 389.827 215.829C391.777 214.565 394.084 213.694 396.239 213.548C397.252 213.48 398.377 213.504 399.234 213.696C401.065 214.105 402.516 215.312 402.324 216.949C402.274 217.394 402.121 218.021 401.845 218.484C401.516 219.037 400.871 219.274 400.205 219.344C398.498 219.528 397.041 219.244 395.463 219.183C394.691 219.152 393.784 219.143 392.942 219.289C392.197 219.416 391.784 219.61 391.45 220.194C390.644 221.607 391.789 222.535 393.384 222.63C394.513 222.699 395.932 222.438 397.167 221.953C397.927 221.655 398.661 221.219 399.345 220.795C399.735 220.553 400.173 220.185 400.613 220.26C401.013 220.328 400.749 220.749 400.577 221.016C399.613 222.519 397.53 223.993 395.539 224.783C394.185 225.32 392.705 225.557 391.421 225.589Z"
        fill="black"
        id="lisboa"
        onClick={(e) => handleCityClick('lisboa', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M782.74 273.45C781.555 273.504 780.748 274.396 780.174 275.155C779.933 275.475 779.716 275.796 779.535 276.12C779.382 276.396 779.21 276.696 779.093 276.971C779.061 277.046 779.043 277.136 779.084 277.192C779.118 277.24 779.157 277.244 779.242 277.24C779.466 277.229 779.745 277.179 779.98 277.156C780.703 277.086 781.364 277.122 782.043 277.143C782.244 277.15 782.468 277.199 782.688 277.046C782.872 276.917 782.937 276.689 783.005 276.536C783.321 275.836 783.725 275.012 783.815 274.342C783.891 273.78 783.524 273.414 782.74 273.45ZM777.473 284.559H776.94C776.895 284.546 776.832 284.555 776.778 284.553C775.13 284.496 773.792 284.126 772.884 283.305C771.627 282.172 771.386 280.542 772.306 278.68C772.311 278.669 772.324 278.653 772.315 278.648C772.345 278.589 772.372 278.531 772.404 278.472C773.141 277.1 774.232 275.865 775.879 274.798C777.829 273.534 780.136 272.663 782.291 272.518C783.303 272.45 784.429 272.473 785.286 272.665C787.117 273.074 788.568 274.281 788.376 275.919C788.326 276.364 788.173 276.99 787.896 277.454C787.568 278.007 786.923 278.244 786.257 278.314C784.55 278.497 783.095 278.213 781.515 278.152C780.743 278.122 779.836 278.113 778.994 278.258C778.249 278.386 777.836 278.58 777.502 279.163C776.696 280.576 777.841 281.505 779.436 281.6C780.565 281.668 781.984 281.408 783.219 280.923C783.979 280.625 784.713 280.188 785.397 279.765C785.787 279.522 786.225 279.154 786.665 279.23C787.065 279.298 786.801 279.718 786.629 279.985C785.665 281.488 783.582 282.963 781.59 283.753C780.237 284.289 778.757 284.526 777.473 284.559Z"
        fill="black"
        id="taipei"
        onClick={(e) => handleCityClick('taipei', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M201 172.45C199.815 172.503 199.009 173.398 198.434 174.155C198.193 174.475 197.976 174.796 197.795 175.12C197.64 175.396 197.47 175.696 197.353 175.971C197.321 176.046 197.303 176.136 197.344 176.191C197.378 176.24 197.418 176.244 197.502 176.24C197.727 176.229 198.005 176.179 198.24 176.156C198.964 176.086 199.625 176.121 200.303 176.143C200.504 176.15 200.729 176.199 200.948 176.046C201.133 175.917 201.197 175.689 201.266 175.536C201.582 174.836 201.986 174.012 202.075 173.342C202.151 172.78 201.785 172.414 201 172.45ZM195.734 183.559H195.2C195.155 183.546 195.093 183.555 195.039 183.553C193.39 183.496 192.053 183.126 191.144 182.305C189.887 181.172 189.647 179.542 190.566 177.68C190.571 177.669 190.584 177.653 190.575 177.648C190.605 177.588 190.632 177.531 190.665 177.472C191.401 176.1 192.493 174.865 194.139 173.798C196.089 172.534 198.396 171.663 200.551 171.518C201.564 171.449 202.69 171.473 203.546 171.665C205.377 172.074 206.828 173.281 206.636 174.918C206.584 175.364 206.433 175.99 206.157 176.454C205.828 177.007 205.184 177.244 204.517 177.314C202.81 177.497 201.354 177.213 199.775 177.152C199.003 177.122 198.097 177.113 197.254 177.258C196.509 177.386 196.096 177.579 195.762 178.163C194.956 179.576 196.102 180.504 197.696 180.6C198.826 180.668 200.244 180.407 201.479 179.923C202.239 179.625 202.973 179.188 203.657 178.765C204.047 178.522 204.485 178.154 204.925 178.229C205.325 178.298 205.061 178.718 204.889 178.985C203.925 180.488 201.842 181.962 199.851 182.752C198.497 183.289 197.017 183.526 195.734 183.559Z"
        fill="black"
        id="montreal"
        onClick={(e) => handleCityClick('montreal', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M74.5183 172.45C73.3333 172.503 72.5271 173.398 71.9525 174.155C71.7119 174.475 71.4946 174.796 71.3133 175.12C71.1588 175.396 70.9883 175.696 70.8716 175.971C70.8392 176.046 70.8213 176.136 70.8626 176.191C70.8967 176.24 70.9362 176.244 71.0206 176.24C71.245 176.229 71.5233 176.179 71.7586 176.156C72.4822 176.086 73.1429 176.121 73.8217 176.143C74.0228 176.15 74.2472 176.199 74.4663 176.046C74.6512 175.917 74.7158 175.689 74.7841 175.536C75.1001 174.836 75.5041 174.012 75.5939 173.342C75.6693 172.78 75.303 172.414 74.5183 172.45ZM69.252 183.559H68.7187C68.6738 183.546 68.611 183.555 68.5571 183.553C66.9088 183.496 65.5711 183.126 64.6625 182.305C63.4056 181.172 63.165 179.542 64.0844 177.68C64.0897 177.669 64.1023 177.653 64.0933 177.648C64.1239 177.588 64.1508 177.531 64.1831 177.472C64.9193 176.1 66.011 174.865 67.6575 173.798C69.6075 172.534 71.9148 171.663 74.0694 171.518C75.0821 171.449 76.208 171.473 77.0644 171.665C78.8959 172.074 80.3467 173.281 80.1546 174.918C80.1025 175.364 79.9517 175.99 79.6752 176.454C79.3466 177.007 78.702 177.244 78.0358 177.314C76.3283 177.497 74.8721 177.213 73.2938 177.152C72.5217 177.122 71.6149 177.113 70.7728 177.258C70.0276 177.386 69.6147 177.579 69.2807 178.163C68.4745 179.576 69.6201 180.504 71.2145 180.6C72.3439 180.668 73.7624 180.407 74.9978 179.923C75.7573 179.625 76.4917 179.188 77.1758 178.765C77.5654 178.522 78.0035 178.154 78.4434 178.229C78.8438 178.298 78.5799 178.718 78.4075 178.985C77.4433 180.488 75.3605 181.962 73.3692 182.752C72.0153 183.289 70.5358 183.526 69.252 183.559Z"
        fill="black"
        id="vancouver"
        onClick={(e) => handleCityClick('vancouver', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M474.489 224.591C473.304 224.645 472.498 225.537 471.924 226.297C471.683 226.616 471.466 226.938 471.284 227.261C471.13 227.537 470.959 227.837 470.843 228.112C470.81 228.187 470.792 228.277 470.834 228.333C470.868 228.381 470.907 228.385 470.992 228.381C471.216 228.37 471.494 228.32 471.73 228.297C472.453 228.227 473.114 228.263 473.793 228.284C473.994 228.291 474.218 228.34 474.437 228.187C474.622 228.058 474.687 227.83 474.755 227.677C475.071 226.977 475.475 226.153 475.565 225.483C475.64 224.921 475.274 224.555 474.489 224.591ZM469.223 235.7H468.69C468.645 235.687 468.582 235.696 468.528 235.695C466.88 235.637 465.542 235.267 464.634 234.447C463.377 233.314 463.136 231.683 464.055 229.821C464.061 229.811 464.073 229.794 464.064 229.789C464.095 229.73 464.122 229.672 464.154 229.613C464.89 228.241 465.982 227.006 467.629 225.939C469.579 224.675 471.886 223.804 474.041 223.659C475.053 223.591 476.179 223.614 477.036 223.806C478.867 224.216 480.318 225.422 480.126 227.06C480.075 227.505 479.923 228.132 479.646 228.595C479.318 229.148 478.673 229.385 478.007 229.455C476.299 229.638 474.843 229.354 473.265 229.293C472.493 229.263 471.586 229.254 470.744 229.399C469.999 229.527 469.586 229.721 469.252 230.304C468.446 231.717 469.591 232.646 471.186 232.741C472.315 232.809 473.733 232.549 474.969 232.064C475.728 231.766 476.463 231.33 477.147 230.906C477.536 230.663 477.975 230.295 478.415 230.371C478.815 230.439 478.551 230.859 478.379 231.127C477.414 232.63 475.332 234.104 473.34 234.894C471.986 235.431 470.507 235.668 469.223 235.7Z"
        fill="black"
        id="roma"
        onClick={(e) => handleCityClick('roma', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M734.986 258.194C733.8 258.248 732.994 259.142 732.42 259.9C732.179 260.22 731.962 260.541 731.78 260.864C731.628 261.141 731.455 261.441 731.339 261.715C731.306 261.791 731.288 261.881 731.33 261.936C731.364 261.985 731.403 261.988 731.488 261.985C731.712 261.974 731.991 261.924 732.226 261.9C732.949 261.83 733.61 261.866 734.289 261.888C734.49 261.895 734.714 261.944 734.933 261.791C735.118 261.662 735.183 261.434 735.251 261.281C735.567 260.581 735.971 259.757 736.061 259.087C736.136 258.525 735.77 258.158 734.986 258.194ZM729.719 269.304H729.186C729.141 269.291 729.078 269.3 729.024 269.298C727.376 269.241 726.038 268.871 725.13 268.05C723.873 266.917 723.632 265.287 724.552 263.425C724.557 263.414 724.569 263.398 724.561 263.393C724.591 263.333 724.618 263.276 724.65 263.217C725.386 261.845 726.478 260.609 728.125 259.543C730.075 258.279 732.382 257.408 734.537 257.263C735.549 257.194 736.675 257.218 737.532 257.41C739.363 257.819 740.814 259.026 740.622 260.663C740.571 261.109 740.419 261.735 740.142 262.198C739.814 262.752 739.169 262.989 738.503 263.059C736.795 263.242 735.341 262.958 733.761 262.897C732.989 262.866 732.082 262.857 731.24 263.003C730.495 263.13 730.082 263.324 729.748 263.908C728.942 265.321 730.087 266.249 731.682 266.344C732.811 266.413 734.23 266.152 735.465 265.668C736.224 265.369 736.959 264.933 737.643 264.509C738.033 264.267 738.471 263.899 738.911 263.974C739.311 264.043 739.047 264.463 738.875 264.73C737.91 266.233 735.828 267.707 733.836 268.497C732.483 269.034 731.003 269.271 729.719 269.304Z"
        fill="black"
        id="bangkok"
        onClick={(e) => handleCityClick('bangkok', e)}
        style={{ cursor: 'pointer' }}
      />
      <path
        d="M814.713 387.285C813.527 387.339 812.721 388.233 812.147 388.991C811.906 389.311 811.689 389.632 811.507 389.955C811.353 390.232 811.182 390.532 811.066 390.806C811.033 390.882 811.015 390.971 811.057 391.027C811.091 391.076 811.13 391.079 811.215 391.076C811.439 391.065 811.718 391.015 811.953 390.991C812.676 390.921 813.337 390.957 814.016 390.979C814.217 390.986 814.441 391.034 814.66 390.882C814.845 390.752 814.91 390.524 814.978 390.372C815.294 389.671 815.698 388.847 815.788 388.178C815.863 387.616 815.497 387.249 814.713 387.285ZM809.446 398.394H808.913C808.868 398.382 808.805 398.391 808.751 398.389C807.103 398.331 805.765 397.962 804.857 397.141C803.6 396.008 803.359 394.378 804.279 392.516C804.284 392.505 804.296 392.489 804.287 392.483C804.318 392.424 804.345 392.367 804.377 392.307C805.113 390.936 806.205 389.7 807.852 388.634C809.802 387.37 812.109 386.499 814.264 386.353C815.276 386.285 816.402 386.308 817.259 386.5C819.09 386.91 820.541 388.117 820.349 389.754C820.297 390.199 820.146 390.826 819.869 391.289C819.541 391.842 818.896 392.079 818.23 392.149C816.522 392.332 815.066 392.049 813.488 391.988C812.716 391.957 811.809 391.948 810.967 392.094C810.222 392.221 809.809 392.415 809.475 392.999C808.669 394.412 809.814 395.34 811.409 395.435C812.538 395.503 813.957 395.243 815.192 394.758C815.951 394.46 816.686 394.024 817.37 393.6C817.76 393.358 818.198 392.99 818.638 393.065C819.038 393.133 818.774 393.553 818.602 393.821C817.637 395.324 815.555 396.798 813.563 397.588C812.209 398.125 810.73 398.362 809.446 398.394Z"
        fill="black"
        id="sydney"
        onClick={(e) => handleCityClick('sydney', e)}
        style={{ cursor: 'pointer' }}
      />
        </svg>
        
        {/* City names positioned outside SVG */}
        {showTooltips && Object.entries(cityCoords).map(([svgId, coords]) => {
          const cityKey = svgIdToCityKey[svgId];
          if (!cityKey || !cities[cityKey]) return null;
          
          const city = cityKeyToDisplayName[cityKey] || cities[cityKey].name;
          const screenCoords = getScreenCoordinates(coords.x, coords.y);
          
          // Cities in top row (left side)
          const topLeftCities = [
            'Vancouver', 'Montreal', 'Paris', 'Taipei', 'London', 'Tokyo'
          ];
          
          // Cities in top row (right side)
          const topRightCities = [
            'New York', 'Berlin', 'Amsterdam', 'Seoul'
          ];
          
          // Cities in bottom row (left side)
          const bottomLeftCities = [
            'Los Angeles', 'Lisbon', 'Barcelona', 'Bangkok', 'Sydney'
          ];
          
          // Cities in bottom row (right side)
          const bottomRightCities = [
            'Mexico City', 'Roma', 'Istanbul', 'Shanghai'
          ];
          
          const isTopLeftCity = topLeftCities.includes(city);
          const isTopRightCity = topRightCities.includes(city);
          const isTopCity = isTopLeftCity || isTopRightCity;
          const isBottomLeftCity = bottomLeftCities.includes(city);
          const isBottomRightCity = bottomRightCities.includes(city);
          
          // Special positioning for Paris and Amsterdam
          const isSpecialTopCity = ['Paris', 'Amsterdam'].includes(city);
          
          // Special positioning for bottom cities
          const isSpecialBottomCity = ['Lisbon'].includes(city);
          
          // Calculate dynamic line positioning based on distance from city name to pin
          const cityY = isSpecialTopCity ? 30 : (isSpecialBottomCity ? svgDimensions.height - 100 : (isTopCity ? 70 : svgDimensions.height - 60));
          const pinY = screenCoords.y;
          
          // Calculate line start position (bottom of city name text)
          const textHeight = 19;
          const lineStartY = cityY - (0.2 * textHeight);
          
          // Calculate line height to reach the pin 
          const lineHeight = Math.abs(pinY - lineStartY);
          
          return (
            <div
              key={`city-${svgId}`}
              className="city-label"
              style={{
                position: 'absolute',
                left: `${screenCoords.x}px`,
                top: isSpecialTopCity ? '-50px' : (isSpecialBottomCity ? `${svgDimensions.height - 40}px` : (isTopCity ? '-10px' : `${svgDimensions.height -0}px`)),
                transform: (isTopRightCity || isBottomRightCity) ? 'translate(0%, -120%)' : 'translate(-100%, -120%)',
                color: '#000000',
                fontFamily: "'Adieu', sans-serif",
                fontWeight: '400',
                fontSize: '12px',
                pointerEvents: 'none',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                zIndex: 20,
              }}
            >
              <div style={{ position: 'relative' }}>
                {/* Vertical line for bottom left cities */}
                {isBottomLeftCity && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 'auto',
                      right: 0,
                      top: `-${lineHeight + 50}px`,
                      width: '1px',
                      height: `${lineHeight + 50}px`,
                      backgroundColor: '#000000',
                      zIndex: 1,
                    }}
                  />
                )}
                
                {/* Vertical line for bottom right cities */}
                {isBottomRightCity && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 'auto',
                      top: `-${lineHeight + 50}px`,
                      width: '1px',
                      height: `${lineHeight + 50}px`,
                      backgroundColor: '#000000',
                      zIndex: 1,
                    }}
                  />
                )}
                
                {/* Vertical line for top left cities */}
                {isTopLeftCity && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 'auto',
                      right: 0,
                      top: `${textHeight}px`,
                      width: '1px',
                      height: `${lineHeight + 70}px`,
                      backgroundColor: '#000000',
                      zIndex: 1,
                    }}
                  />
                )}
                
                {/* Vertical line for top right cities */}
                {isTopRightCity && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 'auto',
                      top: `${textHeight}px`,
                      width: '1px',
                      height: `${lineHeight + 70}px`,
                      backgroundColor: '#000000',
                      zIndex: 1,
                    }}
                  />
                )}
                
                <p
                  onClick={() => {
                    setModalCity(city);
                    setModalCityData(cities[cityKey]);
                    
                    const artistIds = getArtistIdsForCity(cityKey);
                    const artists = artistIds.map(id => 
                      ARTISTS_DATA.find(artist => artist.id === id)
                    ).filter(Boolean);
                    
                    setModalArtists(artists);
                    const clickCoords = { x: screenCoords.x, y: cityY };
                    setModalPosition(clickCoords);
                    setRawClickPosition(clickCoords);
                  }}
                  style={{
                    textTransform: 'lowercase',
                    ...(isTopCity ? {
                      borderBottom: '1px solid #000000',
                      paddingBottom: '4px'
                    } : {
                      borderTop: '1px solid #000000',
                      paddingTop: '4px',
                      marginBottom: `-${textHeight}px`
                    }),
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  {city}
                </p>
              </div>
            </div>
          );
        })}
        
        {/* Gradient overlays */}
        <div className="svg-gradient-top"></div>
        <div className="svg-gradient-bottom"></div>

        {/* Floating popover modal at click position */}
        {modalCity && modalCityData && modalPosition && (
          <div
            className="modal-content modal-popover"
            style={{
              position: 'absolute',
              left: `${modalPosition.x}px`,
              top: `${modalPosition.y}px`,
              zIndex: 1000,
              transform: modalTransform
            }}
            ref={modalRef}
            role="dialog"
            aria-modal="false"
          >
            <button
              className="modal-close"
              onClick={() => {
                setModalCity(null);
                setModalCityData(null);
                setModalArtists([]);
                setModalPosition(null);
              }}
              aria-label="Close"
            >
              ×
            </button>
            <div className="modal-body">
              <h2>{modalCity}</h2>
              <p className="modal-subtitle">Featured Artists</p>
              {modalArtists && modalArtists.length > 0 ? (
                <div className="modal-artists-list">
                  {modalArtists.map((artist) => (
                    <ArtistCard 
                      key={artist.id} 
                      artist={artist} 
                      variant="list" 
                    />
                  ))}
                </div>
              ) : (
                <p className="modal-no-artists">No artists available for this city.</p>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default WorldMapSvg;
